import { axiosAPI as axios, getSignedUrl } from 'utils';
import { dbGet, dbSet } from './db';
import { buildCustomLevelDirectReusePlan } from './customLevelDirectReuse';
import { cacheActivityData, cacheVmPlacementActivityData, getCachedCommonRuntime, setActiveVmPlacementDataScope, isActiveVmPlacementActivity } from './cache';
import { getOfflineState, isOnline, subscribeOfflineState, updateOfflineState } from './state';
import {
  MOBILE_ASSET_AUDIT_RUNTIME_PREFIXES,
  OFFLINE_MANIFEST_ALLOWED_EXACT_PREFIXES,
  PLACEMENT_REQUIRED_SHARED_KEYS,
  REQUIRED_EXACT_RUNTIME_KEYS,
  collectActivityRuntimeAssetKeys,
  getRuntimeThemeDescriptors,
} from './runtimeAssetContract';

const META_KEY = 'asset.meta.v1';
const PACK_META_KEY = 'asset.pack-meta.v1';
const ROOT = 'flc-offline-v1';
const CUSTOM_LEVEL_PACK_SCHEMA = 10;
const PLACEMENT_PACK_SCHEMA = 6;

// One-time cache refresh for the three Activities whose legacy Vietnamese
// Placement base TARs contain older data.json snapshots. Other Placement
// locales and Custom Content retain their original identity and behavior.
const VM_PLACEMENT_REFRESH_IDS = Object.freeze([
  '5c63ffd2-0c77-4da4-86db-0a2966a4041f',
  '8e71a56a-0ea9-4760-b228-28ad57d4f4ad',
  'c53VNmpA5h',
]);
const VM_PLACEMENT_REFRESH_SCHEMA = 'vm-ages-4-8-source-overlay-20261013-v1';
const isVmPlacementSourceRefresh = (scope, ageBand, instructionLanguage, activityIds = []) => (
  scope === 'placement'
  && String(ageBand || '') === 'age_4_8'
  && normalizeOfflineLanguageCode(instructionLanguage) === 'vm'
  && VM_PLACEMENT_REFRESH_IDS.every((id) => activityIds.map(String).includes(id))
);

const createdDirs = new Set();
let assetMeta = {};
let packMeta = {};
let initialized = false;
let packMetaInitialized = false;
let syncPromise = null;
let onDemandAssetSyncChain = Promise.resolve();
let missingNoticeAt = 0;

// UI code historically builds Help/Audio URLs from `imagesURL`, which already
// contains `/Images`, so runtime URLs can canonicalize as `Images/Images/...`.
// Older offline packages also exist with the same files stored as `Images/...`.
// Resolve both forms so an app update cannot invalidate already-downloaded
// controls just because the key spelling changed between package revisions.
const alternateImageAssetKey = (key = '') => {
  const value = String(key || '');
  if (value.startsWith('Images/Images/')) {
    return `Images/${value.slice('Images/Images/'.length)}`;
  }
  if (value.startsWith('Images/Help/') || value.startsWith('Images/Audio/')) {
    return `Images/Images/${value.slice('Images/'.length)}`;
  }
  return '';
};

const getAssetMetaEntry = (key = '') => {
  const value = String(key || '');
  if (!value) return null;
  const direct = assetMeta[value];
  if (direct?.nativeUri) return direct;
  const alternate = alternateImageAssetKey(value);
  return alternate ? (assetMeta[alternate] || null) : null;
};

const filesystem = () => (
  window.Capacitor?.Plugins?.FLCOfflineStorage
  || window.Capacitor?.Plugins?.Filesystem
  || null
);
const convertFileSrc = (uri) => window.Capacitor?.convertFileSrc?.(uri) || uri;

const stripQuery = (value = '') => String(value || '').split('#')[0].split('?')[0];

const MISSING_ASSET_LOG_PATH = `${ROOT}/diagnostics/missing-assets.jsonl`;
const isOfflineManifestExactKey = (key = '') => OFFLINE_MANIFEST_ALLOWED_EXACT_PREFIXES.some((prefix) => (
  String(key || '').startsWith(prefix)
));

const hasRequiredExactRuntimeAssets = () => REQUIRED_EXACT_RUNTIME_KEYS.every((key) => (
  Boolean(getAssetMetaEntry(key)?.nativeUri)
));

const auditPrefixForKey = (key = '') => (
  MOBILE_ASSET_AUDIT_RUNTIME_PREFIXES.find((prefix) => String(key || '').startsWith(prefix))
  || 'runtime-baseline/other'
);

const missingAssetLogKeys = new Set();
let offlineDiagnosticActivityContext = { activityId: '', template: '', screen: 'activity' };
let offlineDiagnosticWriteChain = Promise.resolve();
let runtimeAssetDiagnosticEpoch = 0;
let runtimeAssetDiagnosticWindowUntil = 0;
const runtimeAssetResolverLogKeys = new Set();

// Temporary end-to-end asset-flow audit. This does not change what is
// downloaded or rendered; it only records what the runtime asks for and what
// each offline package plans to download. Records intentionally go into the
// existing missing-assets.jsonl so the current mobile export flow can retrieve
// them without any shell/plugin changes.
const assetFlowRuntimeSeen = new Set();
const ASSET_FLOW_CHUNK_SIZE = 80;

// Only QuickStart/ParentVideos are on-demand. Activity-local video stays in
// its TAR because it is a required part of offline activity playback.
const isDeferredVideoAssetKey = (key = '') => (
  /^Files\/animations\/(?:QuickStart|ParentVideos)\/.+\.(?:mp4|webm|mov|m4v)$/i.test(String(key || ''))
);

const assetFlowTypeForKey = (key = '') => {
  const value = String(key || '').toLowerCase();
  if (/\.(mp4|webm|mov|m4v)(?:$|\?)/.test(value)) return 'video';
  if (/\.(mp3|wav|m4a|aac|ogg)(?:$|\?)/.test(value)) return 'audio';
  if (/\.(png|jpe?g|gif|webp|svg)(?:$|\?)/.test(value)) return 'image';
  if (/\.json(?:$|\?)/.test(value)) return 'data';
  if (/\.(woff2?|ttf|otf)(?:$|\?)/.test(value)) return 'font';
  return 'asset';
};

const assetFlowAreaForKey = (key = '') => {
  const value = String(key || '');
  if (value.startsWith('Images/Login/') || value.startsWith('Audio/Generic/LoginHelp/')) return 'login';
  if (value.startsWith('Images/GlobalButtons/')) return 'global-bar';
  if (value.startsWith('Images/SettingsDialog/')) return 'setup-settings';
  if (value.startsWith('Images/ReportMenuImages/')) return 'reports';
  if (value.startsWith('Files/animations/QuickStart/')) return 'quickstart-video';
  if (value.startsWith('Files/animations/ParentVideos/')) return 'parent-video';
  if (value.startsWith('Images/SuccessAnimations/')) return 'success-animation';
  if (value.startsWith('Audio/Generic/Feedback/')) return 'feedback-audio';
  if (value.startsWith('Audio/Generic/Strings/')) return 'strings-audio';
  if (value.startsWith('Audio/Generic/TeamHelp/')) return 'team-help-audio';
  if (value.startsWith('Audio/Generic/ActivityHelp/')) return 'activity-help-audio';
  if (value.startsWith('Audio/Generic/MenuHelp/')) return 'menu-help-audio';
  if (value.startsWith('Audio/Generic/WordsDB/')) return 'wordsdb-audio';
  if (value.startsWith('Themes/')) return 'theme';
  if (value.startsWith('public/Activities/')) return 'activity';
  if (value.startsWith('Images/FlinkPlay/')) return 'flink-play-ui';
  if (value.startsWith('Files/LandingPageAssets/')) return 'landing-page-assets';
  return 'other';
};

const assetFlowLocaleForKey = (key = '') => {
  const value = String(key || '');
  const prefixes = [
    'Files/animations/QuickStart/',
    'Files/animations/ParentVideos/',
    'Audio/Generic/LoginHelp/',
    'Audio/Generic/Strings/',
    'Audio/Generic/Feedback/',
    'Audio/Generic/TeamHelp/',
    'Audio/Generic/ActivityHelp/',
    'Audio/Generic/MenuHelp/',
    'Audio/Generic/WordsDB/',
  ];
  const prefix = prefixes.find((candidate) => value.startsWith(candidate));
  if (prefix) return value.slice(prefix.length).split('/')[0] || '';
  const versionMatch = value.match(/languageVersionData\/(immersion_[^/]+)\//i);
  return versionMatch?.[1] || '';
};

const assetFlowRoute = () => {
  try {
    return `${window.location?.pathname || ''}${window.location?.search || ''}`;
  } catch (_) {
    return '';
  }
};

const assetFlowRecordForKey = (key = '', extra = {}) => ({
  key: String(key || ''),
  assetType: assetFlowTypeForKey(key),
  area: assetFlowAreaForKey(key),
  locale: assetFlowLocaleForKey(key),
  route: assetFlowRoute(),
  activityId: String(offlineDiagnosticActivityContext.activityId || ''),
  template: String(offlineDiagnosticActivityContext.template || ''),
  screen: String(offlineDiagnosticActivityContext.screen || ''),
  ...extra,
});

const logAssetFlowRuntimeRequest = (rawUrl = '', extra = {}) => {
  const url = String(rawUrl || '').trim();
  if (!url || /^data:|^blob:/i.test(url)) return;
  const key = canonicalAssetKey(url);
  if (!key || !isOfflineManifestExactKey(key)) return;
  const dedupeKey = [
    assetFlowRoute(),
    offlineDiagnosticActivityContext.activityId || '',
    extra.channel || '',
    key,
  ].join('|');
  if (assetFlowRuntimeSeen.has(dedupeKey)) return;
  assetFlowRuntimeSeen.add(dedupeKey);
  const entry = getAssetMetaEntry(key);
  Promise.resolve(logOfflineDiagnosticEvent('asset-flow-runtime-request', assetFlowRecordForKey(key, {
    channel: String(extra.channel || ''),
    tag: String(extra.tag || ''),
    url,
    cached: Boolean(entry?.nativeUri),
    cachedBytes: Number(entry?.size || 0),
    connected: getOfflineState()?.connected !== false,
  }))).catch(() => undefined);
};

const logAssetFlowValue = (value = '', extra = {}) => {
  const text = String(value || '');
  if (!text) return;
  const urls = [];
  if (/url\(/i.test(text)) {
    text.replace(/url\((['"]?)(.*?)\1\)/gi, (match, quote, rawUrl) => {
      if (rawUrl) urls.push(rawUrl);
      return match;
    });
  } else if (String(extra.channel || '').includes('srcset')) {
    text.split(',').forEach((candidate) => {
      const rawUrl = candidate.trim().split(/\s+/)[0];
      if (rawUrl) urls.push(rawUrl);
    });
  } else {
    urls.push(text);
  }
  urls.forEach((url) => logAssetFlowRuntimeRequest(url, extra));
};

export async function logOfflineDiagnosticEvent(type, details = {}) {
  const fs = filesystem();
  if (!fs?.appendText || !type) return;

  const record = {
    time: new Date().toISOString(),
    type: String(type),
    ...details,
  };

  const write = async () => {
    try {
      await fs.appendText({
        path: MISSING_ASSET_LOG_PATH,
        text: `${JSON.stringify(record)}\n`,
      });
    } catch (error) {
      console.warn('[mobile-offline][diagnostic-event-log-failed]', error?.message || error);
    }
  };

  // Runtime asset diagnostics can emit many records at the same instant when
  // connectivity changes. Serialize appends so iOS/Android cannot interleave
  // JSONL writes and corrupt the diagnostic file.
  offlineDiagnosticWriteChain = offlineDiagnosticWriteChain.then(write, write);
  return offlineDiagnosticWriteChain;
}

const makeOfflineDiagnosticSession = (scope) => {
  const startedAtMs = Date.now();
  return {
    sessionId: `${scope}-${startedAtMs}-${Math.random().toString(36).slice(2, 10)}`,
    scope,
    startedAt: new Date(startedAtMs).toISOString(),
    startedAtMs,
    mode: scope === 'custom-level' ? 'package' : 'per-file',
    fallbackUsed: false,
  };
};

const stampDiagnostic = (session, name) => {
  if (!session) return;
  const ms = Date.now();
  session[`${name}At`] = new Date(ms).toISOString();
  session[`${name}AtMs`] = ms;
};

const readDiagnosticText = async (method, args = {}) => {
  try {
    const fs = filesystem();
    if (typeof fs?.[method] !== 'function') return '';
    const result = await fs[method](args);
    return String(result?.text || '');
  } catch (_) {
    return '';
  }
};

// Custom Level's diagnostics contain thousands of asset-flow lines and can time out.
// Do not keep the learner on the preparing screen while uploading them.
// Placement retains its existing diagnostics path unchanged.
function uploadReadyCustomDiagnosticsInBackground(session, playData, extra = {}) {
  if (typeof window === 'undefined' || typeof window.setTimeout !== 'function') return;
  window.setTimeout(() => {
    Promise.resolve(uploadOfflineDiagnostics(session, playData, extra)).catch((error) => {
      console.warn('[mobile-custom-content][background-diagnostics-upload-failed]', error);
    });
  }, 20000);
}

async function uploadOfflineDiagnostics(session, playData, extra = {}) {
  if (!session || !isOnline()) return { uploaded: false, reason: 'offline' };

  const completedAtMs = Date.now();
  const summary = {
    ...session,
    ...extra,
    completedAt: new Date(completedAtMs).toISOString(),
    completedAtMs,
    durationTotalMs: Math.max(0, completedAtMs - Number(session.startedAtMs || completedAtMs)),
    platform: String(window.Capacitor?.getPlatform?.() || 'web'),
    userAgent: typeof navigator !== 'undefined' ? String(navigator.userAgent || '') : '',
    generatedLevelId: String(
      playData?.mobileBootstrap?.generatedCustomContentLevel?._id
      || playData?.mobileBootstrap?.solutionMenu?.generatedCustomContentLevelId
      || '',
    ),
    familyMemberId: String(playData?.learner?._id || ''),
    familyId: String(playData?.family?._id || playData?.family?.familyId || ''),
    learningCenterId: String(playData?.learningCenter?._id || ''),
    productId: String(playData?.family?.productId || playData?.product?._id || ''),
  };

  const prepareEnd = Number(session.downloadStartedAtMs || session.packageDownloadStartedAtMs || session.manifestReadyAtMs || 0);
  if (prepareEnd && session.startedAtMs) summary.durationPrepareMs = Math.max(0, prepareEnd - session.startedAtMs);
  if (session.downloadStartedAtMs && session.downloadCompletedAtMs) {
    summary.durationDownloadMs = Math.max(0, session.downloadCompletedAtMs - session.downloadStartedAtMs);
  }
  if (session.verifyStartedAtMs && session.verifyCompletedAtMs) {
    summary.durationVerifyMs = Math.max(0, session.verifyCompletedAtMs - session.verifyStartedAtMs);
  }
  if (session.packagePrepareStartedAtMs && session.packageReadyAtMs) {
    summary.durationPackagePrepareMs = Math.max(0, session.packageReadyAtMs - session.packagePrepareStartedAtMs);
  }

  const [missingAssetsJsonl, debugLog] = await Promise.all([
    readDiagnosticText('readText', { path: MISSING_ASSET_LOG_PATH, directory: 'DATA' }),
    readDiagnosticText('readDebugLog'),
  ]);

  try {
    const response = await axios.post('/api/mobile-v2/offline/diagnostics', {
      summary,
      missingAssetsJsonl,
      debugLog,
    }, { timeout: 10000 });
    const prefix = response?.data?.data?.prefix || '';
    await logOfflineDiagnosticEvent('offline-diagnostics-uploaded', {
      scope: summary.scope,
      sessionId: summary.sessionId,
      prefix,
    });
    return { uploaded: true, prefix };
  } catch (error) {
    await logOfflineDiagnosticEvent('offline-diagnostics-upload-failed', {
      scope: summary.scope,
      sessionId: summary.sessionId,
      errorMessage: error?.message || String(error),
      status: Number(error?.response?.status || 0),
    });
    return { uploaded: false, reason: error?.message || String(error) };
  }
}

export function setOfflineDiagnosticActivityContext(context = null) {
  if (!context) {
    offlineDiagnosticActivityContext = { activityId: '', template: '', screen: 'activity' };
    return;
  }
  offlineDiagnosticActivityContext = {
    activityId: String(context.activityId || ''),
    template: String(context.template || ''),
    screen: String(context.screen || 'activity'),
  };
}

const missingAssetType = (key = '') => {
  const value = String(key || '').toLowerCase();
  if (/\.(png|jpe?g|gif|webp|svg)(?:$|\?)/.test(value)) return 'image';
  if (/\.(mp3|wav|m4a|aac|ogg)(?:$|\?)/.test(value)) return 'audio';
  if (/\.(mp4|webm|mov|m4v)(?:$|\?)/.test(value)) return 'video';
  if (/\.(json)(?:$|\?)/.test(value)) return 'data';
  if (/\.(xml)(?:$|\?)/.test(value)) return 'xml';
  if (/\.(woff2?|ttf|otf)(?:$|\?)/.test(value)) return 'font';
  if (/\.(pdf)(?:$|\?)/.test(value)) return 'document';
  if (value.includes('/audio/')) return 'audio';
  if (value.includes('/images/')) return 'image';
  return 'asset';
};

async function logPlacementManifestDiagnostics(files = [], totalBytes = 0) {
  const fs = filesystem();
  if (!fs?.appendText || !Array.isArray(files) || !files.length) return;

  const groups = new Map();
  files.forEach((entry) => {
    const key = String(entry?.key || '');
    if (!key) return;
    const parts = key.split('/').filter(Boolean);
    let group = parts.slice(0, 2).join('/') || parts[0] || 'unknown';
    if (parts[0] === 'public' && parts[1] === 'Activities' && parts[2]) {
      group = `public/Activities/${parts[2]}`;
    }
    const current = groups.get(group) || { bytes: 0, files: 0 };
    current.bytes += Number(entry?.size || 0);
    current.files += 1;
    groups.set(group, current);
  });

  const now = new Date().toISOString();
  const lines = [JSON.stringify({
    time: now,
    type: 'placement-manifest-summary',
    totalBytes: Number(totalBytes || 0),
    fileCount: files.length,
  })];

  [...groups.entries()]
    .sort((left, right) => right[1].bytes - left[1].bytes)
    .forEach(([group, value]) => {
      lines.push(JSON.stringify({
        time: now,
        type: 'placement-manifest-group',
        group,
        bytes: value.bytes,
        files: value.files,
      }));
    });

  files
    .slice()
    .sort((left, right) => Number(right?.size || 0) - Number(left?.size || 0))
    .forEach((entry) => {
      lines.push(JSON.stringify({
        time: now,
        type: 'placement-manifest-file',
        source: String(entry?.source || ''),
        path: String(entry?.key || ''),
        bytes: Number(entry?.size || 0),
      }));
    });

  try {
    await fs.appendText({
      path: MISSING_ASSET_LOG_PATH,
      text: `${lines.join('\n')}\n`,
    });
  } catch (error) {
    console.warn('[mobile-offline][placement-manifest-log-failed]', error?.message || error);
  }
}

async function logMobileManifestAudit({ scope, requests = [], batchResults = [], manifest = {} } = {}) {
  const fs = filesystem();
  if (!fs?.appendText || !Array.isArray(requests) || !Array.isArray(batchResults)) return;

  const identityFor = (entry = {}) => `${String(entry?.source || '')}:${String(entry?.key || '')}`;
  const precise = new Map();
  const runtime = new Map();
  const preciseOrigin = new Map();

  requests.forEach((request = {}, index) => {
    const batch = batchResults[index] || {};
    const entries = Array.isArray(batch?.files) ? batch.files : [];
    const isRuntimeBaseline = request.includeRuntimeBaseline === true;
    const isActivity = Array.isArray(request.activityIds) && request.activityIds.length > 0;
    const isExact = Array.isArray(request.assetKeys) && request.assetKeys.length > 0;

    entries.forEach((entry) => {
      if (!entry?.key) return;
      const identity = identityFor(entry);
      if (isRuntimeBaseline) {
        if (!runtime.has(identity)) runtime.set(identity, entry);
        return;
      }

      // Activity batches and exact-key batches are the content we can prove is
      // requested by the current mobile flow without expanding a whole prefix.
      if (isActivity || isExact) {
        if (!precise.has(identity)) precise.set(identity, entry);
        const origins = preciseOrigin.get(identity) || new Set();
        if (isActivity) origins.add('activity-content');
        if (isExact) origins.add('exact-reference');
        preciseOrigin.set(identity, origins);
      }
    });
  });

  const runtimeUsed = [];
  const runtimeFilterCandidates = [];
  runtime.forEach((entry, identity) => {
    if (precise.has(identity)) runtimeUsed.push(entry);
    else runtimeFilterCandidates.push(entry);
  });

  const usedByMobile = new Map(precise);
  runtimeUsed.forEach((entry) => usedByMobile.set(identityFor(entry), entry));

  const sumBytes = (entries = []) => entries.reduce((sum, entry) => sum + Number(entry?.size || 0), 0);
  const manifestFiles = Array.isArray(manifest?.files) ? manifest.files : [];

  const prefixStats = new Map();
  runtime.forEach((entry, identity) => {
    const prefix = auditPrefixForKey(entry?.key);
    const current = prefixStats.get(prefix) || {
      prefix,
      totalFiles: 0,
      totalBytes: 0,
      usedFiles: 0,
      usedBytes: 0,
      filterCandidateFiles: 0,
      filterCandidateBytes: 0,
    };
    const bytes = Number(entry?.size || 0);
    current.totalFiles += 1;
    current.totalBytes += bytes;
    if (precise.has(identity)) {
      current.usedFiles += 1;
      current.usedBytes += bytes;
    } else {
      current.filterCandidateFiles += 1;
      current.filterCandidateBytes += bytes;
    }
    prefixStats.set(prefix, current);
  });

  const usedEntries = [...usedByMobile.values()];
  const currentBytes = Number(manifest?.totalBytes || sumBytes(manifestFiles));
  const usedBytes = sumBytes(usedEntries);
  const candidateBytes = sumBytes(runtimeFilterCandidates);
  const now = new Date().toISOString();
  const lines = [];

  lines.push(JSON.stringify({
    time: now,
    type: 'mobile-asset-audit-summary',
    scope: String(scope || 'unknown'),
    currentManifestFiles: manifestFiles.length,
    currentManifestBytes: currentBytes,
    usedByMobileFiles: usedEntries.length,
    usedByMobileBytes: usedBytes,
    runtimeBaselineFiles: runtime.size,
    runtimeBaselineBytes: sumBytes([...runtime.values()]),
    filterCandidateFiles: runtimeFilterCandidates.length,
    filterCandidateBytes: candidateBytes,
    projectedManifestBytesWithoutPrefixOnlyCandidates: Math.max(0, currentBytes - candidateBytes),
    note: 'Audit only. No manifest files were removed.',
  }));

  [...prefixStats.values()]
    .sort((left, right) => right.filterCandidateBytes - left.filterCandidateBytes)
    .forEach((stat) => {
      lines.push(JSON.stringify({
        time: now,
        type: 'mobile-asset-audit-prefix',
        scope: String(scope || 'unknown'),
        ...stat,
      }));
    });

  const preciseBreakdown = new Map();
  precise.forEach((entry, identity) => {
    const origin = [...(preciseOrigin.get(identity) || [])].sort().join('+') || 'precise';
    const current = preciseBreakdown.get(origin) || { files: 0, bytes: 0 };
    current.files += 1;
    current.bytes += Number(entry?.size || 0);
    preciseBreakdown.set(origin, current);
  });
  [...preciseBreakdown.entries()].forEach(([origin, value]) => {
    lines.push(JSON.stringify({
      time: now,
      type: 'mobile-asset-audit-used',
      scope: String(scope || 'unknown'),
      origin,
      files: value.files,
      bytes: value.bytes,
    }));
  });

  // Keep the log useful without turning diagnostics into another multi-megabyte
  // asset dump: include only the largest prefix-only candidates. Prefix totals
  // above always contain the complete file/byte counts.
  runtimeFilterCandidates
    .slice()
    .sort((left, right) => Number(right?.size || 0) - Number(left?.size || 0))
    .slice(0, 100)
    .forEach((entry) => {
      lines.push(JSON.stringify({
        time: now,
        type: 'mobile-asset-audit-filter-candidate',
        scope: String(scope || 'unknown'),
        prefix: auditPrefixForKey(entry?.key),
        source: String(entry?.source || ''),
        path: String(entry?.key || ''),
        bytes: Number(entry?.size || 0),
      }));
    });

  try {
    await fs.appendText({
      path: MISSING_ASSET_LOG_PATH,
      text: `${lines.join('\n')}\n`,
    });
    console.log('[mobile-offline][asset-audit]', {
      scope: String(scope || 'unknown'),
      currentManifestFiles: manifestFiles.length,
      currentManifestBytes: currentBytes,
      usedByMobileFiles: usedEntries.length,
      usedByMobileBytes: usedBytes,
      filterCandidateFiles: runtimeFilterCandidates.length,
      filterCandidateBytes: candidateBytes,
      prefixes: [...prefixStats.values()],
    });
  } catch (error) {
    console.warn('[mobile-offline][asset-audit-log-failed]', error?.message || error);
  }
}

function logMissingOfflineAsset(url, key) {
  if (/^data:/i.test(String(url || ''))) return;
  const fs = filesystem();
  const activityId = String(offlineDiagnosticActivityContext.activityId || '');
  const path = String(key || stripQuery(url) || '');
  if (!path || !fs?.appendText) return;

  // Do not require activityId here. Custom Level main-menu assets (logo,
  // backgrounds, navigation controls) are rendered outside an Activity and
  // were previously invisible to missing-assets diagnostics.
  const dedupeKey = `${runtimeAssetDiagnosticEpoch}|${activityId}|${offlineDiagnosticActivityContext.screen || ''}|${path}`;
  if (missingAssetLogKeys.has(dedupeKey)) return;
  missingAssetLogKeys.add(dedupeKey);

  const entry = key ? getAssetMetaEntry(key) : null;
  Promise.resolve(logOfflineDiagnosticEvent('runtime-asset-missing', {
    activityId,
    assetType: missingAssetType(path),
    path,
    url: String(url || ''),
    key: String(key || ''),
    hasAssetMeta: Boolean(entry),
    nativeUri: String(entry?.nativeUri || ''),
    template: offlineDiagnosticActivityContext.template || '',
    screen: offlineDiagnosticActivityContext.screen || 'activity',
    connected: getOfflineState()?.connected !== false,
    navigatorOnline: typeof navigator === 'undefined' ? null : navigator.onLine !== false,
    epoch: runtimeAssetDiagnosticEpoch,
  })).catch((error) => {
    missingAssetLogKeys.delete(dedupeKey);
    console.warn('[mobile-offline][missing-asset-log-failed]', error?.message || error);
  });
}

export function canonicalAssetKey(url = '') {
  const clean = stripQuery(url);
  if (/^data:/i.test(clean)) return '';

  let path = clean;
  try { path = new URL(clean, window.location.href).pathname || clean; } catch (_) {}

  // S3 keys are stored decoded (for example "Pet Tricks.jpg"), while the
  // browser/runtime can request the same key as "Pet%20Tricks.jpg". Decode
  // valid URI escapes so both forms resolve to the same local entry, but never
  // allow a malformed '%' sequence to abort Placement preparation.
  const rawPath = String(path || '');
  try { path = decodeURIComponent(rawPath); } catch (_) { path = rawPath; }
  path = String(path || '').replace(/^\/+/, '');

  const markers = ['public/Activities/', 'public/Users/', 'Images/', 'Audio/', 'Files/', 'Themes/'];
  const marker = markers.find((entry) => path.includes(entry));
  if (!marker) return '';
  return path.slice(path.indexOf(marker));
}

const safePath = (entry, manifestVersion = 'active') => `${ROOT}/packs/${manifestVersion}/${entry.source || 'asset'}/${String(entry.key || '')}`
  .replace(/\.\./g, '_')
  .replace(/\/{2,}/g, '/');

const parentPath = (path) => path.split('/').slice(0, -1).join('/');

async function ensureDirectory(path) {
  const fs = filesystem();
  const dir = parentPath(path);
  if (!fs?.mkdir || !dir || createdDirs.has(dir)) return;
  try { await fs.mkdir({ path: dir, directory: 'DATA', recursive: true }); } catch (_) {}
  createdDirs.add(dir);
}

async function loadMeta() {
  if (initialized) return assetMeta;
  initialized = true;
  assetMeta = (await dbGet(META_KEY, {})) || {};
  return assetMeta;
}

async function saveMeta() {
  await dbSet(META_KEY, assetMeta);
}

const OFFLINE_NATIVE_ROOT_MARKER = '/FLCOffline/';

const decodeOfflineNativeRelativePath = (value = '') => {
  const raw = String(value || '').trim();
  if (!raw) return '';

  // Stored iOS URIs contain an app-container UUID. That UUID is not stable
  // across every install/update even when Application Support data survives.
  // Keep only the path below FLCOffline and ask the native plugin for the
  // current container root at launch.
  let candidate = raw;
  let markerIndex = candidate.indexOf(OFFLINE_NATIVE_ROOT_MARKER);
  if (markerIndex < 0) {
    try { candidate = decodeURI(candidate); } catch (_) {}
    markerIndex = candidate.indexOf(OFFLINE_NATIVE_ROOT_MARKER);
  }
  if (markerIndex < 0) return '';

  const relative = candidate.slice(markerIndex + OFFLINE_NATIVE_ROOT_MARKER.length);
  return relative
    .split('/')
    .filter(Boolean)
    .map((segment) => {
      try { return decodeURIComponent(segment); } catch (_) { return segment; }
    })
    .join('/');
};

const offlineNativeRootFromUri = (value = '') => {
  const raw = String(value || '').trim();
  if (!raw) return '';
  const markerIndex = raw.indexOf(OFFLINE_NATIVE_ROOT_MARKER);
  if (markerIndex < 0) return '';
  return raw.slice(0, markerIndex + OFFLINE_NATIVE_ROOT_MARKER.length);
};

const rebasePersistedAssetNativeUris = async () => {
  const fs = filesystem();
  const entries = Object.entries(assetMeta || {});
  if (!entries.length || !fs?.getUri) {
    return { status: 'skipped', changedCount: 0, anchorKey: '', currentRootUri: '' };
  }

  const preferredKeys = [
    'Files/LandingPageAssets/Logo/Family_Logo_2024.svg',
    'Images/GlobalButtons/logout.png',
    'Images/FlinkPlay/stars/1-2.png',
    'Themes/KidsPatterns/Backgrounds/background2.jpg',
    ...REQUIRED_EXACT_RUNTIME_KEYS,
    ...entries.map(([key]) => key),
  ];
  const seen = new Set();
  const candidates = preferredKeys.filter((key) => {
    const normalized = String(key || '');
    if (!normalized || seen.has(normalized)) return false;
    seen.add(normalized);
    return true;
  });

  let anchorKey = '';
  let currentRootUri = '';
  let anchorRelativePath = '';

  // A healthy install normally resolves the first common-ui key. Keep a small
  // bounded fallback set so launch never turns into a 4k-file verification pass.
  for (const key of candidates.slice(0, 64)) {
    const entry = getAssetMetaEntry(key);
    const relativePath = decodeOfflineNativeRelativePath(entry?.nativeUri || '');
    if (!relativePath) continue;
    try {
      const current = await fs.getUri({ path: relativePath, directory: 'DATA' });
      const currentUri = String(current?.uri || current?.path || '');
      const rootUri = offlineNativeRootFromUri(currentUri);
      if (!currentUri || !rootUri) continue;
      anchorKey = key;
      currentRootUri = rootUri;
      anchorRelativePath = relativePath;
      break;
    } catch (_) {}
  }

  if (!currentRootUri) {
    await logOfflineDiagnosticEvent('runtime-asset-meta-rebase-unavailable', {
      assetMetaCount: entries.length,
      attemptedAnchors: Math.min(candidates.length, 64),
    });
    return { status: 'unavailable', changedCount: 0, anchorKey: '', currentRootUri: '' };
  }

  let changedCount = 0;
  const previousRoots = new Set();
  Object.entries(assetMeta || {}).forEach(([key, entry]) => {
    if (!entry?.nativeUri) return;
    const relativePath = decodeOfflineNativeRelativePath(entry.nativeUri);
    if (!relativePath) return;
    const previousRoot = offlineNativeRootFromUri(entry.nativeUri);
    if (previousRoot) previousRoots.add(previousRoot);
    const nextNativeUri = `${currentRootUri}${encodeNativeRelativePath(relativePath)}`;
    if (nextNativeUri === entry.nativeUri) return;
    assetMeta[key] = { ...entry, nativeUri: nextNativeUri };
    changedCount += 1;
  });

  if (changedCount) await saveMeta();
  await logOfflineDiagnosticEvent('runtime-asset-meta-rebased', {
    status: 'ready',
    assetMetaCount: entries.length,
    changedCount,
    previousRootCount: previousRoots.size,
    anchorKey,
    anchorRelativePath,
    currentRootUri,
  });
  return { status: 'ready', changedCount, anchorKey, currentRootUri };
};


async function loadPackMeta() {
  if (packMetaInitialized) return packMeta;
  packMetaInitialized = true;
  packMeta = (await dbGet(PACK_META_KEY, {})) || {};
  return packMeta;
}

async function savePackMeta() {
  await dbSet(PACK_META_KEY, packMeta);
}

const READY_SIGNATURE_CACHE_LIMIT = 12;

const getReadyPackForSignature = (scope, signature) => {
  const current = packMeta?.[scope];
  if (current?.status === 'ready' && current?.signature === signature) return current;
  return packMeta?.readyBySignature?.[scope]?.[signature] || null;
};

const rememberReadyPackForSignature = (scope, signature, record) => {
  packMeta[scope] = record;
  packMeta.readyBySignature = packMeta.readyBySignature || {};
  const scoped = { ...(packMeta.readyBySignature[scope] || {}) };
  scoped[signature] = record;
  const trimmed = Object.entries(scoped)
    .sort((left, right) => Number(right?.[1]?.readyAt || 0) - Number(left?.[1]?.readyAt || 0))
    .slice(0, READY_SIGNATURE_CACHE_LIMIT);
  packMeta.readyBySignature[scope] = Object.fromEntries(trimmed);
};

let prebuiltMetaSaveChain = Promise.resolve();

const queuePrebuiltMetaSave = () => {
  const assetSnapshot = { ...assetMeta };
  const packSnapshot = {
    ...packMeta,
    prebuiltPacks: { ...(packMeta.prebuiltPacks || {}) },
  };
  prebuiltMetaSaveChain = prebuiltMetaSaveChain.then(() => Promise.all([
    dbSet(META_KEY, assetSnapshot),
    dbSet(PACK_META_KEY, packSnapshot),
  ]));
  return prebuiltMetaSaveChain;
};

// A ready Custom Level record can survive an iOS app-container relocation or
// a partially cleaned download. Probe only a few real native files rather than
// scanning hundreds of Activity assets on every login. Placement is untouched.
async function verifySavedCustomLevelStorage(savedPack) {
  if (!savedPack || savedPack.mode !== 'prebuilt') return true;
  const fs = filesystem();
  if (!fs?.getUri) return false;

  const versions = Array.isArray(savedPack.prebuiltPackVersions)
    ? savedPack.prebuiltPackVersions : [];
  const selected = [versions[0], versions[Math.floor(versions.length / 2)], versions[versions.length - 1]]
    .filter(Boolean);
  const wanted = new Map(selected.map((item) => [String(item.packId || ''), String(item.version || '')]));
  const samples = new Map();
  let directSample = null;
  for (const [key, entry] of Object.entries(assetMeta)) {
    if (!entry?.nativeUri) continue;
    const packId = String(entry.prebuiltPackId || '');
    if (wanted.has(packId) && String(entry.prebuiltPackVersion || '') === wanted.get(packId)) {
      if (!samples.has(packId)) samples.set(packId, { key, entry });
    } else if (!directSample && !packId
      && String(entry.manifestVersion || '') === String(savedPack.manifestVersion || '')) {
      directSample = { key, entry };
    }
  }
  const missingPackIds = [...wanted.keys()].filter((id) => !samples.has(id));
  if (missingPackIds.length) {
    missingPackIds.forEach((id) => { delete packMeta.prebuiltPacks?.[id]; });
    await queuePrebuiltMetaSave();
    return false;
  }
  const probes = [...wanted.keys()].map((id) => samples.get(id)).filter(Boolean);
  if (directSample) probes.push(directSample);
  if (!probes.length) return false;

  let invalid = false;
  let relocated = false;
  for (const { entry } of probes) {
    const oldUri = String(entry.nativeUri || '');
    const marker = '/FLCOffline/';
    const markerIndex = oldUri.indexOf(marker);
    if (markerIndex < 0) { invalid = true; }
    let resolved = null;
    if (markerIndex >= 0) {
      try {
        const nativePath = decodeURIComponent(oldUri.slice(markerIndex + marker.length).split(/[?#]/)[0]);
        resolved = await fs.getUri({ path: nativePath, directory: 'DATA' });
      } catch (_) { /* Missing on-device content must be fetched again. */ }
    }
    if (!resolved?.uri || Number(resolved.size || 0) !== Number(entry.size || 0)) {
      invalid = true;
      const packId = String(entry.prebuiltPackId || '');
      if (packId) {
        delete packMeta.prebuiltPacks?.[packId];
      } else {
        // Shared direct-file aliases can point to the same physical URI.
        for (const assetKey of Object.keys(assetMeta)) {
          if (assetMeta[assetKey]?.nativeUri === oldUri) delete assetMeta[assetKey];
        }
      }
      continue;
    }
    const newUri = String(resolved.uri);
    const rootMarker = '/flc-offline-v1/';
    const oldIndex = oldUri.indexOf(rootMarker);
    const newIndex = newUri.indexOf(rootMarker);
    if (oldUri !== newUri && oldIndex >= 0 && newIndex >= 0) {
      // Swift returns the current app sandbox root, which can change on iOS
      // update. The relative files remain identical, so refresh every URI.
      const oldPrefix = oldUri.slice(0, oldIndex + 1);
      const newPrefix = newUri.slice(0, newIndex + 1);
      for (const meta of Object.values(assetMeta)) {
        if (String(meta?.nativeUri || '').startsWith(oldPrefix + 'flc-offline-v1/')) {
          meta.nativeUri = newPrefix + String(meta.nativeUri).slice(oldPrefix.length);
          relocated = true;
        }
      }
    }
  }
  if (invalid || relocated) await queuePrebuiltMetaSave();
  return !invalid;
}

const hashSignature = (value = '') => {
  let hash = 5381;
  const input = String(value || '');
  for (let index = 0; index < input.length; index += 1) {
    hash = ((hash << 5) + hash) ^ input.charCodeAt(index);
  }
  return (hash >>> 0).toString(36);
};

const normalizeOfflineLanguageCode = (value = '') => {
  const code = String(value || '').trim().toLowerCase().split(/[-_]/)[0];
  return /^[a-z]{2,3}$/.test(code) ? code : '';
};

const getMobileSelectedLanguageCode = () => {
  if (typeof window === 'undefined') return '';
  try {
    return normalizeOfflineLanguageCode(window.localStorage?.getItem('localeCode') || '');
  } catch (_) {
    return '';
  }
};

const getGeneratedCustomContentInstructionLanguageCode = (playData) => {
  const generatedLevel = playData?.mobileBootstrap?.generatedCustomContentLevel || {};
  const solutionId = String(playData?.mobileBootstrap?.solutionId || '');
  const selectedSolution = (playData?.product?.solutions || [])
    .find((solution) => String(solution?._id || '') === solutionId)
    || (playData?.product?.solutions || [])[0]
    || null;

  return normalizeOfflineLanguageCode(
    generatedLevel?.instructionLanguage
    || generatedLevel?.contentPlan?.instructionLanguage
    || playData?.learner?.customContentOnboarding?.instructionLanguage
    || playData?.mobileBootstrap?.offlineAssessment?.instructionLanguage
    || selectedSolution?.activityLocale
    || selectedSolution?.locale
    || 'en'
  ) || 'en';
};

// Match the Activity locale actually selected by the FlinkPlay runtime.
// For a transition Solution the authored Activity language (usually EN) must
// stay independent of the localized interface/help language. Keep Placement's
// existing locale flow unchanged.
const getGeneratedCustomContentActivityLanguageCode = (playData) => {
  const solutionId = String(playData?.mobileBootstrap?.solutionId || '');
  const selectedSolution = playData?.solution
    || (playData?.product?.solutions || []).find(
      (solution) => String(solution?._id || '') === solutionId,
    )
    || null;
  const authoredLocale = normalizeOfflineLanguageCode(selectedSolution?.activityLocale);
  if (String(selectedSolution?.languageMode || '').trim().toLowerCase() === 'transition'
    && authoredLocale) {
    return authoredLocale;
  }
  const runtimeLocale = normalizeOfflineLanguageCode(
    playData?.activityLocale?.code || playData?.activityLocale,
  );
  return runtimeLocale || normalizeOfflineLanguageCode(
    playData?.mobileBootstrap?.generatedCustomContentLevel?.activityLocale
    || playData?.mobileBootstrap?.generatedCustomContentLevel?.contentPlan?.activityLocale,
  ) || getGeneratedCustomContentInstructionLanguageCode(playData);
};

const getGeneratedCustomContentInterfaceLanguageCode = (playData) => (
  normalizeOfflineLanguageCode(
    playData?.learner?.customContentOnboarding?.explanationLanguage
    || playData?.mobileBootstrap?.offlineAssessment?.explanationLanguage
    || getMobileSelectedLanguageCode()
    || 'en'
  ) || 'en'
);

const buildPackSignature = (playData, scope, activityIds = []) => {
  const activities = playData?.mobileBootstrap?.solutionMenu?.activities || [];
  const stableActivityIds = [...activityIds]
    .map((activityId) => String(activityId || ''))
    .filter(Boolean)
    .sort();
  const activityIdSet = new Set(stableActivityIds);
  const activityVersionData = activities
    .filter((activity) => activityIdSet.has(String(activity?._id || '')))
    .map((activity) => [
      String(activity?._id || ''),
      String(activity?.updatedAt || activity?.modifiedAt || activity?.lastModified || ''),
      String(activity?.contentFolder || ''),
      String(activity?.templateId || activity?.template?._id || ''),
    ])
    .sort((left, right) => String(left[0]).localeCompare(String(right[0])));

  // Custom Level offline data belongs to the generated level/content itself, not
  // to the transient authenticated session. Keeping learner/family/LC/product in
  // this signature made the same already-downloaded level look new after logout
  // + login. Placement keeps its existing account-scoped identity.
  const payload = scope === 'custom-level'
    ? {
      scope,
      solutionId: String(playData?.mobileBootstrap?.solutionId || ''),
      generatedLevelId: String(playData?.mobileBootstrap?.generatedCustomContentLevel?._id || ''),
      // Bump only the Custom Level package identity when its offline completeness
      // contract changes. Placement keeps its existing signature/cache untouched.
      packSchema: CUSTOM_LEVEL_PACK_SCHEMA,
      instructionLanguage: getGeneratedCustomContentActivityLanguageCode(playData),
      activityIds: stableActivityIds,
      activityVersionData,
    }
    : {
      scope,
      packSchema: PLACEMENT_PACK_SCHEMA,
      learnerId: String(playData?.learner?._id || ''),
      familyId: String(playData?.family?._id || playData?.family?.familyId || ''),
      learningCenterId: String(playData?.learningCenter?._id || ''),
      productId: String(playData?.family?.productId || playData?.product?._id || ''),
      solutionId: String(playData?.mobileBootstrap?.solutionId || ''),
      generatedLevelId: String(playData?.mobileBootstrap?.generatedCustomContentLevel?._id || ''),
      // The prebuilt Placement manifest is versioned from the exact Activity pack
      // versions. Include it so updated P1/P2/P3/P4 content cannot be hidden by a
      // previously-ready local package with the same Activity IDs.
      instructionLanguage: normalizeOfflineLanguageCode(
        playData?.mobileBootstrap?.offlineAssessment?.instructionLanguage
      ) || 'en',
      prebuiltManifestVersion: String(
        playData?.mobileBootstrap?.offlineAssessment?.prebuiltManifestVersion || ''
      ),
      ...(isVmPlacementSourceRefresh(
        scope,
        playData?.mobileBootstrap?.offlineAssessment?.ageBand
          || playData?.learner?.customContentOnboarding?.ageBand,
        playData?.mobileBootstrap?.offlineAssessment?.instructionLanguage
          || playData?.learner?.customContentOnboarding?.instructionLanguage,
        stableActivityIds,
      ) ? { vmSourceRefresh: VM_PLACEMENT_REFRESH_SCHEMA } : {}),
      activityIds: stableActivityIds,
      activityVersionData,
      assessmentSlots: (playData?.mobileBootstrap?.offlineAssessment?.assessmentActivitySlots || []).map((slot) => [
        String(slot?.activityKey || ''),
        String(slot?.sourceActivityId || ''),
      ]),
    };

  return hashSignature(JSON.stringify(payload));
};

export async function isOfflinePackReadyForPlayData(playData, scope = 'placement') {
  if (!playData) return false;
  await loadMeta();
  await loadPackMeta();

  const normalizedScope = normalizeSyncScope(scope, playData);
  const allActivities = playData?.mobileBootstrap?.solutionMenu?.activities || [];
  const placementIds = playData?.mobileBootstrap?.offlineAssessment?.assessmentLeafActivityIds || [];
  const placementSlotIds = (playData?.mobileBootstrap?.offlineAssessment?.assessmentActivitySlots || [])
    .map((slot) => String(slot?.sourceActivityId || ''))
    .filter(Boolean);
  const placementActivityIds = [...new Set([
    ...placementIds.map((activityId) => String(activityId || '')).filter(Boolean),
    ...placementSlotIds,
  ])];
  const generatedCustomLevelActivityIds = normalizedScope === 'custom-level'
    ? getGeneratedCustomLevelActivityIds(playData)
    : [];
  const activityIds = normalizedScope === 'placement'
    ? placementActivityIds
    : (generatedCustomLevelActivityIds.length
      ? generatedCustomLevelActivityIds
      : [...new Set(allActivities.map((activity) => String(activity?._id || '')).filter(Boolean))]);

  setActiveVmPlacementDataScope(isVmPlacementSourceRefresh(
    normalizedScope,
    playData?.mobileBootstrap?.offlineAssessment?.ageBand
      || playData?.learner?.customContentOnboarding?.ageBand,
    playData?.mobileBootstrap?.offlineAssessment?.instructionLanguage
      || playData?.learner?.customContentOnboarding?.instructionLanguage,
    activityIds,
  ));
  if (!activityIds.length) return false;

  const signature = buildPackSignature(playData, normalizedScope, activityIds);
  const savedPack = getReadyPackForSignature(normalizedScope, signature);
  const offlinePackageMode = getOfflinePackageMode();
  const savedPackMode = String(savedPack?.mode || 'legacy');
  const modeMatches = offlinePackageMode === 'legacy'
    ? savedPackMode !== 'prebuilt'
    : savedPackMode === 'prebuilt';

  // A generated Custom Level/Placement package that is already active locally
  // must stay ready across app restarts. Do not force an online re-resolve on
  // every launch: that made the UI show the full package size again and could
  // trigger a complete legacy re-download after a transient resolver failure.
  return Boolean(
    modeMatches
    && savedPack?.status === 'ready'
    && savedPack?.signature === signature
    && Number(savedPack?.fileCount || 0) > 0
    && hasRequiredExactRuntimeAssets()
    && (normalizedScope !== 'custom-level' || await verifySavedCustomLevelStorage(savedPack))
  );
}

const isAlreadyLocalOfflineUrl = (url = '') => {
  const value = String(url || '').trim();
  return /^(?:data:|blob:|file:|capacitor:|ionic:)/i.test(value)
    || value.includes('/_capacitor_file_/')
    || value.includes('/_app_file_/');
};

const isNativeAssetRuntime = () => {
  try {
    if (window.Capacitor?.isNativePlatform?.()) return true;
    const platform = String(window.Capacitor?.getPlatform?.() || '').toLowerCase();
    return platform === 'ios' || platform === 'android';
  } catch (_) {
    return false;
  }
};

// Use successfully downloaded localized media when the same VM Activity
// opens online. A native/local cache hit is required; unresolved URLs and
// every other locale continue through the original networking behavior.
const preferCachedVmMedia = (url = '') => {
  if (!isNativeAssetRuntime() || !isOnline() || isAlreadyLocalOfflineUrl(url)) return url;
  const activityId = String(offlineDiagnosticActivityContext.activityId || '');
  if (!isActiveVmPlacementActivity(activityId)) return url;
  const key = canonicalAssetKey(url);
  if (!key?.startsWith(`public/Activities/${activityId}/languageVersionData/immersion_vm/`)) return url;
  const cached = getAssetMetaEntry(key);
  return cached?.nativeUri ? convertFileSrc(cached.nativeUri) : url;
};

const shouldResolveFromOfflineStorage = () => {
  // In a bundled Capacitor app the native Network plugin is authoritative.
  // navigator.onLine can lag behind iOS by seconds after Wi-Fi reconnects;
  // treating that lag as "still offline" leaves DOM assets pinned to local URLs.
  if (isNativeAssetRuntime()) return !isOnline();
  return typeof navigator !== 'undefined' && navigator.onLine === false;
};

const logRuntimeAssetResolverDecision = (url, key, entry, status, resolvedUrl) => {
  const dedupeKey = `${runtimeAssetDiagnosticEpoch}|${status}|${key || url}`;
  if (runtimeAssetResolverLogKeys.has(dedupeKey)) return;
  runtimeAssetResolverLogKeys.add(dedupeKey);
  Promise.resolve(logOfflineDiagnosticEvent('runtime-asset-resolver', {
    epoch: runtimeAssetDiagnosticEpoch,
    status,
    url: String(url || ''),
    key: String(key || ''),
    hasAssetMeta: Boolean(entry),
    nativeUri: String(entry?.nativeUri || ''),
    resolvedUrl: String(resolvedUrl || ''),
    screen: offlineDiagnosticActivityContext.screen || 'activity',
    activityId: String(offlineDiagnosticActivityContext.activityId || ''),
    connected: getOfflineState()?.connected !== false,
    navigatorOnline: typeof navigator === 'undefined' ? null : navigator.onLine !== false,
  })).catch(() => undefined);
};

export function resolveOfflineAssetUrlSync(url) {
  if (!url || !shouldResolveFromOfflineStorage() || isAlreadyLocalOfflineUrl(url)) return url;
  const key = canonicalAssetKey(url);
  const entry = key ? getAssetMetaEntry(key) : null;
  if (entry?.nativeUri) {
    const resolvedUrl = convertFileSrc(entry.nativeUri);
    logRuntimeAssetResolverDecision(url, key, entry, 'hit', resolvedUrl);
    return resolvedUrl;
  }

  logRuntimeAssetResolverDecision(url, key, entry, 'miss', url);
  logMissingOfflineAsset(url, key);

  const now = Date.now();
  if (now - missingNoticeAt > 1200) {
    missingNoticeAt = now;
    console.warn('[mobile-offline][runtime-asset-missing]', { url, key });
    window.dispatchEvent(new CustomEvent('flc:offline-content-missing', {
      detail: { url, key },
    }));
  }
  return url;
}

export function getOfflineAssetEntrySync(keyOrUrl = '') {
  const key = canonicalAssetKey(keyOrUrl);
  return key ? getAssetMetaEntry(key) : null;
}

export function hasOfflineAssetSync(keyOrUrl = '') {
  return Boolean(getOfflineAssetEntrySync(keyOrUrl)?.nativeUri);
}

export function resolveOfflineAssetKeySync(keyOrUrl = '') {
  const entry = getOfflineAssetEntrySync(keyOrUrl);
  return entry?.nativeUri ? convertFileSrc(entry.nativeUri) : '';
}

export function listOfflineAssetKeysSync(prefix = '') {
  const normalizedPrefix = canonicalAssetKey(prefix) || String(prefix || '').replace(/^\/+/, '');
  if (!normalizedPrefix) return [];
  return Object.keys(assetMeta).filter((key) => key.startsWith(normalizedPrefix));
}


// Persist a very small set of runtime assets that are needed by the screen the
// user is on right now (for example Login audio for the currently selected
// locale). This is intentionally independent from Placement/Custom Level pack
// readiness: one small UI/audio file must never make an already-ready content
// package look stale or trigger another package scan.
export async function ensureOfflineAssetUrls(urls = [], options = {}) {
  const requested = [...new Set((Array.isArray(urls) ? urls : [urls])
    .map((url) => String(url || '').trim())
    .filter(Boolean))];
  if (!requested.length || !isNativeAssetRuntime()) {
    return { status: 'skipped', requestedCount: requested.length, downloadedCount: 0 };
  }

  const run = async () => {
    if (syncPromise) {
      try { await syncPromise; } catch (_) {}
    }
    await loadMeta();

    const targets = requested
      .map((url) => ({ url, key: canonicalAssetKey(url) }))
      .filter(({ key }) => key && isOfflineManifestExactKey(key));
    const missing = targets.filter(({ key }) => !getAssetMetaEntry(key)?.nativeUri);

    await logOfflineDiagnosticEvent('asset-flow-on-demand-plan', {
      reason: String(options.reason || 'runtime'),
      route: assetFlowRoute(),
      requestedCount: targets.length,
      missingCount: missing.length,
      assets: targets.map(({ key }) => assetFlowRecordForKey(key, {
        cached: Boolean(getAssetMetaEntry(key)?.nativeUri),
        cachedBytes: Number(getAssetMetaEntry(key)?.size || 0),
      })),
    });

    if (!missing.length) {
      return { status: 'ready', requestedCount: targets.length, downloadedCount: 0 };
    }
    if (!isOnline()) {
      await logOfflineDiagnosticEvent('runtime-on-demand-assets-unavailable-offline', {
        reason: String(options.reason || 'runtime'),
        requestedCount: targets.length,
        missingCount: missing.length,
        keys: missing.map(({ key }) => key).slice(0, 20),
      });
      return { status: 'offline-missing', requestedCount: targets.length, downloadedCount: 0 };
    }

    const fs = filesystem();
    if (!fs?.downloadFile || !fs?.getUri) {
      return { status: 'native-unavailable', requestedCount: targets.length, downloadedCount: 0 };
    }

    let downloadedCount = 0;
    let downloadedBytes = 0;
    for (const { url, key } of missing) {
      if (!isOnline()) break;
      await logOfflineDiagnosticEvent('asset-flow-download-start', assetFlowRecordForKey(key, {
        source: 'runtime-on-demand',
        reason: String(options.reason || 'runtime'),
      }));
      const path = `${ROOT}/runtime-on-demand/${key}`;
      await ensureDirectory(path);
      await fs.downloadFile({ url, path, directory: 'DATA', expectedSize: 0 });
      const uri = await fs.getUri({ path, directory: 'DATA' });
      const size = Number(uri?.size || 0);
      const nativeUri = uri?.uri || uri?.path || '';
      if (!nativeUri) throw new Error(`On-demand offline asset has no local URI: ${key}`);
      assetMeta[key] = {
        source: 'runtime-on-demand',
        key,
        etag: '',
        size,
        nativeUri,
        savedAt: Date.now(),
        manifestVersion: 'runtime-on-demand-v1',
      };
      downloadedCount += 1;
      downloadedBytes += size;
      await logOfflineDiagnosticEvent('asset-flow-download-complete', assetFlowRecordForKey(key, {
        source: 'runtime-on-demand',
        reason: String(options.reason || 'runtime'),
        bytes: size,
      }));
    }
    if (downloadedCount) await saveMeta();

    await logOfflineDiagnosticEvent('runtime-on-demand-assets-ready', {
      reason: String(options.reason || 'runtime'),
      requestedCount: targets.length,
      downloadedCount,
      downloadedBytes,
      keys: missing.map(({ key }) => key).slice(0, 20),
    });
    return { status: 'ready', requestedCount: targets.length, downloadedCount, downloadedBytes };
  };

  const task = onDemandAssetSyncChain.then(run, run);
  onDemandAssetSyncChain = task.catch(() => undefined);
  return task;
}

function collectAssetKeys(value, keys = new Set(), depth = 0) {
  if (depth > 14 || value === null || value === undefined) return keys;
  if (typeof value === 'string') {
    const key = canonicalAssetKey(value);
    if (key && !key.endsWith('/data.json') && !key.endsWith('/gameData.json')) keys.add(key);
    return keys;
  }
  if (Array.isArray(value)) {
    value.forEach((item) => collectAssetKeys(item, keys, depth + 1));
    return keys;
  }
  if (typeof value === 'object') {
    Object.values(value).forEach((item) => collectAssetKeys(item, keys, depth + 1));
  }
  return keys;
}

const ACTIVITY_DATA_FILES = ['data.json', 'gameData.json'];
const ACTIVITY_DATA_ATTEMPTS = 3;
const ACTIVITY_DATA_TIMEOUT_MS = 12000;

const isRecoverableNetworkError = (error) => {
  const message = String(error?.message || error || '').toLowerCase();
  const code = String(error?.code || '').toLowerCase();
  return !isOnline()
    || code === 'econnaborted'
    || code === 'etimedout'
    || message.includes('network connection was lost')
    || message.includes('network request failed')
    || message.includes('failed to fetch')
    || message.includes('internet connection')
    || message.includes('timed out')
    || message.includes('timeout');
};

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const waitForConnection = async ({ emitLoading, loadingSource, scope, detail } = {}) => {
  if (isOnline()) return;

  emitLoadingPhase(emitLoading, loadingSource, {
    label: scope === 'placement' ? 'Preparing Placement...' : 'Preparing content...',
    detail: detail || 'Connection lost. Waiting to reconnect...',
  });
  updateOfflineState({ packStatus: 'paused', message: '', partial: true });

  await new Promise((resolve) => {
    let settled = false;
    let nativeHandle = null;
    const finish = () => {
      if (settled || !isOnline()) return;
      settled = true;
      window.removeEventListener('online', finish);
      try { nativeHandle?.remove?.(); } catch (_) {}
      resolve();
    };

    window.addEventListener('online', finish);
    const network = window.Capacitor?.Plugins?.Network;
    if (network?.addListener) {
      Promise.resolve(network.addListener('networkStatusChange', (status) => {
        if (status?.connected !== false) {
          // state.js is refreshed by the global network monitor; give it one tick.
          setTimeout(finish, 25);
        }
      })).then((handle) => { nativeHandle = handle; }).catch(() => undefined);
    }

    const poll = setInterval(() => {
      if (!settled && isOnline()) {
        clearInterval(poll);
        finish();
      } else if (settled) {
        clearInterval(poll);
      }
    }, 500);
  });

  emitLoadingPhase(emitLoading, loadingSource, {
    label: scope === 'placement' ? 'Preparing Placement...' : 'Preparing content...',
    detail: 'Connection restored. Resuming download...',
  });
};

const withTimeout = (promise, timeoutMs, message) => new Promise((resolve, reject) => {
  const timer = setTimeout(() => reject(new Error(message)), timeoutMs);
  Promise.resolve(promise).then(
    (value) => { clearTimeout(timer); resolve(value); },
    (error) => { clearTimeout(timer); reject(error); },
  );
});

const friendlyAssetType = (entry = {}) => {
  const key = String(entry?.key || '').toLowerCase();
  if (/\.(mp3|wav|m4a|aac|ogg)(?:$|\?)/.test(key) || key.includes('/audio/')) return 'audio';
  if (/\.(png|jpe?g|gif|webp|svg)(?:$|\?)/.test(key) || key.includes('/images/')) return 'image';
  if (/\.(pdf)(?:$|\?)/.test(key) || key.includes('/files/')) return 'document';
  return 'content file';
};

const readableSyncError = (error, scope) => {
  const target = scope === 'placement' ? 'Placement' : 'Custom Level';
  const message = String(error?.message || error || 'Unknown error');
  if (/manifest/i.test(message) && /timed out/i.test(message)) return `${target} package request timed out. Check the API connection and try again.`;
  if (/runtime cache/i.test(message)) return `${target} runtime data could not be read from local storage.`;
  if (/download stalled/i.test(message)) return message;
  if (/validation failed/i.test(message)) return message;
  return message;
};

const getHttpStatus = (error) => Number(error?.response?.status || error?.status || 0) || null;

const sanitizeDiagnosticValue = (value = '') => String(value || '')
  .replace(/(?:data|gameData)\.json/gi, 'activity data')
  .replace(/\s+/g, ' ')
  .trim();

const makeStageError = (error, context = {}) => {
  const original = error instanceof Error ? error : new Error(String(error || 'Unknown error'));
  const stage = sanitizeDiagnosticValue(context.stage || original.offlineStage || 'Preparing content');
  const operation = sanitizeDiagnosticValue(context.operation || original.offlineOperation || 'Unknown operation');
  const status = getHttpStatus(original);
  const baseMessage = sanitizeDiagnosticValue(original.message || String(original));
  const parts = [stage, operation];
  if (context.itemNumber && context.totalItems) parts.push(`${context.itemNumber}/${context.totalItems}`);
  if (context.activityId) parts.push(`Activity ${context.activityId}`);
  if (status) parts.push(`HTTP ${status}`);
  parts.push(baseMessage || 'Unknown error');
  original.offlineStage = stage;
  original.offlineOperation = operation;
  original.offlineDiagnostic = parts.filter(Boolean).join(' · ');
  return original;
};

const diagnosticMessage = (error, scope) => {
  if (error?.offlineDiagnostic) return error.offlineDiagnostic;
  return readableSyncError(error, scope);
};

async function fetchWithTimeout(url, options = {}, timeoutMs = ACTIVITY_DATA_TIMEOUT_MS) {
  const controller = typeof AbortController !== 'undefined' ? new AbortController() : null;
  const timer = controller ? setTimeout(() => controller.abort(), timeoutMs) : null;
  try {
    return await fetch(url, {
      ...options,
      ...(controller ? { signal: controller.signal } : {}),
    });
  } finally {
    if (timer) clearTimeout(timer);
  }
}

async function resolveActivityData(activityId, reportProgress) {
  const attempts = [];

  for (const fileName of ACTIVITY_DATA_FILES) {
    const key = `public/Activities/${activityId}/${fileName}`;

    for (let attempt = 1; attempt <= ACTIVITY_DATA_ATTEMPTS; attempt += 1) {
      if (!isOnline()) throw new Error(`Internet connection was lost while resolving Activity ${activityId}.`);

      reportProgress?.({ activityId, fileName, attempt, state: 'requesting' });
      try {
        const signedUrl = await getSignedUrl({ key, method: 'get', uploadBucket: true });
        const response = await fetchWithTimeout(
          signedUrl,
          { credentials: 'omit', cache: 'no-store' },
          ACTIVITY_DATA_TIMEOUT_MS,
        );

        attempts.push({ fileName, attempt, status: response.status });
        if (response.ok) {
          const data = await response.json();
          return { data, key, fileName, attempts };
        }

        // 404 means this Activity uses the other supported runtime data filename.
        // Retrying a missing filename wastes time and hides the actual fallback.
        if (response.status === 404 || response.status === 403) break;
      } catch (error) {
        const message = error?.name === 'AbortError'
          ? `timeout after ${ACTIVITY_DATA_TIMEOUT_MS}ms`
          : (error?.message || String(error));
        attempts.push({ fileName, attempt, error: message });
        console.warn('[mobile-offline][activity-data-attempt-failed]', {
          activityId,
          fileName,
          key,
          attempt,
          maxAttempts: ACTIVITY_DATA_ATTEMPTS,
          error: message,
        });
      }

      if (attempt < ACTIVITY_DATA_ATTEMPTS) await sleep(350 * attempt);
    }
  }

  const error = new Error(`Activity ${activityId} has no readable data.json or gameData.json.`);
  error.activityId = activityId;
  error.attempts = attempts;
  throw error;
}

async function prefetchActivityData(activityIds = [], onProgress, activityById = new Map()) {
  const assetKeys = new Set();
  const stringNumbers = new Set();
  const failed = [];
  const resolved = [];
  const parentVideoSources = [];
  let next = 0;
  let completed = 0;

  const report = (detail = {}) => {
    onProgress?.({
      completed,
      total: activityIds.length,
      failed: failed.length,
      ...detail,
    });
  };

  const worker = async () => {
    while (next < activityIds.length) {
      const index = next;
      next += 1;
      const activityId = activityIds[index];

      try {
        const result = await resolveActivityData(activityId, (detail) => report({
          index,
          activityId,
          ...detail,
        }));
        await cacheActivityData(activityId, result.data);
        const activityMeta = activityById.get(String(activityId || '')) || {};
        collectActivityRuntimeAssetKeys(result.data, {
          activityId,
          contentFolder: activityMeta?.contentFolder || `public/Activities/${activityId}`,
          canonicalize: canonicalAssetKey,
          keys: assetKeys,
        });
        collectReferencedStringNumbers(result.data, stringNumbers);
        // ParentAdvice runtime reads only currentActivity.data.options.parentVideos.
        // Do not recursively collect every nested `parentVideos` field from the
        // activity payload: that can pull unrelated menu/login/settings videos
        // into the Placement download.
        if (result?.data?.options?.parentVideos) {
          parentVideoSources.push(result.data.options.parentVideos);
        }
        resolved.push({ activityId, fileName: result.fileName, key: result.key });
        completed += 1;
        report({
          index,
          activityId,
          fileName: result.fileName,
          state: 'resolved',
        });
      } catch (error) {
        completed += 1;
        failed.push({
          activityId,
          error: error?.message || String(error),
          attempts: error?.attempts || [],
        });
        console.error('[mobile-offline][activity-data-failed]', {
          activityId,
          completed,
          total: activityIds.length,
          error: error?.message || String(error),
          attempts: error?.attempts || [],
        });
        report({ index, activityId, state: 'failed', error: error?.message || String(error) });
      }
    }
  };

  await Promise.all(Array.from({ length: Math.min(8, activityIds.length || 1) }, () => worker()));

  if (failed.length) {
    const ids = failed.slice(0, 5).map((item) => item.activityId).join(', ');
    const suffix = failed.length > 5 ? ` +${failed.length - 5} more` : '';
    const error = new Error(`Could not resolve Activity data for ${failed.length} Activity(s): ${ids}${suffix}`);
    error.failedActivities = failed;
    throw error;
  }

  return {
    assetKeys: [...assetKeys],
    stringNumbers: [...stringNumbers],
    parentVideoSources,
    resolved,
  };
}

function getOfflineActivityLanguageCodes(playData) {
  // After Placement, the Login language is a runtime content choice. Keep the
  // generated Custom Level activity set unchanged and switch only its language
  // layer (base stays shared between languages). Placement itself continues to
  // use the language captured during registration/onboarding.
  const generatedLevelId = String(playData?.mobileBootstrap?.generatedCustomContentLevel?._id || '');
  if (generatedLevelId) {
    return [getGeneratedCustomContentActivityLanguageCode(playData)];
  }

  const solutionId = String(playData?.mobileBootstrap?.solutionId || '');
  const selectedSolution = (playData?.product?.solutions || []).find((item) => String(item?._id || '') === solutionId) || {};
  const raw = [
    playData?.mobileBootstrap?.offlineAssessment?.instructionLanguage,
    playData?.learner?.customContentOnboarding?.instructionLanguage,
    playData?.learner?.customContentOnboarding?.explanationLanguage,
    selectedSolution?.activityLocale,
    selectedSolution?.locale,
  ];
  const codes = [...new Set(raw
    .map(normalizeOfflineLanguageCode)
    .filter(Boolean))]
    .slice(0, 3);
  return codes.length ? codes : ['en'];
}

function getLocaleTargets(playData, common) {
  const solutionId = String(playData?.mobileBootstrap?.solutionId || '');
  const selectedSolution = (playData?.product?.solutions || []).find((item) => String(item?._id || '') === solutionId) || {};
  const wantedCodes = new Set([
    getMobileSelectedLanguageCode(),
    selectedSolution?.locale,
    selectedSolution?.activityLocale,
    playData?.mobileBootstrap?.offlineAssessment?.instructionLanguage,
    playData?.learner?.customContentOnboarding?.instructionLanguage,
    playData?.learner?.customContentOnboarding?.explanationLanguage,
    'en',
  ].map((value) => String(value || '').trim().toLowerCase()).filter(Boolean));

  return (common?.locales || [])
    .filter((locale) => wantedCodes.has(String(locale?.code || '').trim().toLowerCase()))
    .map((locale) => ({
      code: String(locale?.code || '').trim(),
      folder: String(
        locale?.audioFolderName || locale?.name || locale?.displayName || locale?.code || '',
      ).replace(/[\\/]+/g, ' ').trim(),
      locale,
    }))
    .filter((item) => item.code && item.folder);
}

function getLocaleFolders(playData, common) {
  return getLocaleTargets(playData, common).map((item) => item.folder);
}

const resolveLocaleCodeFromValue = (common, value) => {
  const raw = value && typeof value === 'object'
    ? (value.code || value._id || value.id || value.name || value.displayName || '')
    : value;
  const normalizedRaw = String(raw || '').trim().toLowerCase();
  if (!normalizedRaw) return '';

  const matched = (common?.locales || []).find((locale) => {
    const candidates = [
      locale?.code,
      locale?._id,
      locale?.id,
      locale?.name,
      locale?.displayName,
    ].map((item) => String(item || '').trim().toLowerCase()).filter(Boolean);
    return candidates.includes(normalizedRaw);
  });

  return normalizeOfflineLanguageCode(matched?.code || normalizedRaw);
};

const localeTargetForCode = (common, rawCode) => {
  const code = resolveLocaleCodeFromValue(common, rawCode);
  if (!code) return null;
  const locale = (common?.locales || []).find((candidate) => (
    normalizeOfflineLanguageCode(candidate?.code) === code
  ));
  if (!locale) return null;
  const folder = String(
    locale?.audioFolderName || locale?.name || locale?.displayName || locale?.code || '',
  ).replace(/[\\/]+/g, ' ').trim();
  return folder ? { code: String(locale.code || code), folder, locale } : null;
};

const getPlacementInterfaceLocaleCode = (playData, common) => {
  const solutionId = String(playData?.mobileBootstrap?.solutionId || '');
  const selectedSolution = (playData?.product?.solutions || [])
    .find((solution) => String(solution?._id || '') === solutionId)
    || (playData?.product?.solutions || [])[0]
    || null;

  // Placement already carries the Login/UI language explicitly as
  // offlineAssessment.explanationLanguage. Do not use localStorage.localeCode
  // as the primary source here: during Placement it can already reflect the
  // Activity/instruction locale (for example vm) and would make UI assets look
  // like Vietnamese even when the interface is English.
  return resolveLocaleCodeFromValue(
    common,
    playData?.mobileBootstrap?.offlineAssessment?.explanationLanguage
      || playData?.learner?.customContentOnboarding?.explanationLanguage
      || selectedSolution?.locale
      || getMobileSelectedLanguageCode()
      || 'en',
  ) || 'en';
};

const getPlacementInstructionLocaleCode = (playData, common) => {
  const solutionId = String(playData?.mobileBootstrap?.solutionId || '');
  const selectedSolution = (playData?.product?.solutions || [])
    .find((solution) => String(solution?._id || '') === solutionId)
    || (playData?.product?.solutions || [])[0]
    || null;

  return resolveLocaleCodeFromValue(
    common,
    playData?.mobileBootstrap?.offlineAssessment?.instructionLanguage
      || playData?.learner?.customContentOnboarding?.instructionLanguage
      || selectedSolution?.activityLocale
      || selectedSolution?.locale
      || 'en',
  ) || 'en';
};

const getPlacementQuickStartLocaleTargets = (playData, common, selectedSolution) => {
  const interfaceCode = getPlacementInterfaceLocaleCode(playData, common);
  const quickStart = common?.config?.loginPageDocuments?.quickStart || {};
  const requestedEntries = findObjectValueCaseInsensitive(quickStart, interfaceCode);
  const requestedTarget = localeTargetForCode(common, interfaceCode);
  if (requestedTarget && Array.isArray(requestedEntries) && requestedEntries.length) {
    return [requestedTarget];
  }

  const englishEntries = findObjectValueCaseInsensitive(quickStart, 'en');
  const englishTarget = localeTargetForCode(common, 'en');
  if (englishTarget && Array.isArray(englishEntries) && englishEntries.length) {
    return [englishTarget];
  }

  return requestedTarget ? [requestedTarget] : [];
};

const getPlacementParentVideoLocaleTargets = (playData, common, selectedSolution) => {
  const instructionCode = getPlacementInstructionLocaleCode(playData, common);
  const configured = Array.isArray(selectedSolution?.parentAdvicesLocales)
    ? selectedSolution.parentAdvicesLocales
        .map((value) => resolveLocaleCodeFromValue(common, value))
        .filter(Boolean)
    : [];

  // Parent Advice opened from an Activity follows that Activity's resolved
  // content locale. If the Solution explicitly restricts Parent Advice locales
  // and does not contain that locale, ParentAdvice falls back to the first
  // configured locale. Package only the initial runtime locale here; do not
  // preload every configured language into the Placement download.
  const requestedCode = configured.length && !configured.includes(instructionCode)
    ? configured[0]
    : instructionCode;
  const target = localeTargetForCode(common, requestedCode);
  return target ? [target] : [];
};

const basenameOnly = (value = '') => String(value || '').split(/[\\/]/).pop().trim();


const SETTINGS_RUNTIME_EXACT_KEYS = [
  'Images/SettingsDialog/icons/modules.png',
  'Images/SettingsDialog/icons/characters.png',
  'Images/SettingsDialog/icons/themes.png',
  'Images/SettingsDialog/icons/animation.jpg',
  'Images/SettingsDialog/icons/activity.jpg',
  'Images/SettingsDialog/icons/team.jpg',
  'Images/SettingsDialog/icons/account.jpg',
  'Images/SettingsDialog/icons/quickstart.png',
  'Images/SettingsDialog/dialogbox/LeftArrow.png',
  'Images/SettingsDialog/dialogbox/RightArrow.png',
];

const VIDEO_FILENAME_RE = /\.(?:mp4|webm|mov|m4v)$/i;
const SHARED_LOCALE_VIDEO_KEYS = new Set(['alllocales', '__all', '_all', 'all', 'shared']);

const findObjectValueCaseInsensitive = (value, key) => {
  if (!value || typeof value !== 'object' || !key) return undefined;
  const exactKey = Object.keys(value).find((candidate) => (
    String(candidate || '').toLowerCase() === String(key || '').toLowerCase()
  ));
  return exactKey ? value[exactKey] : undefined;
};

const collectNamedFieldValues = (value, fieldName, out = [], depth = 0) => {
  if (depth > 18 || value === null || value === undefined) return out;
  if (Array.isArray(value)) {
    value.forEach((item) => collectNamedFieldValues(item, fieldName, out, depth + 1));
    return out;
  }
  if (typeof value !== 'object') return out;

  Object.entries(value).forEach(([key, item]) => {
    if (String(key || '').toLowerCase() === String(fieldName || '').toLowerCase()) out.push(item);
    collectNamedFieldValues(item, fieldName, out, depth + 1);
  });
  return out;
};

const collectLocaleVideoFilenames = (
  value,
  localeCode,
  knownLocaleCodes,
  out = new Set(),
  depth = 0,
) => {
  if (depth > 16 || value === null || value === undefined) return out;

  if (typeof value === 'string') {
    const filename = basenameOnly(value);
    if (filename && VIDEO_FILENAME_RE.test(filename)) out.add(filename);
    return out;
  }

  if (Array.isArray(value)) {
    value.forEach((item) => collectLocaleVideoFilenames(
      item,
      localeCode,
      knownLocaleCodes,
      out,
      depth + 1,
    ));
    return out;
  }

  if (typeof value !== 'object') return out;

  const entries = Object.entries(value);
  const localeScoped = entries.some(([key]) => {
    const normalized = String(key || '').toLowerCase();
    return knownLocaleCodes.has(normalized) || SHARED_LOCALE_VIDEO_KEYS.has(normalized);
  });

  if (localeScoped) {
    entries.forEach(([key, item]) => {
      const normalized = String(key || '').toLowerCase();
      if (
        normalized === String(localeCode || '').toLowerCase()
        || SHARED_LOCALE_VIDEO_KEYS.has(normalized)
      ) {
        collectLocaleVideoFilenames(item, localeCode, knownLocaleCodes, out, depth + 1);
      }
    });
    return out;
  }

  entries.forEach(([, item]) => collectLocaleVideoFilenames(
    item,
    localeCode,
    knownLocaleCodes,
    out,
    depth + 1,
  ));
  return out;
};

const REPORT_MENU_IMAGE_RE = /\.(?:png|jpe?g|gif|webp|svg)$/i;

const addReportMenuImageKey = (value, keys) => {
  if (!value) return;
  const exact = canonicalAssetKey(value);
  if (exact) {
    keys.add(exact);
    return;
  }
  const filename = basenameOnly(value);
  if (filename && REPORT_MENU_IMAGE_RE.test(filename)) {
    keys.add(`Images/ReportMenuImages/${filename}`);
  }
};

function collectCustomLevelShellAssetKeys(
  playData,
  common,
  activityDataResult,
  helpCommon,
  keys,
  scope = 'custom-level',
) {
  SETTINGS_RUNTIME_EXACT_KEYS.forEach((key) => keys.add(key));

  const solutionMenu = playData?.mobileBootstrap?.solutionMenu || {};
  Object.values(solutionMenu?.reports || {}).forEach((report) => {
    addReportMenuImageKey(report?.image, keys);
  });
  (solutionMenu?.menuGroups || []).forEach((group) => {
    addReportMenuImageKey(group?.generatedSubjectImage, keys);
  });

  // Custom Level intentionally shows report stars (noReports=false), so the
  // empty/partial/mastered variants for every visible learner must be local.
  const visibleLearnerCount = Math.max(
    1,
    Number(playData?.product?.onePerson ? 1 : (1 + (playData?.team || []).length)),
  );
  for (let learnerIndex = 1; learnerIndex <= Math.min(visibleLearnerCount, 8); learnerIndex += 1) {
    [1, 2, 3].forEach((type) => keys.add(`Images/FlinkPlay/stars/${learnerIndex}-${type}.png`));
  }

  const solutionId = String(playData?.mobileBootstrap?.solutionId || '');
  const selectedSolution = (playData?.product?.solutions || [])
    .find((solution) => String(solution?._id || '') === solutionId)
    || (playData?.product?.solutions || [])[0]
    || null;

  (selectedSolution?.themes || []).forEach((theme) => {
    const filename = `${String(theme?.name || '').replace(/\s/g, '')}.png`;
    if (filename !== '.png') keys.add(`Images/SettingsDialog/themes/${filename}`);
  });

  if ((selectedSolution?.characters || []).length) {
    keys.add('Images/SettingsDialog/characters/NoCharacter.png');
  }
  (selectedSolution?.characters || []).forEach((character) => {
    const filename = `${String(character?.name || '').replace(/\s/g, '')}.png`;
    if (filename !== '.png') keys.add(`Images/SettingsDialog/characters/${filename}`);
  });

  const customInstructionTarget = scope === 'custom-level'
    ? localeTargetForCode(common, getGeneratedCustomContentActivityLanguageCode(playData))
    : null;
  const localeTargets = scope === 'custom-level'
    ? (customInstructionTarget ? [customInstructionTarget] : [])
    : getLocaleTargets(playData, common);
  const quickStartLocaleTargets = scope === 'placement'
    ? getPlacementQuickStartLocaleTargets(playData, common, selectedSolution)
    : localeTargets;
  const parentVideoLocaleTargets = scope === 'placement'
    ? getPlacementParentVideoLocaleTargets(playData, common, selectedSolution)
    : localeTargets;
  const knownLocaleCodes = new Set((common?.locales || [])
    .map((locale) => String(locale?.code || '').trim().toLowerCase())
    .filter(Boolean));
  const quickStart = common?.config?.loginPageDocuments?.quickStart || {};

  // Placement Parent Tip is activity-scoped. Pull Parent Videos only from the
  // Activity templates/data that belong to the 14 Placement Activities. The
  // previous collector also traversed global Settings, Family Reports, Login,
  // menu and unrelated Solution videos, which made Placement preload videos it
  // cannot use while running an Activity.
  const parentVideoSources = scope === 'placement'
    ? [
        ...(helpCommon?.activityTemplates || []).map((template) => template?.parentVideos).filter(Boolean),
        ...(activityDataResult?.parentVideoSources || []),
      ]
    : [
        ...(helpCommon?.activityTemplates || []).map((template) => template?.parentVideos).filter(Boolean),
        ...(activityDataResult?.parentVideoSources || []),
        ...collectNamedFieldValues(playData?.mobileBootstrap?.solutionMenu || {}, 'parentVideos'),
      ];

  quickStartLocaleTargets.forEach(({ code, folder }) => {
    const quickStartEntries = findObjectValueCaseInsensitive(quickStart, code);
    (Array.isArray(quickStartEntries) ? quickStartEntries : []).forEach((video) => {
      const filename = basenameOnly(video?.value || video?.filename || video);
      if (filename && VIDEO_FILENAME_RE.test(filename)) {
        keys.add(`Files/animations/QuickStart/${folder}/${filename}`);
      }
    });
  });

  const sharedParentVideoKeys = ['allLocales', '__all', '_all', 'all', 'shared'];
  const getFlatParentVideoFilename = (source, code) => {
    if (typeof source === 'string') return basenameOnly(source);
    if (!source || typeof source !== 'object') return '';

    const direct = findObjectValueCaseInsensitive(source, code);
    if (typeof direct === 'string' && direct.trim()) return basenameOnly(direct);

    for (const key of sharedParentVideoKeys) {
      const shared = findObjectValueCaseInsensitive(source, key);
      if (typeof shared === 'string' && shared.trim()) return basenameOnly(shared);
    }
    return '';
  };

  const configuredParentCodes = Array.isArray(selectedSolution?.parentAdvicesLocales)
    ? selectedSolution.parentAdvicesLocales
        .map((value) => resolveLocaleCodeFromValue(common, value))
        .filter(Boolean)
    : [];

  if (scope === 'placement') {
    // ParentAdvice runtime uses exactly one filename from
    // currentActivity.data.options.parentVideos OR currentActivity.template.parentVideos.
    // Resolve those flat locale maps exactly; never recursively walk the whole
    // parentVideos object, because that was the source of mainmenu/login/settings/
    // familyreports and other unrelated videos in the Placement package.
    parentVideoLocaleTargets.forEach(({ code }) => {
      parentVideoSources.forEach((source) => {
        let effectiveCode = code;
        let filename = getFlatParentVideoFilename(source, effectiveCode);

        if (!filename && configuredParentCodes.length) {
          for (const fallbackCode of configuredParentCodes) {
            const candidate = getFlatParentVideoFilename(source, fallbackCode);
            if (candidate) {
              effectiveCode = fallbackCode;
              filename = candidate;
              break;
            }
          }
        }

        if (!filename) return;
        const target = localeTargetForCode(common, effectiveCode);
        if (!target?.folder) return;
        keys.add(`Files/animations/ParentVideos/${target.folder}/${filename}`);
      });
    });
  } else {
    parentVideoLocaleTargets.forEach(({ code, folder }) => {
      const parentVideoFilenames = new Set();
      parentVideoSources.forEach((source) => collectLocaleVideoFilenames(
        source,
        code,
        knownLocaleCodes,
        parentVideoFilenames,
      ));
      parentVideoFilenames.forEach((filename) => {
        keys.add(`Files/animations/ParentVideos/${folder}/${filename}`);
      });
    });
  }
}

const isAllowedActivityLanguageAssetKey = (value = '', languageCodes = []) => {
  const key = String(value || '');
  const marker = '/languageVersionData/';
  const markerIndex = key.indexOf(marker);
  if (markerIndex < 0) return true;
  const languageFolder = key.slice(markerIndex + marker.length).split('/')[0].toLowerCase();
  const allowedFolders = new Set((languageCodes || []).flatMap((rawCode) => {
    const code = String(rawCode || '').trim().toLowerCase().split(/[-_]/)[0];
    if (!code) return [];
    // Vietnamese authored Activity assets exist only in immersion_vm. There is
    // no languageVersionData/vm folder and it must never be requested/accepted.
    if (code === 'vm') return ['immersion_vm'];
    return [code, `immersion_${code}`];
  }));
  return allowedFolders.has(languageFolder);
};

function collectReferencedStringNumbers(value, out = new Set(), depth = 0) {
  if (depth > 18 || value === null || value === undefined) return out;
  if (Array.isArray(value)) {
    value.forEach((item) => collectReferencedStringNumbers(item, out, depth + 1));
    return out;
  }
  if (typeof value !== 'object') return out;

  Object.entries(value).forEach(([field, item]) => {
    const fieldName = String(field || '').toLowerCase();
    const looksLikeStringReference = fieldName === 'text' || fieldName.includes('string');
    if (looksLikeStringReference && (typeof item === 'number' || /^\d+$/.test(String(item || '')))) {
      const number = Number(item);
      if (Number.isFinite(number) && number > 0) out.add(number);
    }
    collectReferencedStringNumbers(item, out, depth + 1);
  });
  return out;
}

function getSelectiveStringAudioLocaleTargets(common, additionalCodes = [], exact = false) {
  const requestedCodes = new Set([
    'en',
    ...(exact ? [] : ['es']),
    ...(Array.isArray(additionalCodes) ? additionalCodes : []),
  ].map(normalizeOfflineLanguageCode).filter(Boolean));

  return (common?.locales || [])
    .filter((locale) => {
      const code = normalizeOfflineLanguageCode(locale?.code);
      const folder = String(locale?.audioFolderName || locale?.name || locale?.displayName || '').trim().toLowerCase();
      return requestedCodes.has(code)
        || folder === 'english'
        || (!exact && folder === 'spanish');
    })
    .map((locale) => ({
      code: String(locale?.code || '').trim(),
      folder: String(locale?.audioFolderName || locale?.name || locale?.displayName || locale?.code || '')
        .replace(/[\\/]+/g, ' ')
        .trim(),
      locale,
    }))
    .filter((item) => item.code && item.folder);
}

function collectSelectiveStringAudioKeys(
  common,
  stringNumbers,
  keys,
  additionalCodes = [],
  exact = false,
) {
  const wanted = stringNumbers instanceof Set ? stringNumbers : new Set(stringNumbers || []);
  if (!wanted.size) return;
  const targets = getSelectiveStringAudioLocaleTargets(common, additionalCodes, exact);
  const strings = Array.isArray(common?.strings) ? common.strings : [];

  targets.forEach(({ code, folder, locale }) => {
    strings.forEach((item) => {
      if (!wanted.has(Number(item?.stringNumber))) return;
      const localized = item?.data?.[code]
        || item?.data?.[String(code).toLowerCase()]
        || item?.data?.[locale?.name]
        || null;
      const filename = basenameOnly(localized?.stringAudio || localized?.audio || '');
      if (filename) keys.add(`Audio/Generic/Strings/${folder}/${filename}`);
    });
  });
}

function collectNamedAudioFields(value, fieldNames, out = new Set(), depth = 0) {
  if (depth > 16 || value === null || value === undefined) return out;
  if (Array.isArray(value)) {
    value.forEach((item) => collectNamedAudioFields(item, fieldNames, out, depth + 1));
    return out;
  }
  if (typeof value !== 'object') return out;

  Object.entries(value).forEach(([key, item]) => {
    if (fieldNames.has(key) && typeof item === 'string') {
      const filename = basenameOnly(item);
      if (filename && !canonicalAssetKey(item)) out.add(filename);
    }
    collectNamedAudioFields(item, fieldNames, out, depth + 1);
  });
  return out;
}

const MOBILE_LOGIN_AUDIO_STRINGS = [
  {
    stringNumber: 1,
    resolveFilename: (localized) => localized?.stringAudio,
    legacyFilename: 'LoginHelp.mp3',
  },
  {
    stringNumber: 3834,
    resolveFilename: (localized) => localized?.stringAudio || localized?.audio,
    legacyFilename: 'loginhelpdialog.mp3',
  },
];

function collectLoginAudioKeys(common, keys, requestedCodes = null) {
  const strings = Array.isArray(common?.strings) ? common.strings : [];
  const locales = Array.isArray(common?.locales) ? common.locales : [];
  const requested = Array.isArray(requestedCodes)
    ? new Set(requestedCodes.map((code) => resolveLocaleCodeFromValue(common, code)).filter(Boolean))
    : null;

  MOBILE_LOGIN_AUDIO_STRINGS.forEach(({ stringNumber, resolveFilename, legacyFilename }) => {
    const item = strings.find((stringItem) => Number(stringItem?.stringNumber) === stringNumber);
    if (!item?.data || typeof item.data !== 'object') return;

    Object.entries(item.data).forEach(([rawCode, localized]) => {
      const code = String(rawCode || '').trim().toLowerCase();
      if (!code || !localized || typeof localized !== 'object') return;
      if (requested && !requested.has(normalizeOfflineLanguageCode(code))) return;

      const locale = locales.find((candidate) => (
        String(candidate?.code || '').trim().toLowerCase() === code
      ));
      if (!locale) return;

      const folder = String(
        locale?.audioFolderName || locale?.name || locale?.displayName || '',
      ).replace(/\/+/g, ' ').trim();
      if (!folder) return;

      const filename = basenameOnly(resolveFilename(localized) || '');
      if (filename) {
        keys.add(`Audio/Generic/Strings/${folder}/${filename}`);
        return;
      }

      keys.add(`Audio/Generic/LoginHelp/${folder}/Online/${legacyFilename}`);
    });
  });
}

function collectGenericHelpAudioKeys(playData, common, keys, localeFoldersOverride = null) {
  const localeFolders = Array.isArray(localeFoldersOverride)
    ? localeFoldersOverride.filter(Boolean)
    : getLocaleFolders(playData, common);

  // Menu help is the only runtime path that uses menu.helpAudio directly.
  // Do not fan a filename out across five unrelated Generic/*Help folders: that
  // created hundreds of non-existent keys and made prebuilt resolve very slow.
  const menuHelpFiles = collectNamedAudioFields(
    playData?.menus || [],
    new Set(['helpAudio']),
  );

  const teamHelpFiles = new Set();
  (common?.activityTemplates || []).forEach((template) => {
    const name = String(template?.activityTemplateName || '').replace(/\s/g, '').trim();
    if (name) teamHelpFiles.add(`${name}.mp3`);
  });

  localeFolders.forEach((folder) => {
    menuHelpFiles.forEach((filename) => {
      keys.add(`Audio/Generic/MenuHelp/${folder}/${filename}`);
    });
    teamHelpFiles.forEach((filename) => {
      keys.add(`Audio/Generic/TeamHelp/${folder}/${filename}`);
    });
    // This one is explicitly constructed by showMultiplayerHelp().
    keys.add(`Audio/Generic/ActivityHelp/${folder}/multiplayermodehelp.mp3`);
  });
}

function collectWordsDbAudioKeys(value, localeFolders, keys, depth = 0) {
  if (depth > 16 || value === null || value === undefined) return;
  if (Array.isArray(value)) {
    value.forEach((item) => collectWordsDbAudioKeys(item, localeFolders, keys, depth + 1));
    return;
  }
  if (typeof value !== 'object') return;

  const word = String(value?.word || '').replace(/[()]/g, '').trim();
  const firstLetter = word.charAt(0).toUpperCase();
  if (firstLetter) {
    [
      ['wordAudio', 'Words'],
      ['definitionAudio', 'Definitions'],
    ].forEach(([field, folderType]) => {
      const raw = value?.[field];
      const filename = basenameOnly(raw || '');
      if (!filename || canonicalAssetKey(raw)) return;
      localeFolders.forEach((folder) => {
        keys.add(`Audio/Generic/WordsDB/${folder}/${folderType}/${firstLetter}/${filename}`);
      });
    });
  }

  Object.values(value).forEach((item) => collectWordsDbAudioKeys(item, localeFolders, keys, depth + 1));
}

async function getStagedEntry(entry, manifestVersion) {
  const fs = filesystem();
  if (!fs?.getUri) return null;
  const path = safePath(entry, manifestVersion);
  try {
    const uri = await fs.getUri({ path, directory: 'DATA' });
    const actualSize = Number(uri?.size || 0);
    const expectedSize = Number(entry.size || 0);
    if (expectedSize > 0 && actualSize !== expectedSize) return null;
    return {
      source: entry.source,
      key: entry.key,
      etag: entry.etag || '',
      size: expectedSize || actualSize,
      nativeUri: uri?.uri || uri?.path || '',
      savedAt: Date.now(),
      manifestVersion,
    };
  } catch (_) {
    return null;
  }
}

async function downloadEntry(entry, completedBytes, manifestVersion) {
  const fs = filesystem();
  if (!fs?.downloadFile || !fs?.getUri) {
    throw new Error('Native offline storage plugin is not available. Sync the updated mobile shell before testing offline content.');
  }

  const path = safePath(entry, manifestVersion);
  await ensureDirectory(path);
  let lastDownloadError = null;
  for (let attempt = 1; attempt <= 3; attempt += 1) {
    try {
      await fs.downloadFile({
        url: entry.url,
        path,
        directory: 'DATA',
        expectedSize: Number(entry.size || 0),
      });
      lastDownloadError = null;
      break;
    } catch (error) {
      lastDownloadError = error;
      if (attempt < 3) await sleep(500 * attempt);
    }
  }
  if (lastDownloadError) throw lastDownloadError;
  const stagedEntry = await getStagedEntry(entry, manifestVersion);
  if (!stagedEntry?.nativeUri) throw new Error(`Downloaded offline file could not be verified: ${entry.key}`);
  return {
    completedBytes: completedBytes + Number(entry.size || 0),
    stagedEntry,
  };
}


const encodeNativeRelativePath = (value = '') => String(value || '')
  .split('/')
  .filter((part) => part !== '')
  .map((part) => encodeURIComponent(part))
  .join('/');

async function syncCustomLevelArchive({
  playData,
  manifest,
  packSignature,
  emitLoading,
  loadingSource,
  diagnostics,
}) {
  const fs = filesystem();
  if (!fs?.downloadPackage || !fs?.extractTar) {
    throw new Error('Native package download is not available in this mobile shell.');
  }

  const customLevelId = String(
    playData?.mobileBootstrap?.generatedCustomContentLevel?._id
    || playData?.mobileBootstrap?.solutionMenu?.generatedCustomContentLevelId
    || '',
  ).trim();
  if (!customLevelId) throw new Error('Custom Level package id is missing.');

  const sourceFiles = Array.isArray(manifest?.files) ? manifest.files : [];
  if (!sourceFiles.length) throw new Error('Custom Level package manifest is empty.');

  emitLoadingPhase(emitLoading, loadingSource, {
    label: 'Preparing content...',
    detail: 'Preparing fast offline package...',
  });

  const requestBody = {
    customLevelId,
    manifest: {
      manifestVersion: manifest?.manifestVersion || '',
      totalBytes: Number(manifest?.totalBytes || 0),
      fileCount: sourceFiles.length,
      // Signed per-file URLs are intentionally not sent back to the API.
      files: sourceFiles.map(({ source, key, size, etag, lastModified }) => ({
        source, key, size, etag, lastModified,
      })),
    },
  };

  let payload = null;
  stampDiagnostic(diagnostics, 'packagePrepareStarted');

  const prepareResponse = await axios.post(
    '/api/mobile-v2/offline/custom-level-package',
    { ...requestBody, action: 'prepare' },
    { timeout: 25000 },
  );
  const prepared = prepareResponse?.data?.data || prepareResponse?.data || {};

  if (prepared.status === 'ready') {
    payload = prepared;
  } else {
    const manifestVersion = String(prepared?.manifest?.manifestVersion || requestBody.manifest.manifestVersion || '').trim();
    const planParts = Array.isArray(prepared?.package?.parts) ? prepared.package.parts : [];
    if (!manifestVersion || !planParts.length) {
      throw new Error('Custom Level package build plan is incomplete.');
    }

    const pendingParts = planParts.filter((part) => part?.ready !== true);
    let builtCount = planParts.length - pendingParts.length;
    const totalParts = planParts.length;
    const emitBuildProgress = () => {
      const ratio = totalParts > 0 ? Math.min(1, builtCount / totalParts) : 0;
      emitLoadingPhase(emitLoading, loadingSource, {
        label: 'Preparing content...',
        detail: `Building offline package · ${builtCount} / ${totalParts}`,
        progress: Math.max(1, Math.round(ratio * 100)),
      });
    };
    emitBuildProgress();

    let nextPart = 0;
    const buildWorker = async () => {
      while (nextPart < pendingParts.length) {
        const index = nextPart;
        nextPart += 1;
        const part = pendingParts[index];
        const partNumber = Number(part?.partNumber || 0);
        let lastError = null;
        for (let attempt = 1; attempt <= 3; attempt += 1) {
          try {
            await axios.post(
              '/api/mobile-v2/offline/custom-level-package',
              {
                action: 'build-part',
                customLevelId,
                manifestVersion,
                partNumber,
              },
              { timeout: 25000 },
            );
            lastError = null;
            break;
          } catch (error) {
            lastError = error;
            await logOfflineDiagnosticEvent('custom-level-package-build-part-failed', {
              customLevelId,
              manifestVersion,
              partNumber,
              partCount: totalParts,
              attempt,
              errorMessage: error?.message || String(error),
            });
            if (attempt < 3) await sleep(750 * attempt);
          }
        }
        if (lastError) throw lastError;
        builtCount += 1;
        emitBuildProgress();
      }
    };

    await Promise.all(Array.from({ length: Math.min(3, pendingParts.length || 1) }, () => buildWorker()));

    const finalizeResponse = await axios.post(
      '/api/mobile-v2/offline/custom-level-package',
      { action: 'finalize', customLevelId, manifestVersion },
      { timeout: 25000 },
    );
    const finalized = finalizeResponse?.data?.data || finalizeResponse?.data || {};
    if (finalized.status !== 'ready' || !finalized.package) {
      throw new Error(`Custom Level package finalization is incomplete${finalized?.package?.missingPart ? ` (missing part ${finalized.package.missingPart})` : ''}.`);
    }
    payload = {
      status: 'ready',
      manifest: prepared.manifest || requestBody.manifest,
      package: finalized.package,
    };
  }

  stampDiagnostic(diagnostics, 'packageReady');
  const packageManifest = payload.manifest || {};
  const packageInfo = payload.package || {};
  const manifestVersion = String(packageManifest.manifestVersion || '').trim();
  const files = Array.isArray(packageManifest.files) ? packageManifest.files : [];
  const parts = (Array.isArray(packageInfo.parts) ? packageInfo.parts : [])
    .map((part, index) => ({
      ...part,
      index,
      url: String(part?.url || '').trim(),
      size: Number(part?.size || 0),
      fileCount: Number(part?.fileCount || 0),
      contentBytes: Number(part?.contentBytes || 0),
    }))
    .filter((part) => part.url && part.size > 0);
  const packageBytes = parts.reduce((sum, part) => sum + part.size, 0);
  if (!manifestVersion || !files.length || !parts.length || packageBytes <= 0) {
    throw new Error('Custom Level package response is incomplete.');
  }

  const destinationPath = `${ROOT}/packs/${manifestVersion}`;
  const packageBasePath = `${ROOT}/packages/custom-level/${customLevelId}/${manifestVersion}`;
  const progressBaseId = `custom-level-${customLevelId}-${manifestVersion}`;
  const partProgress = new Array(parts.length).fill(0);
  let progressHandle = null;
  let extractedRootUri = '';
  let extractedFiles = 0;
  let extractedBytes = 0;
  let extractionChain = Promise.resolve();

  const emitDownloadProgress = () => {
    const downloadedBytes = partProgress.reduce((sum, bytes) => sum + Math.max(0, Number(bytes || 0)), 0);
    const ratio = packageBytes > 0 ? Math.min(1, downloadedBytes / packageBytes) : 0;
    updateOfflineState({
      packStatus: 'downloading',
      totalBytes: Number(packageManifest.totalBytes || 0),
      updateBytes: packageBytes,
      downloadedBytes,
      pendingFiles: files.length,
      readyFiles: 0,
      message: '',
      partial: true,
    });
    emitLoadingPhase(emitLoading, loadingSource, {
      label: 'Preparing content...',
      detail: `Downloading package · ${formatBytes(downloadedBytes)} / ${formatBytes(packageBytes)}`,
      progress: Math.round(ratio * 100),
    });
  };

  const extractDownloadedPart = (part, packagePath) => {
    extractionChain = extractionChain.then(async () => {
      emitLoadingPhase(emitLoading, loadingSource, {
        label: 'Preparing content...',
        detail: `Unpacking package ${part.index + 1} / ${parts.length}...`,
      });
      const extracted = await fs.extractTar({
        path: packagePath,
        destination: destinationPath,
        directory: 'DATA',
      });
      const rootUri = String(extracted?.rootUri || '').replace(/\/+$/, '') + '/';
      const partFileCount = Number(extracted?.fileCount || 0);
      const partBytes = Number(extracted?.extractedBytes || 0);
      if (!rootUri || rootUri === '/') throw new Error('Custom Level package extraction returned no local path.');
      if (part.fileCount > 0 && partFileCount !== part.fileCount) {
        throw new Error(`Custom Level package extraction is incomplete: expected ${part.fileCount} files, got ${partFileCount}.`);
      }
      if (part.contentBytes > 0 && partBytes !== part.contentBytes) {
        throw new Error(`Custom Level package extraction size mismatch: expected ${part.contentBytes} bytes, got ${partBytes}.`);
      }
      extractedRootUri = extractedRootUri || rootUri;
      extractedFiles += partFileCount;
      extractedBytes += partBytes;
      try { await fs.remove?.({ path: packagePath, directory: 'DATA' }); } catch (_) {}
    });
    return extractionChain;
  };

  try {
    diagnostics.mode = 'package';
    diagnostics.packagePartCount = parts.length;
    diagnostics.packageBytes = packageBytes;
    stampDiagnostic(diagnostics, 'downloadStarted');
    if (typeof fs.addListener === 'function') {
      progressHandle = await fs.addListener('offlinePackageProgress', (event = {}) => {
        const progressId = String(event.progressId || '');
        const prefix = `${progressBaseId}-part-`;
        if (!progressId.startsWith(prefix)) return;
        const partNumber = Number(progressId.slice(prefix.length));
        const index = partNumber - 1;
        if (!Number.isInteger(index) || index < 0 || index >= parts.length) return;
        const expected = parts[index].size;
        partProgress[index] = Math.min(expected, Math.max(0, Number(event.downloadedBytes || 0)));
        emitDownloadProgress();
      });
    }

    emitDownloadProgress();
    let nextPart = 0;
    const worker = async () => {
      while (nextPart < parts.length) {
        const index = nextPart;
        nextPart += 1;
        const part = parts[index];
        const partNumber = String(index + 1).padStart(3, '0');
        const packagePath = `${packageBasePath}/part-${partNumber}.tar`;
        const progressId = `${progressBaseId}-part-${index + 1}`;

        let alreadyDownloaded = false;
        try {
          const existing = await fs.getUri?.({ path: packagePath, directory: 'DATA' });
          alreadyDownloaded = Number(existing?.size || 0) === part.size;
        } catch (_) {}

        if (!alreadyDownloaded) {
          let lastError = null;
          for (let attempt = 1; attempt <= 3; attempt += 1) {
            try {
              await fs.downloadPackage({
                url: part.url,
                path: packagePath,
                directory: 'DATA',
                expectedSize: part.size,
                progressId,
              });
              lastError = null;
              break;
            } catch (error) {
              lastError = error;
              await logOfflineDiagnosticEvent('custom-level-package-part-failed', {
                customLevelId,
                manifestVersion,
                part: index + 1,
                partCount: parts.length,
                attempt,
                expectedSize: part.size,
                errorMessage: error?.message || String(error),
              });
              if (attempt < 3) await sleep(1000 * attempt);
            }
          }
          if (lastError) throw lastError;
        }

        partProgress[index] = part.size;
        emitDownloadProgress();
        await extractDownloadedPart(part, packagePath);
      }
    };

    await Promise.all(Array.from({ length: Math.min(6, parts.length) }, () => worker()));
    await extractionChain;
    stampDiagnostic(diagnostics, 'downloadCompleted');
    stampDiagnostic(diagnostics, 'verifyStarted');

    if (!extractedRootUri || extractedFiles !== files.length) {
      throw new Error(`Custom Level package extraction did not complete (${extractedFiles}/${files.length} files).`);
    }
    const expectedContentBytes = files.reduce((sum, entry) => sum + Number(entry?.size || 0), 0);
    if (expectedContentBytes > 0 && extractedBytes !== expectedContentBytes) {
      throw new Error(`Custom Level package content size mismatch: expected ${expectedContentBytes}, got ${extractedBytes}.`);
    }
    stampDiagnostic(diagnostics, 'verifyCompleted');

    const nextMeta = { ...assetMeta };
    files.forEach((entry) => {
      const relativePath = encodeNativeRelativePath(`${entry.source || 'asset'}/${entry.key || ''}`);
      nextMeta[entry.key] = {
        source: entry.source,
        key: entry.key,
        etag: entry.etag || '',
        size: Number(entry.size || 0),
        nativeUri: `${extractedRootUri}${relativePath}`,
        savedAt: Date.now(),
        manifestVersion,
      };
    });

    assetMeta = nextMeta;
    packMeta['custom-level'] = {
      status: 'ready',
      signature: packSignature,
      manifestVersion,
      fileCount: files.length,
      totalBytes: Number(packageManifest.totalBytes || 0),
      packageBytes,
      packagePartCount: parts.length,
      packageManifestKey: packageInfo.manifestKey || null,
      generatedLevelId: customLevelId,
      activityIds: [...new Set((playData?.mobileBootstrap?.solutionMenu?.activities || [])
        .map((activity) => String(activity?._id || '')).filter(Boolean))].sort(),
      readyAt: Date.now(),
    };
    await Promise.all([saveMeta(), savePackMeta()]);

    updateOfflineState({
      packStatus: 'ready',
      totalBytes: Number(packageManifest.totalBytes || 0),
      updateBytes: packageBytes,
      downloadedBytes: packageBytes,
      pendingFiles: 0,
      readyFiles: files.length,
      lastUpdatedAt: Date.now(),
      message: '',
      partial: false,
    });
    emitLoadingPhase(emitLoading, loadingSource, {
      label: 'Preparing content...',
      detail: 'Content ready offline',
      progress: 100,
    });

    await logOfflineDiagnosticEvent('custom-level-package-ready', {
      customLevelId,
      manifestVersion,
      fileCount: files.length,
      totalBytes: Number(packageManifest.totalBytes || 0),
      packageBytes,
      packagePartCount: parts.length,
    });

    uploadReadyCustomDiagnosticsInBackground(diagnostics, playData, {
      status: 'ready',
      manifestVersion,
      fileCount: files.length,
      verifiedFiles: files.length,
      missingFiles: 0,
      totalBytes: Number(packageManifest.totalBytes || 0),
      downloadedBytes: packageBytes,
      packageBytes,
      packagePartCount: parts.length,
      packageManifestKey: packageInfo.manifestKey || null,
    });

    return {
      status: 'ready',
      scope: 'custom-level',
      manifestVersion,
      fileCount: files.length,
      totalBytes: Number(packageManifest.totalBytes || 0),
      packageBytes,
      packagePartCount: parts.length,
    };
  } finally {
    try { await progressHandle?.remove?.(); } catch (_) {}
  }
}


const OFFLINE_PACKAGE_MODES = new Set(['legacy', 'prebuilt', 'prebuilt-with-fallback']);

// Mobile-only rollout switch. This deliberately lives in frontend source so the
// existing AWS/Lambda deployment process does not gain any new environment
// variables or operational dependencies. Leave `legacy` for the current flow.
// Change only this value when building a test mobile bundle.
const MOBILE_OFFLINE_PACKAGE_MODE = 'prebuilt-with-fallback';

function getOfflinePackageMode() {
  const mode = String(MOBILE_OFFLINE_PACKAGE_MODE || 'legacy').trim().toLowerCase();
  return OFFLINE_PACKAGE_MODES.has(mode) ? mode : 'legacy';
}

const safePrebuiltPathSegment = (value = '') => String(value || '')
  .replace(/[^A-Za-z0-9_.-]+/g, '_')
  .replace(/^_+|_+$/g, '')
  .slice(0, 180) || 'pack';

function prebuiltPackInstalled(pack) {
  const installed = packMeta?.prebuiltPacks?.[pack?.packId];
  if (!installed || installed.status !== 'ready' || installed.version !== pack?.version) return false;
  const files = Array.isArray(pack?.files) ? pack.files : [];
  if (!files.length || Number(installed.fileCount || 0) !== files.length) return false;
  return files.every((entry) => {
    const local = assetMeta[entry.key];
    return Boolean(
      local?.nativeUri
      && String(local.etag || '') === String(entry.etag || '')
      && Number(local.size || 0) === Number(entry.size || 0)
    );
  });
}

async function installPrebuiltPack({
  pack,
  downloadedBefore = 0,
  totalDownloadBytes,
  emitLoading,
  loadingSource,
  scope,
  onProgressBytes = null,
}) {
  const fs = filesystem();
  const parts = (Array.isArray(pack?.parts) ? pack.parts : [])
    .map((part, index) => ({
      ...part,
      index,
      url: String(part?.url || '').trim(),
      size: Number(part?.size || 0),
      fileCount: Number(part?.fileCount || 0),
      contentBytes: Number(part?.contentBytes || 0),
    }))
    .filter((part) => part.url && part.size > 0);
  const files = Array.isArray(pack?.files) ? pack.files : [];
  // Custom Level only: an optimized TAR may contain one canonical physical file
  // for several identical logical activity asset keys. Native extractTar supports
  // only regular TAR entries; resolve the aliases in assetMeta after extraction.
  // Never enable this interpretation for Placement or legacy prebuilt packs.
  const compactCustomPack = scope === 'custom-level'
    && pack?.format === 'custom-level-dedup-v1';
  const physicalFiles = compactCustomPack ? files.filter((entry) => !entry.aliasOf) : files;
  const physicalByIdentity = new Map(physicalFiles.map((entry) => [
    `${entry.source}:${entry.key}`, entry,
  ]));
  if (!parts.length || !files.length || !physicalFiles.length) {
    throw new Error(`Prebuilt pack is incomplete: ${pack?.packId || 'unknown'}`);
  }
  if (compactCustomPack) {
    if (physicalByIdentity.size !== physicalFiles.length
      || Number(pack.physicalFileCount || 0) !== physicalFiles.length) {
      throw new Error(`Invalid Custom Level dedup manifest: physical count mismatch`);
    }
    for (const entry of files) {
      if (entry.aliasOf && (!physicalByIdentity.has(entry.aliasOf)
        || Number(physicalByIdentity.get(entry.aliasOf)?.size || 0) !== Number(entry.size || 0))) {
        throw new Error(`Invalid Custom Level alias: ${entry.key}`);
      }
    }
  }

  const packPath = `${ROOT}/prebuilt/${safePrebuiltPathSegment(pack.packId)}/${safePrebuiltPathSegment(pack.version)}`;
  const archivePath = `${ROOT}/packages/prebuilt/${safePrebuiltPathSegment(pack.packId)}/${safePrebuiltPathSegment(pack.version)}`;
  const progressPrefix = `prebuilt-${safePrebuiltPathSegment(pack.packId)}-${safePrebuiltPathSegment(pack.version)}`;
  const partProgress = new Array(parts.length).fill(0);
  let progressHandle = null;
  let extractedRootUri = '';
  let extractedFiles = 0;
  let extractedBytes = 0;
  let extractionChain = Promise.resolve();

  const emitProgress = () => {
    const currentPackBytes = partProgress.reduce((sum, bytes) => sum + Math.max(0, Number(bytes || 0)), 0);
    if (typeof onProgressBytes === 'function') {
      onProgressBytes(currentPackBytes);
      return;
    }
    const downloadedBytes = Math.min(totalDownloadBytes, downloadedBefore + currentPackBytes);
    const ratio = totalDownloadBytes > 0 ? Math.min(1, downloadedBytes / totalDownloadBytes) : 1;
    updateOfflineState({
      packStatus: 'downloading',
      updateBytes: totalDownloadBytes,
      downloadedBytes,
      message: '',
      partial: true,
    });
    emitLoadingPhase(emitLoading, loadingSource, {
      label: scope === 'placement' ? 'Preparing Placement...' : 'Preparing content...',
      detail: `Downloading content · ${formatBytes(downloadedBytes)} / ${formatBytes(totalDownloadBytes)}`,
      progress: Math.round(ratio * 100),
    });
  };

  const extractPart = (part, localTarPath) => {
    extractionChain = extractionChain.then(async () => {
      emitLoadingPhase(emitLoading, loadingSource, {
        label: scope === 'placement' ? 'Preparing Placement...' : 'Preparing content...',
        detail: `Preparing content · ${pack.packId}`,
      });
      const extracted = await fs.extractTar({
        path: localTarPath,
        destination: packPath,
        directory: 'DATA',
      });
      const rootUri = String(extracted?.rootUri || '').replace(/\/+$/, '') + '/';
      const partFileCount = Number(extracted?.fileCount || 0);
      const partBytes = Number(extracted?.extractedBytes || 0);
      if (!rootUri || rootUri === '/') throw new Error(`Prebuilt pack extraction returned no local path: ${pack.packId}`);
      if (part.fileCount > 0 && partFileCount !== part.fileCount) {
        throw new Error(`Prebuilt pack ${pack.packId} extracted ${partFileCount}/${part.fileCount} files.`);
      }
      if (part.contentBytes > 0 && partBytes !== part.contentBytes) {
        throw new Error(`Prebuilt pack ${pack.packId} extracted ${partBytes}/${part.contentBytes} bytes.`);
      }
      extractedRootUri = extractedRootUri || rootUri;
      extractedFiles += partFileCount;
      extractedBytes += partBytes;
      try { await fs.remove?.({ path: localTarPath, directory: 'DATA' }); } catch (_) {}
    });
    return extractionChain;
  };

  try {
    if (typeof fs.addListener === 'function') {
      progressHandle = await fs.addListener('offlinePackageProgress', (event = {}) => {
        const progressId = String(event.progressId || '');
        const prefix = `${progressPrefix}-part-`;
        if (!progressId.startsWith(prefix)) return;
        const index = Number(progressId.slice(prefix.length)) - 1;
        if (!Number.isInteger(index) || index < 0 || index >= parts.length) return;
        partProgress[index] = Math.min(parts[index].size, Math.max(0, Number(event.downloadedBytes || 0)));
        emitProgress();
      });
    }

    let nextPart = 0;
    const worker = async () => {
      while (nextPart < parts.length) {
        const index = nextPart;
        nextPart += 1;
        const part = parts[index];
        const partNumber = String(index + 1).padStart(3, '0');
        const localTarPath = `${archivePath}/part-${partNumber}.tar`;
        const progressId = `${progressPrefix}-part-${index + 1}`;
        let alreadyDownloaded = false;
        try {
          const existing = await fs.getUri?.({ path: localTarPath, directory: 'DATA' });
          alreadyDownloaded = Number(existing?.size || 0) === part.size;
        } catch (_) {}

        if (!alreadyDownloaded) {
          let lastError = null;
          for (let attempt = 1; attempt <= 3; attempt += 1) {
            try {
              await fs.downloadPackage({
                url: part.url,
                path: localTarPath,
                directory: 'DATA',
                expectedSize: part.size,
                progressId,
              });
              lastError = null;
              break;
            } catch (error) {
              lastError = error;
              if (attempt < 3) await sleep(750 * attempt);
            }
          }
          if (lastError) throw lastError;
        }

        partProgress[index] = part.size;
        emitProgress();
        await extractPart(part, localTarPath);
      }
    };

    emitProgress();
    await Promise.all(Array.from({ length: Math.min(4, parts.length || 1) }, () => worker()));
    await extractionChain;

    const physicalBytes = physicalFiles.reduce((sum, entry) => sum + Number(entry?.size || 0), 0);
    if (!extractedRootUri || extractedFiles !== physicalFiles.length) {
      throw new Error(`Prebuilt pack ${pack.packId} extraction is incomplete (${extractedFiles}/${physicalFiles.length}).`);
    }
    if (physicalBytes > 0 && extractedBytes !== physicalBytes) {
      throw new Error(`Prebuilt pack ${pack.packId} size mismatch (${extractedBytes}/${physicalBytes}).`);
    }

    files.forEach((entry) => {
      const physical = compactCustomPack && entry.aliasOf
        ? physicalByIdentity.get(entry.aliasOf) : entry;
      const relativePath = encodeNativeRelativePath(`${physical.source || 'asset'}/${physical.key || ''}`);
      assetMeta[entry.key] = {
        source: entry.source,
        key: entry.key,
        etag: entry.etag || '',
        size: Number(entry.size || 0),
        nativeUri: `${extractedRootUri}${relativePath}`,
        savedAt: Date.now(),
        manifestVersion: pack.version,
        prebuiltPackId: pack.packId,
        prebuiltPackVersion: pack.version,
      };
    });
    packMeta.prebuiltPacks = packMeta.prebuiltPacks || {};
    packMeta.prebuiltPacks[pack.packId] = {
      status: 'ready',
      version: pack.version,
      fileCount: files.length,
      totalBytes: Number(pack.totalBytes || files.reduce((sum, entry) => sum + Number(entry?.size || 0), 0)),
      totalPackageBytes: Number(pack.totalPackageBytes || parts.reduce((sum, part) => sum + part.size, 0)),
      readyAt: Date.now(),
    };
    await queuePrebuiltMetaSave();
    return Number(pack.totalPackageBytes || parts.reduce((sum, part) => sum + part.size, 0));
  } finally {
    try { await progressHandle?.remove?.(); } catch (_) {}
  }
}



async function syncPrebuiltOfflinePacks({
  playData,
  common,
  scope,
  activityIds,
  manifestPayload,
  packSignature,
  emitLoading,
  loadingSource,
  diagnostics,
}) {
  const fs = filesystem();
  if (!fs?.downloadPackage || !fs?.extractTar || !fs?.getUri) {
    throw new Error('Native prebuilt package support is not available in this mobile shell.');
  }

  const generatedActivityIds = scope === 'custom-level'
    ? getGeneratedCustomLevelActivityIds(playData)
    : [];
  const requestedActivityIds = generatedActivityIds.length
    ? generatedActivityIds
    : [...activityIds].map((activityId) => String(activityId || '')).filter(Boolean);

  const assessment = playData?.mobileBootstrap?.offlineAssessment || {};
  const onboardingLanguage = normalizeOfflineLanguageCode(
    playData?.learner?.customContentOnboarding?.instructionLanguage,
  );
  const placementLanguage = normalizeOfflineLanguageCode(assessment.instructionLanguage);
  const selectedLanguageCode = scope === 'custom-level'
    ? getGeneratedCustomContentActivityLanguageCode(playData)
    : (normalizeOfflineLanguageCode(manifestPayload.activityLanguageCodes?.[0])
      || placementLanguage
      || onboardingLanguage
      || 'en');
  const instructionLanguage = scope === 'custom-level'
    ? selectedLanguageCode
    : (placementLanguage || onboardingLanguage || selectedLanguageCode);
  const placementAllowedStringFolders = scope === 'placement'
    ? new Set([
        getPlacementInterfaceLocaleCode(playData, common),
        getPlacementInstructionLocaleCode(playData, common),
        'en',
      ]
        .map((code) => localeTargetForCode(common, code)?.folder)
        .filter(Boolean)
        .map((folder) => String(folder).toLowerCase()))
    : null;

  const requestedAssetKeys = (manifestPayload.assetKeys || []).filter((key) => {
    const value = String(key || '');
    if (!isAllowedActivityLanguageAssetKey(value, [selectedLanguageCode])) return false;
    if (isDeferredVideoAssetKey(value)) return false;
    if (scope === 'placement' && value.startsWith('Audio/Generic/Strings/')) {
      const folder = value.slice('Audio/Generic/Strings/'.length).split('/')[0].toLowerCase();
      if (!placementAllowedStringFolders.has(folder)) return false;
    }
    // Activity-local files are owned by the activity base/language TARs. Do not
    // validate individual public/Activities/... references against live S3:
    // legacy Activity JSON contains stale/deleted/optional filenames and one of
    // those must never invalidate an otherwise complete prebuilt activity pack.
    // If an activity pack itself is missing, the resolver already enumerates
    // that activity prefix and returns its existing files as the narrow direct
    // fallback.
    if (value.startsWith('public/Activities/')) return false;
    return true;
  });

  const runtimeThemeNames = getRuntimeThemeDescriptors(playData, scope)
    .map((theme) => String(theme?.name || '').trim())
    .filter(Boolean);

  emitLoadingPhase(emitLoading, loadingSource, {
    label: scope === 'placement' ? 'Preparing Placement...' : 'Preparing content...',
    detail: 'Checking content...',
  });

  const response = await axios.post('/api/mobile-v2/offline/prebuilt/resolve', {
    scope,
    // Capability handshake: older app builds must continue receiving original
    // per-Activity TARs even if the server enables Custom Level dedup.
    supportsCustomLevelDedupV1: scope === 'custom-level',
    activityIds: requestedActivityIds,
    activityLanguageCodes: [selectedLanguageCode],
    assetKeys: requestedAssetKeys,
    themeNames: runtimeThemeNames,
    ageBand: assessment.ageBand || playData?.learner?.customContentOnboarding?.ageBand || '',
    instructionLanguage,
    assessmentRootGroupId: assessment.assessmentRootGroupId || '',
  }, { timeout: 30000 });
  const resolved = response?.data?.data || response?.data || {};
  if (resolved.available !== true) {
    const reason = String(resolved.reason || 'prebuilt-unavailable');
    const missingActivityIds = Array.isArray(resolved.missingActivityIds)
      ? resolved.missingActivityIds.slice(0, 10).join(', ')
      : '';
    const missingShared = Array.isArray(resolved.missingShared)
      ? resolved.missingShared.slice(0, 10).join(', ')
      : '';
    throw new Error(
      `Prebuilt offline content is unavailable (${reason})`
      + `${missingActivityIds ? ` · activities: ${missingActivityIds}` : ''}`
      + `${missingShared ? ` · shared: ${missingShared}` : ''}`
    );
  }

  const missingRuntimeAssetKeys = Array.isArray(resolved.missingAssetKeys)
    ? resolved.missingAssetKeys.filter(Boolean)
    : [];

  // The prebuilt resolver already HEAD-checks every unresolved exact key in the
  // correct bucket and returns existing objects in directFiles. A second trip
  // through /offline/manifest can only repeat those checks. On the 2026-10-06
  // Custom Level it rechecked 723 missing/stale keys and recovered 0, adding
  // ~40 seconds. Keep missing optional refs diagnostic-only.
  if (missingRuntimeAssetKeys.length) {
    await logOfflineDiagnosticEvent('prebuilt-runtime-assets-missing-nonfatal', {
      scope,
      missingCount: missingRuntimeAssetKeys.length,
      keys: missingRuntimeAssetKeys.slice(0, 100),
    });
    console.warn('[mobile-offline][prebuilt-runtime-assets-missing-nonfatal]', {
      scope,
      missingCount: missingRuntimeAssetKeys.length,
      keys: missingRuntimeAssetKeys.slice(0, 10),
    });
  }

  const fallbackActivityIds = Array.isArray(resolved.fallbackActivityIds)
    ? resolved.fallbackActivityIds.filter(Boolean)
    : [];
  const fallbackThemeNames = Array.isArray(resolved.fallbackThemeNames)
    ? resolved.fallbackThemeNames.filter(Boolean)
    : [];
  if (fallbackActivityIds.length || fallbackThemeNames.length) {
    await logOfflineDiagnosticEvent('prebuilt-partial-direct-fallback', {
      scope,
      activityCount: fallbackActivityIds.length,
      activityIds: fallbackActivityIds.slice(0, 100),
      themeCount: fallbackThemeNames.length,
      themeNames: fallbackThemeNames.slice(0, 50),
    });
  }

  const packs = Array.isArray(resolved.packs) ? resolved.packs : [];
  if (scope === 'custom-level') {
    const compact = packs.find((pack) => pack?.format === 'custom-level-dedup-v1');
    await logOfflineDiagnosticEvent('custom-level-dedup-selection', {
      scope, optimized: Boolean(compact),
      packId: String(compact?.packId || ''),
      physicalFiles: Number(compact?.physicalFileCount || 0),
      logicalFiles: Number(compact?.fileCount || 0),
      packageBytes: Number(compact?.size || 0),
    });
  }
  // Extra safety: previous server manifests and fallback lists can include
  // optional video even when the client no longer requests those asset keys.
  // Do not block Placement readiness or validation on that optional media.
  const deferredVideos = (resolved.directFiles || []).filter((entry) => isDeferredVideoAssetKey(entry?.key));
  const directFiles = (Array.isArray(resolved.directFiles) ? resolved.directFiles : [])
    .filter((entry) => !isDeferredVideoAssetKey(entry?.key));
  const files = (Array.isArray(resolved.files) ? resolved.files : [])
    .filter((entry) => !isDeferredVideoAssetKey(entry?.key));
  if (deferredVideos.length) {
    await logOfflineDiagnosticEvent('video-on-demand-deferred-from-pack', {
      scope, count: deferredVideos.length,
      bytes: deferredVideos.reduce((sum, entry) => sum + Number(entry?.size || 0), 0),
      keys: deferredVideos.map((entry) => entry?.key).slice(0, 30),
    });
  }
  if (!files.length) throw new Error('Prebuilt offline manifest is empty.');

  const effectiveTotalBytes = files.reduce((sum, entry) => sum + Number(entry?.size || 0), 0);
  const manifestVersion = String(resolved.manifestVersion || 'prebuilt');
  const missingPacks = packs.filter((pack) => !prebuiltPackInstalled(pack));
  const missingDirectFiles = directFiles.filter((entry) => {
    const local = assetMeta[entry.key];
    return !local?.nativeUri
      || String(local.etag || '') !== String(entry.etag || '')
      || Number(local.size || 0) !== Number(entry.size || 0);
  });
  const packDownloadBytes = missingPacks.reduce(
    (sum, pack) => sum + Number(pack.totalPackageBytes || 0),
    0,
  );
  // Placement retains its original download list byte-for-byte. In a Custom
  // Level only, reuse identical Activity content under multiple logical paths.
  const directDownloadJobs = scope === 'custom-level'
    ? buildCustomLevelDirectReusePlan(missingDirectFiles)
    : missingDirectFiles.map((entry) => ({ entry, aliases: [] }));
  const directDownloadBytes = directDownloadJobs.reduce(
    (sum, { entry }) => sum + Number(entry.size || 0),
    0,
  );
  if (scope === 'custom-level' && directDownloadJobs.length < missingDirectFiles.length) {
    await logOfflineDiagnosticEvent('custom-level-identical-direct-reuse', {
      logicalFileCount: missingDirectFiles.length,
      physicalDownloadCount: directDownloadJobs.length,
      reusedFileCount: missingDirectFiles.length - directDownloadJobs.length,
      originalDirectBytes: missingDirectFiles.reduce((sum, entry) => sum + Number(entry.size || 0), 0),
      actualDirectDownloadBytes: directDownloadBytes,
    });
  }
  const totalDownloadBytes = packDownloadBytes + directDownloadBytes;

  const generatedLevel = playData?.mobileBootstrap?.generatedCustomContentLevel || {};
  const customLevelIndex = Number(
    generatedLevel?.customLevelIndex
    || generatedLevel?.contentPlan?.customLevelIndex
    || 0,
  );
  const flowContext = {
    scope,
    ageBand: String(assessment.ageBand || playData?.learner?.customContentOnboarding?.ageBand || ''),
    instructionLanguage: String(instructionLanguage || ''),
    interfaceLanguage: String(
      assessment.explanationLanguage
      || playData?.learner?.customContentOnboarding?.explanationLanguage
      || '',
    ),
    customLevelIndex: customLevelIndex || null,
    generatedLevelId: String(generatedLevel?._id || ''),
    activityCount: requestedActivityIds.length,
    route: assetFlowRoute(),
  };

  await logOfflineDiagnosticEvent('asset-flow-package-summary', {
    ...flowContext,
    manifestVersion,
    packCount: packs.length,
    missingPackCount: missingPacks.length,
    directFileCount: directFiles.length,
    missingDirectFileCount: missingDirectFiles.length,
    packDownloadBytes,
    directDownloadBytes,
    totalDownloadBytes,
  });

  for (let offset = 0; offset < missingDirectFiles.length; offset += ASSET_FLOW_CHUNK_SIZE) {
    await logOfflineDiagnosticEvent('asset-flow-package-direct-files', {
      ...flowContext,
      offset,
      total: missingDirectFiles.length,
      assets: missingDirectFiles.slice(offset, offset + ASSET_FLOW_CHUNK_SIZE).map((entry) => (
        assetFlowRecordForKey(entry?.key, {
          bytes: Number(entry?.size || 0),
          etag: String(entry?.etag || ''),
          source: String(entry?.source || 'direct'),
        })
      )),
    });
  }

  for (const pack of missingPacks) {
    const packFiles = Array.isArray(pack?.files) ? pack.files : [];
    await logOfflineDiagnosticEvent('asset-flow-package-pack', {
      ...flowContext,
      packId: String(pack?.packId || ''),
      version: String(pack?.version || ''),
      bytes: Number(pack?.totalPackageBytes || 0),
      fileCount: Number(pack?.fileCount || packFiles.length || 0),
    });
    for (let offset = 0; offset < packFiles.length; offset += ASSET_FLOW_CHUNK_SIZE) {
      await logOfflineDiagnosticEvent('asset-flow-package-pack-files', {
        ...flowContext,
        packId: String(pack?.packId || ''),
        version: String(pack?.version || ''),
        offset,
        total: packFiles.length,
        assets: packFiles.slice(offset, offset + ASSET_FLOW_CHUNK_SIZE).map((entry) => (
          assetFlowRecordForKey(entry?.key, {
            bytes: Number(entry?.size || 0),
            source: String(entry?.source || ''),
            etag: String(entry?.etag || ''),
          })
        )),
      });
    }
  }

  // Size diagnostics for prebuilt downloads. Keep this immediately after the
  // missing-pack/direct-file calculation so the numbers match the UI exactly.
  const directSizeGroups = new Map();
  const classifyDirectKey = (key = '') => {
    const value = String(key || '');
    const prefixes = [
      'Files/animations/QuickStart/',
      'Files/animations/ParentVideos/',
      'Audio/Generic/LoginHelp/',
      'Audio/Generic/Strings/',
      'Audio/Generic/MenuHelp/',
      'Audio/Generic/TeamHelp/',
      'Audio/Generic/ActivityHelp/',
      'Audio/Generic/WordsDB/',
      'Images/SettingsDialog/',
      'Images/ReportMenuImages/',
      'Images/FlinkPlay/stars/',
    ];
    return prefixes.find((prefix) => value.startsWith(prefix)) || 'other';
  };
  missingDirectFiles.forEach((entry) => {
    const group = classifyDirectKey(entry?.key);
    const current = directSizeGroups.get(group) || { bytes: 0, files: 0 };
    current.bytes += Number(entry?.size || 0);
    current.files += 1;
    directSizeGroups.set(group, current);
  });
  const directGroups = [...directSizeGroups.entries()]
    .map(([group, value]) => ({
      group,
      files: value.files,
      bytes: value.bytes,
      mib: Number((value.bytes / 1024 / 1024).toFixed(2)),
    }))
    .sort((a, b) => b.bytes - a.bytes);
  const largestDirectFiles = [...missingDirectFiles]
    .sort((a, b) => Number(b?.size || 0) - Number(a?.size || 0))
    .slice(0, 50)
    .map((entry) => ({
      mib: Number((Number(entry?.size || 0) / 1024 / 1024).toFixed(2)),
      size: Number(entry?.size || 0),
      key: String(entry?.key || ''),
    }));
  console.log('[mobile-offline][prebuilt-size-breakdown]', {
    scope,
    languageCode: selectedLanguageCode,
    packCount: packs.length,
    missingPackCount: missingPacks.length,
    directFileCount: directFiles.length,
    missingDirectFileCount: missingDirectFiles.length,
    packDownloadMiB: Number((packDownloadBytes / 1024 / 1024).toFixed(2)),
    directDownloadMiB: Number((directDownloadBytes / 1024 / 1024).toFixed(2)),
    totalDownloadMiB: Number((totalDownloadBytes / 1024 / 1024).toFixed(2)),
    directGroups,
    largestDirectFiles,
  });

  diagnostics.mode = 'prebuilt';
  diagnostics.prebuiltPackCount = packs.length;
  diagnostics.prebuiltMissingPackCount = missingPacks.length;
  diagnostics.prebuiltDirectFileCount = directFiles.length;
  diagnostics.prebuiltRecoveredRuntimeFileCount = 0;
  diagnostics.prebuiltMissingDirectFileCount = missingDirectFiles.length;
  diagnostics.prebuiltPackageBytes = totalDownloadBytes;
  diagnostics.manifestVersion = manifestVersion;
  diagnostics.fileCount = files.length;
  diagnostics.totalBytes = effectiveTotalBytes;

  await logOfflineDiagnosticEvent('prebuilt-offline-resolved', {
    scope,
    manifestVersion,
    languageCode: selectedLanguageCode,
    activityCount: requestedActivityIds.length,
    packCount: packs.length,
    missingPackCount: missingPacks.length,
    missingPackIds: missingPacks.map((pack) => String(pack?.packId || '')).filter(Boolean).slice(0, 100),
    directFileCount: directFiles.length,
    missingDirectFileCount: missingDirectFiles.length,
    fileCount: files.length,
    totalBytes: effectiveTotalBytes,
    downloadBytes: totalDownloadBytes,
    placementVersion: String(resolved?.placementVersion || ''),
    packDownloadBytes,
    directDownloadBytes,
    packDownloadMiB: Number((packDownloadBytes / 1024 / 1024).toFixed(2)),
    directDownloadMiB: Number((directDownloadBytes / 1024 / 1024).toFixed(2)),
    totalDownloadMiB: Number((totalDownloadBytes / 1024 / 1024).toFixed(2)),
    directGroups,
    largestDirectFiles,
    missingPacks: missingPacks.map((pack) => ({
      packId: String(pack?.packId || ''),
      version: String(pack?.version || ''),
      bytes: Number(pack?.totalPackageBytes || 0),
      mib: Number((Number(pack?.totalPackageBytes || 0) / 1024 / 1024).toFixed(2)),
    })),
  });

  const pendingPackFiles = missingPacks.reduce(
    (sum, pack) => sum + Number(pack.fileCount || 0),
    0,
  );
  updateOfflineState({
    packStatus: totalDownloadBytes > 0 ? 'downloading' : 'activating',
    totalBytes: effectiveTotalBytes,
    updateBytes: totalDownloadBytes,
    downloadedBytes: 0,
    pendingFiles: pendingPackFiles + missingDirectFiles.length,
    readyFiles: Math.max(0, files.length - pendingPackFiles - missingDirectFiles.length),
    message: '',
    partial: totalDownloadBytes > 0,
  });

  stampDiagnostic(diagnostics, 'downloadStarted');

  // Activity packs are independent. Download/extract several of them in parallel
  // so a Custom Level does not pay one full network round-trip per Activity.
  // Native storage already supports concurrent package downloads; cap at 4 to
  // avoid saturating mobile I/O while still removing the sequential bottleneck.
  const packProgress = new Map();
  missingPacks.forEach((pack) => {
    packProgress.set(`${pack.packId}:${pack.version}`, 0);
  });
  let lastReportedPackBytes = -1;
  const reportPackProgress = () => {
    const currentPackBytes = [...packProgress.values()]
      .reduce((sum, bytes) => sum + Math.max(0, Number(bytes || 0)), 0);
    const currentBytes = Math.min(totalDownloadBytes, currentPackBytes);
    if (currentBytes === lastReportedPackBytes) return;
    lastReportedPackBytes = currentBytes;
    updateOfflineState({
      packStatus: 'downloading',
      updateBytes: totalDownloadBytes,
      downloadedBytes: currentBytes,
      message: '',
      partial: true,
    });
    emitLoadingPhase(emitLoading, loadingSource, {
      label: scope === 'placement' ? 'Preparing Placement...' : 'Preparing content...',
      detail: `Downloading content · ${formatBytes(currentBytes)} / ${formatBytes(totalDownloadBytes)}`,
      progress: totalDownloadBytes > 0
        ? Math.round(Math.min(1, currentBytes / totalDownloadBytes) * 100)
        : 100,
    });
  };

  if (missingPacks.length) {
    // Download and extract each independent prebuilt pack in one pipeline.
    // The previous two-stage flow waited for every archive to download before
    // extraction started, making network time + extraction time fully additive.
    stampDiagnostic(diagnostics, 'packageDownloadStarted');
    stampDiagnostic(diagnostics, 'packageExtractStarted');

    let nextInstallIndex = 0;
    const installWorker = async () => {
      while (nextInstallIndex < missingPacks.length) {
        const index = nextInstallIndex;
        nextInstallIndex += 1;
        const pack = missingPacks[index];
        const progressKey = `${pack.packId}:${pack.version}`;
        const packageBytes = Number(
          pack.totalPackageBytes
          || (Array.isArray(pack.parts)
            ? pack.parts.reduce((sum, part) => sum + Number(part?.size || 0), 0)
            : 0),
        );

        await installPrebuiltPack({
          pack,
          totalDownloadBytes,
          emitLoading,
          loadingSource,
          scope,
          onProgressBytes: (bytes) => {
            packProgress.set(progressKey, Math.min(packageBytes, Math.max(0, Number(bytes || 0))));
            reportPackProgress();
          },
        });

        packProgress.set(progressKey, packageBytes);
        reportPackProgress();
      }
    };

    await Promise.all(Array.from(
      { length: Math.min(4, missingPacks.length) },
      () => installWorker(),
    ));

    stampDiagnostic(diagnostics, 'packageDownloadCompleted');
    stampDiagnostic(diagnostics, 'packageExtractCompleted');
  }

  if (missingDirectFiles.length) {
    let nextIndex = 0;
    let completedDirectBytes = 0;
    const directWorker = async () => {
      while (nextIndex < directDownloadJobs.length) {
        const index = nextIndex;
        nextIndex += 1;
        const { entry, aliases } = directDownloadJobs[index];
        await logOfflineDiagnosticEvent('asset-flow-download-start', assetFlowRecordForKey(entry?.key, {
          ...flowContext,
          source: 'prebuilt-direct',
          bytes: Number(entry?.size || 0),
        }));
        const result = await downloadEntry(entry, 0, manifestVersion);
        assetMeta[entry.key] = result.stagedEntry;
        // Every original key must still pass the normal offline verification.
        // Multiple keys may point to the same native content URI, but retain
        // their own ETag, size and source in the persisted lookup map.
        for (const alias of aliases) {
          assetMeta[alias.key] = {
            ...result.stagedEntry,
            source: alias.source,
            key: alias.key,
            etag: alias.etag || '',
            size: Number(alias.size || 0),
          };
        }
        await logOfflineDiagnosticEvent('asset-flow-download-complete', assetFlowRecordForKey(entry?.key, {
          ...flowContext,
          source: 'prebuilt-direct',
          bytes: Number(entry?.size || 0),
        }));
        completedDirectBytes += Number(entry.size || 0);
        const currentBytes = Math.min(
          totalDownloadBytes,
          packDownloadBytes + completedDirectBytes,
        );
        updateOfflineState({
          packStatus: 'downloading',
          updateBytes: totalDownloadBytes,
          downloadedBytes: currentBytes,
          message: '',
          partial: true,
        });
        emitLoadingPhase(emitLoading, loadingSource, {
          label: scope === 'placement' ? 'Preparing Placement...' : 'Preparing content...',
          detail: `Downloading required files · ${formatBytes(currentBytes)} / ${formatBytes(totalDownloadBytes)}`,
          progress: totalDownloadBytes > 0
            ? Math.round(Math.min(1, currentBytes / totalDownloadBytes) * 100)
            : 100,
        });
      }
    };
    await Promise.all(Array.from(
      { length: Math.min(4, missingDirectFiles.length) },
      () => directWorker(),
    ));
    await saveMeta();
  }
  stampDiagnostic(diagnostics, 'downloadCompleted');

  stampDiagnostic(diagnostics, 'verifyStarted');
  const missingFiles = files.filter((entry) => {
    const local = assetMeta[entry.key];
    return !local?.nativeUri
      || String(local.etag || '') !== String(entry.etag || '')
      || Number(local.size || 0) !== Number(entry.size || 0);
  });
  if (missingFiles.length) {
    throw new Error(`Prebuilt offline validation failed (${missingFiles.length} file(s) missing).`);
  }
  stampDiagnostic(diagnostics, 'verifyCompleted');

  // The old bundled TAR has no authored immersion_vm data for these Activities.
  // Replace the IndexedDB cache with the verified, just-downloaded source JSON.
  // This is required because ActivityAPI reads cached Activity data offline.
  if (isVmPlacementSourceRefresh(
    scope,
    assessment.ageBand || playData?.learner?.customContentOnboarding?.ageBand,
    instructionLanguage,
    requestedActivityIds,
  )) {
    for (const activityId of VM_PLACEMENT_REFRESH_IDS) {
      const key = `public/Activities/${activityId}/data.json`;
      const entry = getAssetMetaEntry(key);
      if (!entry?.nativeUri || !directFiles.some((item) => item.key === key)) {
        throw new Error(`VM Placement is missing refreshed Activity data: ${activityId}`);
      }
      const response = await fetch(convertFileSrc(entry.nativeUri), { cache: 'no-store' });
      if (!response.ok) throw new Error(`Cannot read refreshed VM Placement data: ${activityId}`);
      const currentData = await response.json();
      if (!currentData?.languageVersionData?.immersion_vm) {
        throw new Error(`VM Placement source lost authored immersion_vm data: ${activityId}`);
      }
      await cacheVmPlacementActivityData(activityId, currentData);
    }
  }

  const readyPackRecord = {
    status: 'ready',
    signature: packSignature,
    manifestVersion,
    mode: 'prebuilt',
    fileCount: files.length,
    totalBytes: effectiveTotalBytes,
    packageBytes: totalDownloadBytes,
    prebuiltPackVersions: packs.map((pack) => ({ packId: pack.packId, version: pack.version })),
    readyAt: Date.now(),
    ...(scope === 'custom-level' ? {
      generatedLevelId: String(playData?.mobileBootstrap?.generatedCustomContentLevel?._id || ''),
      activityIds: requestedActivityIds.slice().sort(),
    } : {}),
  };
  rememberReadyPackForSignature(scope, packSignature, readyPackRecord);
  await Promise.all([saveMeta(), savePackMeta()]);

  updateOfflineState({
    packStatus: 'ready',
    totalBytes: effectiveTotalBytes,
    updateBytes: totalDownloadBytes,
    downloadedBytes: totalDownloadBytes,
    pendingFiles: 0,
    readyFiles: files.length,
    lastUpdatedAt: Date.now(),
    message: '',
    partial: false,
  });
  emitLoadingPhase(emitLoading, loadingSource, {
    label: scope === 'placement' ? 'Preparing Placement...' : 'Preparing content...',
    detail: 'Content ready offline',
    progress: 100,
  });

  if (scope === 'custom-level') {
    const measurement = {
      ...flowContext,
      productId: String(playData?.product?._id || ''),
      solutionId: String(playData?.mobileBootstrap?.solutionId || ''),
      activityLanguage: selectedLanguageCode,
      packageCount: packs.length,
      downloadedPackages: missingPacks.length,
      directFilesDownloaded: directDownloadJobs.length,
      requiredDownloadBytes: totalDownloadBytes,
      requiredDownloadMiB: Number((totalDownloadBytes / (1024 * 1024)).toFixed(2)),
      offlineContentBytes: effectiveTotalBytes,
      offlineContentMiB: Number((effectiveTotalBytes / (1024 * 1024)).toFixed(2)),
      cacheHit: totalDownloadBytes === 0,
    };
    console.info('[mobile-offline][custom-level-download-measurement]', measurement);
    await logOfflineDiagnosticEvent('custom-level-download-measurement', measurement);
  }

  await logOfflineDiagnosticEvent('prebuilt-offline-ready', {
    scope,
    manifestVersion,
    packCount: packs.length,
    downloadedPackCount: missingPacks.length,
    directFileCount: directFiles.length,
    downloadedDirectFileCount: missingDirectFiles.length,
    fileCount: files.length,
    totalBytes: effectiveTotalBytes,
    downloadedBytes: totalDownloadBytes,
  });
  const uploadReadyDiagnostics = scope === 'custom-level'
    ? uploadReadyCustomDiagnosticsInBackground
    : uploadOfflineDiagnostics;
  await uploadReadyDiagnostics(diagnostics, playData, {
    status: 'ready',
    mode: 'prebuilt',
    manifestVersion,
    fileCount: files.length,
    verifiedFiles: files.length,
    missingFiles: 0,
    totalBytes: effectiveTotalBytes,
    downloadedBytes: totalDownloadBytes,
    packageBytes: totalDownloadBytes,
    prebuiltPackCount: packs.length,
    prebuiltDownloadedPackCount: missingPacks.length,
  });

  return {
    status: 'ready',
    scope,
    mode: 'prebuilt',
    manifestVersion,
    fileCount: files.length,
    totalBytes: effectiveTotalBytes,
    downloadedBytes: totalDownloadBytes,
    packCount: packs.length,
    downloadedPackCount: missingPacks.length,
    directFileCount: directFiles.length,
    downloadedDirectFileCount: missingDirectFiles.length,
  };
}


function getGeneratedCustomLevelActivityIds(playData) {
  const generatedLevel = playData?.mobileBootstrap?.generatedCustomContentLevel;
  const subjects = generatedLevel?.contentPlan?.subjects || {};
  const ids = [];

  Object.entries(subjects).forEach(([subjectKey, subject]) => {
    if (subjectKey === 'reading') {
      (subject?.selection?.selectedBooks || []).forEach((book) => {
        (book?.activities || []).forEach((activity) => {
          const activityId = String(activity?.activityId || '').trim();
          if (activityId) ids.push(activityId);
        });
      });
      return;
    }

    (subject?.activities || []).forEach((activity) => {
      const activityId = String(activity?.activityId || '').trim();
      if (activityId) ids.push(activityId);
    });
  });

  return [...new Set(ids)];
}

const normalizeSyncScope = (scope, playData) => {
  if (scope === 'placement' || scope === 'custom-level') return scope;
  return playData?.mobileBootstrap?.generatedCustomContentLevel?._id ? 'custom-level' : 'placement';
};

const emitLoadingPhase = (enabled, source, detail = {}) => {
  if (!enabled || typeof window === 'undefined') return;
  window.dispatchEvent(new CustomEvent('flc:mobile-loading-phase', {
    detail: { source, active: true, ...detail },
  }));
};

export async function syncOfflineAssetsFromPlayData(playData, options = {}) {
  if (!isOnline() || !playData) return { status: 'skipped' };

  // Never allow two pack activations to race. If an online-resume sync is already
  // finishing, wait for it and then build the requested scope from the latest state.
  if (syncPromise) {
    try { await syncPromise; } catch (_) {}
    if (!isOnline()) return { status: 'paused' };
  }

  const scope = normalizeSyncScope(options.scope, playData);
  const loadingSource = options.loadingSource || (scope === 'custom-level' ? 'custom-content' : 'assessment');
  const emitLoading = options.emitLoading === true;
  const allActivities = playData?.mobileBootstrap?.solutionMenu?.activities || [];
  const placementIds = playData?.mobileBootstrap?.offlineAssessment?.assessmentLeafActivityIds || [];
  const placementSlotIds = (playData?.mobileBootstrap?.offlineAssessment?.assessmentActivitySlots || [])
    .map((slot) => String(slot?.sourceActivityId || ''))
    .filter(Boolean);
  const placementActivityIds = [...new Set([
    ...placementIds.map((activityId) => String(activityId || '')).filter(Boolean),
    ...placementSlotIds,
  ])];
  const generatedCustomLevelActivityIds = scope === 'custom-level'
    ? getGeneratedCustomLevelActivityIds(playData)
    : [];
  const activityIds = scope === 'placement'
    ? placementActivityIds
    : (generatedCustomLevelActivityIds.length
      ? generatedCustomLevelActivityIds
      : [...new Set(allActivities.map((activity) => String(activity?._id || '')).filter(Boolean))]);
  setActiveVmPlacementDataScope(isVmPlacementSourceRefresh(
    scope,
    playData?.mobileBootstrap?.offlineAssessment?.ageBand
      || playData?.learner?.customContentOnboarding?.ageBand,
    playData?.mobileBootstrap?.offlineAssessment?.instructionLanguage
      || playData?.learner?.customContentOnboarding?.instructionLanguage,
    activityIds,
  ));
  const activityIdSet = new Set(activityIds.map(String));
  const activities = allActivities.filter((activity) => activityIdSet.has(String(activity?._id || '')));
  const activityById = new Map(activities
    .map((activity) => [String(activity?._id || ''), activity])
    .filter(([activityId]) => Boolean(activityId)));

  if (!activityIds.length) return { status: 'empty', scope };
  const packSignature = buildPackSignature(playData, scope, activityIds);
  const diagnostics = makeOfflineDiagnosticSession(scope);
  diagnostics.activityCount = activityIds.length;

  syncPromise = (async () => {
    let currentStage = 'Starting offline preparation';
    let currentOperation = 'Loading local package metadata';
    try {
      try {
        await loadMeta();
        await loadPackMeta();
      } catch (error) {
        throw makeStageError(error, { stage: currentStage, operation: currentOperation });
      }

      const offlinePackageMode = getOfflinePackageMode();
      diagnostics.offlinePackageMode = offlinePackageMode;
      const savedPack = getReadyPackForSignature(scope, packSignature);
      const savedPackMode = String(savedPack?.mode || 'legacy');
      const savedPackMatchesMode = offlinePackageMode === 'legacy'
        ? savedPackMode !== 'prebuilt'
        : savedPackMode === 'prebuilt';
      if (
        savedPackMatchesMode
        && savedPack?.status === 'ready'
        && savedPack?.signature === packSignature
        && Number(savedPack?.fileCount || 0) > 0
        && hasRequiredExactRuntimeAssets()
        && (scope !== 'custom-level' || await verifySavedCustomLevelStorage(savedPack))
      ) {
        if (scope === 'custom-level') {
          await logOfflineDiagnosticEvent('custom-level-download-measurement', {
            generatedLevelId: String(playData?.mobileBootstrap?.generatedCustomContentLevel?._id || ''),
            productId: String(playData?.product?._id || ''),
            solutionId: String(playData?.mobileBootstrap?.solutionId || ''),
            customLevelIndex: Number(
              playData?.mobileBootstrap?.generatedCustomContentLevel?.customLevelIndex
              || playData?.mobileBootstrap?.generatedCustomContentLevel?.contentPlan?.customLevelIndex
              || 0,
            ) || null,
            activityLanguage: getGeneratedCustomContentActivityLanguageCode(playData),
            activityCount: activityIds.length,
            requiredDownloadBytes: 0,
            requiredDownloadMiB: 0,
            offlineContentBytes: Number(savedPack.totalBytes || 0),
            offlineContentMiB: Number((Number(savedPack.totalBytes || 0) / (1024 * 1024)).toFixed(2)),
            cacheHit: true,
          });
        }
        updateOfflineState({
          packStatus: 'ready',
          totalBytes: Number(savedPack.totalBytes || 0),
          updateBytes: 0,
          downloadedBytes: 0,
          pendingFiles: 0,
          readyFiles: Number(savedPack.fileCount || 0),
          lastUpdatedAt: savedPack.readyAt || null,
          message: '',
          partial: false,
        });
        return {
          status: 'ready',
          scope,
          cached: true,
          manifestVersion: savedPack.manifestVersion || null,
          fileCount: Number(savedPack.fileCount || 0),
          totalBytes: Number(savedPack.totalBytes || 0),
        };
      }

      updateOfflineState({ packStatus: 'checking', message: '', partial: false });
      emitLoadingPhase(emitLoading, loadingSource, {
        label: scope === 'placement' ? 'Preparing Placement...' : 'Preparing content...',
        detail: scope === 'placement'
          ? 'Checking learning activities...'
          : `Checking offline content · 0 / ${activityIds.length}`,
      });

      currentStage = 'Resolving activities';
      currentOperation = 'Reading activity runtime data';
      let activityDataResult;
      try {
        activityDataResult = await prefetchActivityData(activityIds, ({
        completed, total, activityId, fileName, attempt, state, failed,
      }) => {
        const safeCompleted = Number.isFinite(completed) ? completed : 0;
        const safeTotal = Number.isFinite(total) ? total : activityIds.length;
        const detail = scope === 'placement'
          ? 'Checking learning activities...'
          : (safeTotal > 0
            ? `Checking offline content · ${safeCompleted} / ${safeTotal}`
            : 'Checking offline content...');
        emitLoadingPhase(emitLoading, loadingSource, {
          label: scope === 'placement' ? 'Preparing Placement...' : 'Preparing content...',
          detail,
        });
      }, activityById);
      } catch (error) {
        throw makeStageError(error, { stage: currentStage, operation: currentOperation, activityId: error?.activityId });
      }
      const referencedAssetKeys = activityDataResult.assetKeys;
      emitLoadingPhase(emitLoading, loadingSource, {
        label: scope === 'placement' ? 'Preparing Placement...' : 'Preparing content...',
        detail: 'Reading runtime configuration...',
      });
      currentStage = 'Reading runtime configuration';
      currentOperation = 'Reading cached mobile runtime';
      let common;
      try {
        common = await withTimeout(
          getCachedCommonRuntime(),
          8000,
          'Runtime cache timed out after 8 seconds.',
        );
      } catch (error) {
        throw makeStageError(error, { stage: currentStage, operation: currentOperation });
      }
      const discoveredAssetKeys = new Set(referencedAssetKeys);
      REQUIRED_EXACT_RUNTIME_KEYS.forEach((key) => discoveredAssetKeys.add(key));
      if (scope === 'placement') {
        const hasAssignedPlacementTheme = getRuntimeThemeDescriptors(playData, 'placement').length > 0;
        PLACEMENT_REQUIRED_SHARED_KEYS.forEach((key) => {
          if (
            hasAssignedPlacementTheme
            && String(key || '').startsWith('Themes/KidsFantasy/Backgrounds/')
          ) return;
          discoveredAssetKeys.add(key);
        });
      }

      emitLoadingPhase(emitLoading, loadingSource, {
        label: scope === 'placement' ? 'Preparing Placement...' : 'Preparing content...',
        detail: 'Building content list...',
      });
      currentStage = 'Building content list';
      currentOperation = 'Scanning runtime asset references';
      try {
        // Keep String audio selective for BOTH Placement and Custom Level.
        // Never add the full common.strings catalogue (~160 MB for EN+ES).
        const activityTemplateIds = new Set(activities.flatMap((activity) => [
          String(activity?.templateId || ''),
          String(activity?.template?._id || ''),
        ]).filter(Boolean));
        const activityTemplateAliases = new Set(activities.flatMap((activity) => [
          String(activity?.templateAlias || '').toUpperCase(),
          String(activity?.template?.alias || '').toUpperCase(),
        ]).filter(Boolean));
        const helpCommon = {
          ...common,
          activityTemplates: (Array.isArray(common?.activityTemplates) ? common.activityTemplates : [])
            .filter((template) => (
              activityTemplateIds.has(String(template?._id || ''))
              || activityTemplateAliases.has(String(template?.alias || '').toUpperCase())
            )),
        };

        if (scope === 'custom-level') {
          // Only the templates used by this generated level and the small global
          // button definition set belong in the blocking Custom Level package.
          // Whole solution/menu/LC recursion pulled thousands of unrelated files
          // (including other levels/locales) into Level 1.
          collectAssetKeys({
            activityTemplates: helpCommon?.activityTemplates || [],
            globalButtons: common?.globalButtons || [],
          }, discoveredAssetKeys);
        }

        const runtimeStringNumbers = new Set(activityDataResult?.stringNumbers || []);
        [1392, 1393, 1394, 1395, 1396, 1397, 1398, 635, 1400, 1401, 1402, 1403, 1404, 1405]
          .forEach((number) => runtimeStringNumbers.add(number));
        collectReferencedStringNumbers(helpCommon?.activityTemplates || [], runtimeStringNumbers);
        collectReferencedStringNumbers(common?.globalButtons || [], runtimeStringNumbers);
        collectReferencedStringNumbers(playData?.mobileBootstrap?.solutionMenu || {}, runtimeStringNumbers);
        collectReferencedStringNumbers(playData?.menus || [], runtimeStringNumbers);
        // Settings controls, QuickStart videos and Parent Tip videos are
        // available during both Placement and Custom Content. Collect the exact
        // runtime files for both scopes so offline behavior matches online.
        const placementInterfaceLanguage = scope === 'placement'
          ? getPlacementInterfaceLocaleCode(playData, common)
          : '';
        const placementInstructionLanguage = scope === 'placement'
          ? normalizeOfflineLanguageCode(playData?.mobileBootstrap?.offlineAssessment?.instructionLanguage)
          : '';

        collectCustomLevelShellAssetKeys(
          playData,
          common,
          activityDataResult,
          helpCommon,
          discoveredAssetKeys,
          scope,
        );
        const customInstructionLanguage = scope === 'custom-level'
          ? getGeneratedCustomContentActivityLanguageCode(playData)
          : '';
        const customInterfaceLanguage = scope === 'custom-level'
          ? getGeneratedCustomContentInterfaceLanguageCode(playData)
          : '';
        const exactLocaleCodes = scope === 'placement'
          ? [placementInterfaceLanguage, placementInstructionLanguage]
          : [customInstructionLanguage, customInterfaceLanguage];

        collectSelectiveStringAudioKeys(
          common,
          runtimeStringNumbers,
          discoveredAssetKeys,
          exactLocaleCodes,
          true,
        );
        collectLoginAudioKeys(common, discoveredAssetKeys, exactLocaleCodes);

        // Generic/Feedback fallback files already live in shared:common-ui.
        // Localized standard feedback is collected above from its String records.

        const exactLocaleFolders = [...new Set(exactLocaleCodes
          .map((code) => localeTargetForCode(common, code)?.folder)
          .filter(Boolean))];
        collectGenericHelpAudioKeys(
          scope === 'placement'
            ? { ...playData, menus: [], mobileBootstrap: { ...(playData?.mobileBootstrap || {}), solutionMenu: {} } }
            : { ...playData, menus: playData?.menus || [] },
          helpCommon,
          discoveredAssetKeys,
          exactLocaleFolders,
        );
        collectWordsDbAudioKeys(
          scope === 'placement'
            ? { activities }
            : { activities },
          exactLocaleFolders,
          discoveredAssetKeys,
        );
      } catch (error) {
        throw makeStageError(error, { stage: currentStage, operation: currentOperation });
      }

      emitLoadingPhase(emitLoading, loadingSource, {
        label: scope === 'placement' ? 'Preparing Placement...' : 'Preparing content...',
        detail: 'Preparing package request...',
      });
      currentStage = 'Preparing package request';
      currentOperation = 'Building manifest request payload';
      let manifestPayload;
      try {
        if (scope === 'custom-level') {
          const rejectedManifestAssetKeys = [...discoveredAssetKeys].filter((key) => (
            !isOfflineManifestExactKey(key)
          ));
          if (rejectedManifestAssetKeys.length) {
            await logOfflineDiagnosticEvent('custom-content-manifest-asset-keys-filtered', {
              filteredCount: rejectedManifestAssetKeys.length,
              keys: rejectedManifestAssetKeys.slice(0, 100),
            });
          }
        }

        // Placement and Custom Level must request only the exact authored
        // language folders selected for this learner/solution. Prefix matching
        // (for example immersion_es*) pulled unrelated variants such as
        // immersion_espr / immersion_esco / immersion_escocopr.
        const activityLanguageCodes = getOfflineActivityLanguageCodes(playData);
        manifestPayload = {
          activityIds,
          assetKeys: [...discoveredAssetKeys].filter((key) => (
            isAllowedActivityLanguageAssetKey(key, activityLanguageCodes)
            && (scope !== 'custom-level' || isOfflineManifestExactKey(key))
          )),
          placementEnglishSpanishOnly: scope === 'placement',
          // Placement keeps the existing reduced Activity baseline. Custom Level must
          // be fully self-contained offline: templates such as Reading can select
          // runtime files from languageVersionData variants that are not discoverable
          // from data.json alone. Request the complete Activity folder only for
          // Custom Level; do not change Placement behavior.
          activityRuntimeBaselineOnly: scope === 'placement',
          // Shared mobile controls/theme assets required by the Activity runtime.
          includeRuntimeBaseline: scope === 'placement' || scope === 'custom-level',
          // Keep complete base Activity data but only the learner's active authored
          // languageVersionData folders. Pulling every authored language inflated
          // a single Custom Level from ~187 MB to ~540 MB.
          activityLanguageCodes,
          // Never request the entire Themes tree. Concrete backgrounds/theme files
          // are already discovered as exact asset keys and cached locally.
          includeThemeAssets: false,
          // Placement already has complete Activity folders plus exact runtime
          // asset keys collected above. Do not ask the backend to expand whole
          // shared/user/character trees; after Custom Content exists those can
          // inflate Placement by hundreds of MB.
          // Placement and Custom Level are exact-content packages. Concrete
          // runtime references are collected above as assetKeys; do not expand
          // whole user/character/shared trees.
          userAssetPrefixes: [],
          localeFolders: [],
          characterNames: [],
          includeShared: false,
        };
      } catch (error) {
        throw makeStageError(error, { stage: currentStage, operation: currentOperation });
      }

      if (offlinePackageMode !== 'legacy') {
        try {
          return await syncPrebuiltOfflinePacks({
            playData,
            common,
            scope,
            activityIds,
            manifestPayload,
            packSignature,
            emitLoading,
            loadingSource,
            diagnostics,
          });
        } catch (prebuiltError) {
          await logOfflineDiagnosticEvent('prebuilt-attempt-failed', {
            scope,
            mode: offlinePackageMode,
            errorMessage: prebuiltError?.message || String(prebuiltError),
          });
          // Placement is prebuilt-only here. Falling back to the legacy manifest
          // hides the real prebuilt error and can spend 120s building a large
          // legacy payload before surfacing an unrelated timeout. Fail fast so
          // the first error is shown immediately and can be fixed at its source.
          if (
            scope === 'placement'
            || offlinePackageMode === 'prebuilt'
            || prebuiltError?.disableLegacyFallback === true
          ) {
            throw makeStageError(prebuiltError, {
              stage: 'Loading prebuilt offline content',
              operation: 'Resolving/downloading prebuilt packs',
            });
          }
          diagnostics.fallbackUsed = true;
          diagnostics.fallbackReason = prebuiltError?.message || String(prebuiltError);
          diagnostics.mode = 'prebuilt-fallback-to-legacy';
          emitLoadingPhase(emitLoading, loadingSource, {
            label: scope === 'placement' ? 'Preparing Placement...' : 'Preparing content...',
            detail: 'Preparing content download...',
          });
        }
      }

      // Legacy path below is intentionally preserved byte-for-byte in behavior.
      // It is reached directly in `legacy`, or only after a prebuilt failure in
      // `prebuilt-with-fallback`. This keeps rollback independent of the new pack path.
      emitLoadingPhase(emitLoading, loadingSource, {
        label: scope === 'placement' ? 'Preparing Placement...' : 'Preparing content...',
        detail: 'Preparing learning content...',
      });

      currentStage = 'Requesting offline package';
      currentOperation = 'POST /api/mobile-v2/offline/manifest (batched)';
      let manifest;
      try {
        const chunk = (items, size) => {
          const output = [];
          for (let index = 0; index < items.length; index += size) output.push(items.slice(index, index + size));
          return output;
        };

        const requests = [];
        chunk(manifestPayload.activityIds, 1).forEach((ids) => {
          requests.push({
            activityIds: ids,
            assetKeys: [],
            userAssetPrefixes: [],
            localeFolders: [],
            characterNames: [],
            includeShared: false,
            placementEnglishSpanishOnly: scope === 'placement',
            // Custom Level keeps the complete base Activity folder but only the
            // learner's active languageVersionData folders.
            activityLanguageCodes: manifestPayload.activityLanguageCodes,
            activityRuntimeBaselineOnly: scope === 'placement',
            includeRuntimeBaseline: false,
            includeThemeAssets: false,
          });
        });
        chunk(manifestPayload.assetKeys, 50).forEach((keys) => {
          requests.push({
            activityIds: [],
            assetKeys: keys,
            userAssetPrefixes: [],
            localeFolders: [],
            characterNames: [],
            includeShared: false,
            placementEnglishSpanishOnly: scope === 'placement',
            activityRuntimeBaselineOnly: false,
            includeRuntimeBaseline: false,
            includeThemeAssets: false,
          });
        });
        if (manifestPayload.includeRuntimeBaseline) {
          requests.push({
            activityIds: [],
            assetKeys: [],
            userAssetPrefixes: [],
            localeFolders: [],
            characterNames: [],
            includeShared: false,
            placementEnglishSpanishOnly: scope === 'placement',
            activityRuntimeBaselineOnly: false,
            includeRuntimeBaseline: true,
            includeThemeAssets: manifestPayload.includeThemeAssets === true,
          });
        }

        if (
          manifestPayload.includeShared
          || manifestPayload.userAssetPrefixes.length
          || manifestPayload.localeFolders.length
          || manifestPayload.characterNames.length
        ) {
          requests.push({
            activityIds: [],
            assetKeys: [],
            userAssetPrefixes: manifestPayload.userAssetPrefixes,
            localeFolders: manifestPayload.localeFolders,
            characterNames: manifestPayload.characterNames,
            includeShared: manifestPayload.includeShared,
            includeThemeAssets: false,
          });
        }

        const batchResults = new Array(requests.length);
        let nextBatch = 0;
        diagnostics.manifestRequestCount = requests.length;
        let completedBatches = 0;
        const BATCH_CONCURRENCY = 8;

        emitLoadingPhase(emitLoading, loadingSource, {
          label: scope === 'placement' ? 'Preparing Placement...' : 'Preparing content...',
          detail: scope === 'placement'
            ? `Building offline package · 0 / ${requests.length} requests`
            : `Preparing download list · 0 / ${requests.length}`,
        });

        const describeManifestRequest = (request = {}) => {
          const firstActivityId = Array.isArray(request.activityIds) && request.activityIds.length
            ? String(request.activityIds[0] || '')
            : '';
          if (firstActivityId) return { kind: 'activity', activityId: firstActivityId };
          if (request.includeRuntimeBaseline) return { kind: 'runtime-baseline' };
          if (Array.isArray(request.assetKeys) && request.assetKeys.length) {
            return { kind: 'asset-keys', assetKeyCount: request.assetKeys.length };
          }
          return { kind: 'shared' };
        };

        const mergeManifestData = (parts = []) => {
          const byIdentity = new Map();
          let manifestVersion = '';
          parts.forEach((part) => {
            if (!part) return;
            if (part.manifestVersion) manifestVersion = String(part.manifestVersion);
            (Array.isArray(part.files) ? part.files : []).forEach((entry) => {
              const identity = `${entry?.source || ''}:${entry?.key || ''}`;
              if (entry?.key && !byIdentity.has(identity)) byIdentity.set(identity, entry);
            });
          });
          const files = [...byIdentity.values()];
          return {
            files,
            fileCount: files.length,
            totalBytes: files.reduce((sum, entry) => sum + Number(entry?.size || 0), 0),
            manifestVersion,
          };
        };

        const requestCustomAssetKeysSafely = async ({ requestNumber, keys, depth = 0 }) => {
          const startedAt = Date.now();
          const requestMeta = {
            kind: 'asset-keys',
            assetKeyCount: keys.length,
            assetKeys: keys,
            splitDepth: depth,
          };

          await logOfflineDiagnosticEvent('custom-content-manifest-request-start', {
            requestNumber,
            requestCount: requests.length,
            attempt: 1,
            ...requestMeta,
          });

          try {
            const batchResponse = await axios.post(
              '/api/mobile-v2/offline/manifest',
              {
                activityIds: [],
                assetKeys: keys,
                userAssetPrefixes: [],
                localeFolders: [],
                characterNames: [],
                includeShared: false,
                placementEnglishSpanishOnly: false,
                activityRuntimeBaselineOnly: false,
                includeRuntimeBaseline: false,
              },
              { timeout: 30000 },
            );
            const data = batchResponse?.data?.data || batchResponse?.data || {};
            await logOfflineDiagnosticEvent('custom-content-manifest-request-complete', {
              requestNumber,
              requestCount: requests.length,
              attempt: 1,
              durationMs: Date.now() - startedAt,
              fileCount: Array.isArray(data?.files) ? data.files.length : Number(data?.fileCount || 0),
              ...requestMeta,
            });
            return data;
          } catch (error) {
            const status = Number(error?.response?.status || 0);
            await logOfflineDiagnosticEvent('custom-content-manifest-request-failed', {
              requestNumber,
              requestCount: requests.length,
              attempt: 1,
              durationMs: Date.now() - startedAt,
              errorName: error?.name || '',
              errorMessage: error?.message || String(error),
              errorCode: error?.code || '',
              status,
              ...requestMeta,
            });

            // The backend rejects an exact-key batch with HTTP 400 when the batch
            // contains only keys that do not map to downloadable mobile content.
            // Split only Custom Content exact-key batches so valid siblings are kept
            // and one stale/non-downloadable key cannot block the whole Custom Level.
            if (status === 400 && keys.length > 1) {
              const middle = Math.ceil(keys.length / 2);
              const leftKeys = keys.slice(0, middle);
              const rightKeys = keys.slice(middle);
              await logOfflineDiagnosticEvent('custom-content-manifest-asset-keys-split', {
                requestNumber,
                requestCount: requests.length,
                splitDepth: depth,
                originalCount: keys.length,
                leftCount: leftKeys.length,
                rightCount: rightKeys.length,
              });
              const [left, right] = await Promise.all([
                requestCustomAssetKeysSafely({ requestNumber, keys: leftKeys, depth: depth + 1 }),
                requestCustomAssetKeysSafely({ requestNumber, keys: rightKeys, depth: depth + 1 }),
              ]);
              return mergeManifestData([left, right]);
            }

            if (status === 400 && keys.length === 1) {
              await logOfflineDiagnosticEvent('custom-content-manifest-asset-key-skipped', {
                requestNumber,
                requestCount: requests.length,
                assetKey: keys[0],
                reason: 'backend-400-no-mobile-content',
              });
              return { files: [], fileCount: 0, totalBytes: 0, manifestVersion: '' };
            }

            throw error;
          }
        };

        const requestManifestBatch = async (batchIndex) => {
          const request = requests[batchIndex];
          const requestNumber = batchIndex + 1;
          const requestMeta = describeManifestRequest(request);

          if (
            scope === 'custom-level'
            && requestMeta.kind === 'asset-keys'
            && Array.isArray(request.assetKeys)
            && request.assetKeys.length
          ) {
            return requestCustomAssetKeysSafely({
              requestNumber,
              keys: request.assetKeys,
            });
          }

          const timeout = scope === 'custom-level' ? 30000 : 120000;
          const maxAttempts = scope === 'custom-level' ? 2 : 1;

          for (let attempt = 1; attempt <= maxAttempts; attempt += 1) {
            const startedAt = Date.now();
            if (scope === 'custom-level') {
              await logOfflineDiagnosticEvent('custom-content-manifest-request-start', {
                requestNumber,
                requestCount: requests.length,
                attempt,
                ...requestMeta,
              });
            }

            try {
              const batchResponse = await axios.post(
                '/api/mobile-v2/offline/manifest',
                request,
                { timeout },
              );
              const data = batchResponse?.data?.data || batchResponse?.data || {};
              if (scope === 'custom-level') {
                await logOfflineDiagnosticEvent('custom-content-manifest-request-complete', {
                  requestNumber,
                  requestCount: requests.length,
                  attempt,
                  durationMs: Date.now() - startedAt,
                  fileCount: Array.isArray(data?.files) ? data.files.length : Number(data?.fileCount || 0),
                  ...requestMeta,
                });
              }
              return data;
            } catch (error) {
              if (scope === 'custom-level') {
                await logOfflineDiagnosticEvent('custom-content-manifest-request-failed', {
                  requestNumber,
                  requestCount: requests.length,
                  attempt,
                  durationMs: Date.now() - startedAt,
                  errorName: error?.name || '',
                  errorMessage: error?.message || String(error),
                  errorCode: error?.code || '',
                  status: Number(error?.response?.status || 0),
                  ...requestMeta,
                });
              }

              if (attempt >= maxAttempts) {
                const id = requestMeta.activityId ? ` activity ${requestMeta.activityId}` : '';
                throw new Error(
                  `Custom Content package request ${requestNumber}/${requests.length}${id} failed after ${maxAttempts} attempts: ${error?.message || error}`,
                );
              }

              emitLoadingPhase(emitLoading, loadingSource, {
                label: 'Preparing content...',
                detail: `Preparing download list · ${completedBatches} / ${requests.length} · retrying ${requestNumber}`,
              });
            }
          }
          return {};
        };

        const worker = async () => {
          while (nextBatch < requests.length) {
            const batchIndex = nextBatch;
            nextBatch += 1;
            batchResults[batchIndex] = await requestManifestBatch(batchIndex);
            completedBatches += 1;
            emitLoadingPhase(emitLoading, loadingSource, {
              label: scope === 'placement' ? 'Preparing Placement...' : 'Preparing content...',
              detail: scope === 'placement'
                ? `Building offline package · ${completedBatches} / ${requests.length} requests`
                : `Preparing download list · ${completedBatches} / ${requests.length}`,
            });
          }
        };
        await Promise.all(Array.from(
          { length: Math.min(BATCH_CONCURRENCY, requests.length || 1) },
          () => worker(),
        ));

        const byKey = new Map();
        batchResults.forEach((batch) => {
          (Array.isArray(batch?.files) ? batch.files : []).forEach((entry) => {
            const identity = `${entry?.source || ''}:${entry?.key || ''}`;
            if (entry?.key && !byKey.has(identity)) byKey.set(identity, entry);
          });
        });
        const files = [...byKey.values()].sort((left, right) => String(left.key).localeCompare(String(right.key)));
        const totalBytes = files.reduce((sum, entry) => sum + Number(entry?.size || 0), 0);
        const manifestVersion = hashSignature(batchResults
          .map((batch) => String(batch?.manifestVersion || ''))
          .join('|'));
        manifest = { manifestVersion, totalBytes, fileCount: files.length, files };
        await logMobileManifestAudit({
          scope,
          requests,
          batchResults,
          manifest,
        });
      } catch (error) {
        throw makeStageError(error, { stage: currentStage, operation: currentOperation });
      }
      const manifestVersion = String(manifest.manifestVersion || 'unknown');
      const files = Array.isArray(manifest.files) ? manifest.files : [];
      stampDiagnostic(diagnostics, 'manifestReady');
      diagnostics.manifestVersion = manifestVersion;
      diagnostics.fileCount = files.length;
      diagnostics.totalBytes = Number(manifest.totalBytes || 0);

      if (scope === 'placement') {
        await logPlacementManifestDiagnostics(files, Number(manifest.totalBytes || 0));
      }

      if (!files.length) {
        throw new Error('Offline manifest returned no files for this content package.');
      }

      emitLoadingPhase(emitLoading, loadingSource, {
        label: scope === 'placement' ? 'Preparing Placement...' : 'Preparing content...',
        detail: `Learning content · ${formatBytes(Number(manifest.totalBytes || 0))}`,
      });

      // For a completely new Custom Level, keep the fast TAR package path.
      // If files from an earlier level/package already exist locally, reuse them
      // and download only files that are new or changed. This prevents Level 2
      // from redownloading Level 1 plus the same shared runtime assets.
      const reusableCustomLevelFiles = scope === 'custom-level'
        ? files.filter((entry) => {
          const local = assetMeta[entry.key];
          return Boolean(
            local?.nativeUri
            && String(local.etag || '') === String(entry.etag || '')
            && Number(local.size || 0) === Number(entry.size || 0)
          );
        })
        : [];
      const useIncrementalCustomLevelSync = scope === 'custom-level'
        && reusableCustomLevelFiles.length > 0;

      if (useIncrementalCustomLevelSync) {
        const reusableBytes = reusableCustomLevelFiles.reduce((sum, entry) => sum + Number(entry?.size || 0), 0);
        const missingBytes = Math.max(0, Number(manifest.totalBytes || 0) - reusableBytes);
        diagnostics.mode = 'incremental-per-file';
        await logOfflineDiagnosticEvent('custom-level-incremental-reuse', {
          manifestVersion,
          totalFiles: files.length,
          reusableFiles: reusableCustomLevelFiles.length,
          downloadFiles: files.length - reusableCustomLevelFiles.length,
          totalBytes: Number(manifest.totalBytes || 0),
          reusableBytes,
          downloadBytes: missingBytes,
        });
        emitLoadingPhase(emitLoading, loadingSource, {
          label: 'Preparing content...',
          detail: `Existing content found · downloading ${formatBytes(missingBytes)} only`,
        });
      }

      if (scope === 'custom-level' && !useIncrementalCustomLevelSync) {
        try {
          return await syncCustomLevelArchive({
            playData,
            manifest,
            packSignature,
            emitLoading,
            loadingSource,
            diagnostics,
          });
        } catch (packageError) {
          console.warn('[mobile-offline][package-fallback]', {
            message: packageError?.message || String(packageError),
          });
          diagnostics.mode = 'per-file-fallback';
          diagnostics.fallbackUsed = true;
          diagnostics.fallbackReason = packageError?.message || String(packageError);
          await logOfflineDiagnosticEvent('custom-level-package-fallback', {
            message: packageError?.message || String(packageError),
          });
          emitLoadingPhase(emitLoading, loadingSource, {
            label: 'Preparing content...',
            detail: 'Fast package unavailable · continuing with standard download...',
          });
        }
      }

      const needsNewVersion = files.filter((entry) => {
        const local = assetMeta[entry.key];
        return !local?.nativeUri
          || String(local.etag || '') !== String(entry.etag || '')
          || Number(local.size || 0) !== Number(entry.size || 0);
      });

      const stagedByKey = new Map();
      let stagedNextIndex = 0;
      let stagedCheckedBytes = 0;
      let stagedFailure = null;
      const STAGED_CHECK_CONCURRENCY = 24;
      const stagedTotalBytes = needsNewVersion.reduce((sum, entry) => sum + Number(entry.size || 0), 0);

      const stagedWorker = async () => {
        while (!stagedFailure) {
          const stagedIndex = stagedNextIndex;
          if (stagedIndex >= needsNewVersion.length) return;
          stagedNextIndex += 1;
          const entry = needsNewVersion[stagedIndex];

          let staged;
          try {
            staged = await withTimeout(
              getStagedEntry(entry, manifestVersion),
              5000,
              `Local storage check timed out for file ${stagedIndex + 1} / ${needsNewVersion.length}.`,
            );
          } catch (error) {
            stagedFailure = makeStageError(error, {
              stage: 'Checking local files',
              operation: `Checking cached file ${stagedIndex + 1} / ${needsNewVersion.length}`,
              itemNumber: stagedIndex + 1,
              totalItems: needsNewVersion.length,
            });
            return;
          }

          if (staged) stagedByKey.set(entry.key, staged);
          stagedCheckedBytes += Number(entry.size || 0);
          emitLoadingPhase(emitLoading, loadingSource, {
            label: scope === 'placement' ? 'Preparing Placement...' : 'Preparing content...',
            detail: scope === 'placement'
              ? `Checking downloaded content · ${formatBytes(stagedCheckedBytes)} / ${formatBytes(stagedTotalBytes)}`
              : `Checking downloaded content · ${formatBytes(stagedCheckedBytes)} / ${formatBytes(stagedTotalBytes)}`,
          });
        }
      };

      currentStage = 'Checking local files';
      currentOperation = 'Checking cached offline files';
      await Promise.all(Array.from(
        { length: Math.min(STAGED_CHECK_CONCURRENCY, needsNewVersion.length || 1) },
        () => stagedWorker(),
      ));
      if (stagedFailure) throw stagedFailure;
      const pending = needsNewVersion.filter((entry) => !stagedByKey.has(entry.key));
      const updateBytes = pending.reduce((sum, entry) => sum + Number(entry.size || 0), 0);
      const totalBytes = files.reduce((sum, entry) => sum + Number(entry.size || 0), 0);

      updateOfflineState({
        totalBytes,
        updateBytes,
        downloadedBytes: 0,
        pendingFiles: pending.length,
        readyFiles: files.length - pending.length,
        packStatus: pending.length ? 'downloading' : (needsNewVersion.length ? 'activating' : 'ready'),
        message: '',
        partial: needsNewVersion.length > 0,
      });

      let completedBytes = 0;
      let completedFiles = 0;
      let downloadNextIndex = 0;
      let downloadFailure = null;
      const DOWNLOAD_CONCURRENCY = 8;

      const downloadWorker = async () => {
        while (!downloadFailure) {
          const index = downloadNextIndex;
          if (index >= pending.length) return;
          downloadNextIndex += 1;

          const currentEntry = pending[index];
          const currentType = friendlyAssetType(currentEntry);
          let result;
          let attempt = 0;

          while (!result && !downloadFailure) {
            if (!isOnline()) {
              await waitForConnection({
                emitLoading,
                loadingSource,
                scope,
                detail: `Connection lost. Waiting to reconnect... ${completedFiles} / ${pending.length} files ready`,
              });
            }

            try {
              result = await withTimeout(
                downloadEntry(currentEntry, 0, manifestVersion),
                120000,
                `Download stalled for ${currentType} ${index + 1} / ${pending.length} after 120 seconds.`,
              );
            } catch (error) {
              if (isRecoverableNetworkError(error)) {
                attempt += 1;
                console.info('[mobile-offline][download-retry]', {
                  scope,
                  manifestVersion,
                  fileNumber: index + 1,
                  totalFiles: pending.length,
                  key: currentEntry?.key || null,
                  attempt,
                  online: isOnline(),
                  message: error?.message || String(error),
                });

                if (!isOnline()) {
                  await waitForConnection({ emitLoading, loadingSource, scope });
                } else {
                  emitLoadingPhase(emitLoading, loadingSource, {
                    label: scope === 'placement' ? 'Preparing Placement...' : 'Preparing content...',
                    detail: 'Connection interrupted. Retrying download...',
                  });
                  await sleep(Math.min(4000, 750 * attempt));
                }

                // Transient network errors are never surfaced to the learner.
                // Keep retrying the same pending file; already completed files remain staged.
                continue;
              }

              const contextualError = makeStageError(error, {
                stage: 'Downloading learning content',
                operation: `Downloading ${currentType}`,
                itemNumber: index + 1,
                totalItems: pending.length,
              });
              console.error('[mobile-offline][download-failed]', {
                scope,
                manifestVersion,
                index,
                fileNumber: index + 1,
                totalFiles: pending.length,
                key: currentEntry?.key || null,
                expectedSize: Number(currentEntry?.size || 0),
                error: contextualError.offlineDiagnostic || contextualError.message,
              });
              downloadFailure = contextualError;
              return;
            }
          }

          stagedByKey.set(currentEntry.key, result.stagedEntry);
          completedBytes += Number(currentEntry.size || 0);
          completedFiles += 1;
          const ratio = updateBytes > 0 ? Math.min(1, completedBytes / updateBytes) : 1;

          updateOfflineState({
            packStatus: 'downloading',
            downloadedBytes: completedBytes,
            pendingFiles: pending.length - completedFiles,
            readyFiles: files.length - pending.length + completedFiles,
            message: '',
            partial: true,
          });
          emitLoadingPhase(emitLoading, loadingSource, {
            label: scope === 'placement' ? 'Preparing Placement...' : 'Preparing content...',
            detail: scope === 'placement'
              ? `${formatBytes(completedBytes)} / ${formatBytes(updateBytes)}`
              : `Downloading learning content · ${formatBytes(completedBytes)} / ${formatBytes(updateBytes)}`,
            progress: Math.round(ratio * 100),
          });
        }
      };

      diagnostics.mode = diagnostics.fallbackUsed ? 'per-file-fallback' : 'per-file';
      diagnostics.downloadedBytes = updateBytes;
      stampDiagnostic(diagnostics, 'downloadStarted');
      emitLoadingPhase(emitLoading, loadingSource, {
        label: scope === 'placement' ? 'Preparing Placement...' : 'Preparing content...',
        detail: scope === 'placement'
          ? `Downloading learning content · ${formatBytes(0)} / ${formatBytes(updateBytes)}`
          : `Downloading learning content · ${formatBytes(0)} / ${formatBytes(updateBytes)}`,
        progress: 0,
      });

      await Promise.all(Array.from(
        { length: Math.min(DOWNLOAD_CONCURRENCY, pending.length || 1) },
        () => downloadWorker(),
      ));
      stampDiagnostic(diagnostics, 'downloadCompleted');

      if (downloadFailure) {
        updateOfflineState({
          packStatus: isOnline() ? 'error' : 'paused',
          downloadedBytes: completedBytes,
          pendingFiles: pending.length - completedFiles,
          message: '',
          partial: true,
        });
        throw downloadFailure;
      }

      stampDiagnostic(diagnostics, 'verifyStarted');
      emitLoadingPhase(emitLoading, loadingSource, {
        label: scope === 'placement' ? 'Preparing Placement...' : 'Preparing content...',
        detail: scope === 'placement'
          ? `Validating offline content · ${formatBytes(totalBytes)}`
          : `Validating offline content · ${formatBytes(totalBytes)}`,
      });

      // Preserve previously activated scopes. Custom Level activation must not erase
      // the Placement pack that was prepared after registration (and vice versa).
      const nextMeta = { ...assetMeta };
      files.forEach((entry) => {
        const active = assetMeta[entry.key];
        const unchanged = active?.nativeUri
          && String(active.etag || '') === String(entry.etag || '')
          && Number(active.size || 0) === Number(entry.size || 0);
        if (unchanged) nextMeta[entry.key] = active;
        else if (stagedByKey.has(entry.key)) nextMeta[entry.key] = stagedByKey.get(entry.key);
      });

      const missingEntries = files.filter((entry) => !nextMeta[entry.key]?.nativeUri);
      if (missingEntries.length) {
        diagnostics.missingFiles = missingEntries.length;
        console.error('[mobile-offline][validation-missing]', {
          scope,
          manifestVersion,
          missingCount: missingEntries.length,
          missingKeys: missingEntries.slice(0, 25).map((entry) => entry.key),
        });
        throw makeStageError(
          new Error(`Offline package validation failed (${missingEntries.length} file(s) missing).`),
          { stage: 'Validating offline content', operation: 'Checking downloaded package completeness' },
        );
      }

      diagnostics.missingFiles = 0;
      diagnostics.verifiedFiles = files.length;
      stampDiagnostic(diagnostics, 'verifyCompleted');
      assetMeta = nextMeta;
      const readyPackRecord = {
        status: 'ready',
        signature: packSignature,
        manifestVersion,
        mode: 'legacy',
        fileCount: files.length,
        totalBytes,
        readyAt: Date.now(),
        ...(scope === 'custom-level' ? {
          generatedLevelId: String(playData?.mobileBootstrap?.generatedCustomContentLevel?._id || ''),
          activityIds: [...activityIds].map((activityId) => String(activityId || '')).filter(Boolean).sort(),
        } : {}),
      };
      rememberReadyPackForSignature(scope, packSignature, readyPackRecord);
      if (scope === 'custom-level') {
        emitLoadingPhase(emitLoading, loadingSource, {
          label: 'Preparing content...',
          detail: 'Saving offline package...',
        });
      }
      currentStage = 'Activating offline package';
      currentOperation = 'Saving package metadata';
      try {
        await Promise.all([saveMeta(), savePackMeta()]);
      } catch (error) {
        throw makeStageError(error, { stage: currentStage, operation: currentOperation });
      }

      updateOfflineState({
        packStatus: 'ready',
        downloadedBytes: updateBytes,
        pendingFiles: 0,
        readyFiles: files.length,
        lastUpdatedAt: Date.now(),
        message: '',
        partial: false,
      });
      emitLoadingPhase(emitLoading, loadingSource, {
        label: scope === 'placement' ? 'Preparing Placement...' : 'Preparing content...',
        detail: 'Content ready offline',
        progress: 100,
      });

      console.info('[mobile-offline][pack-ready]', {
        scope,
        manifestVersion,
        activityCount: activityIds.length,
        fileCount: files.length,
        totalBytes,
      });
      const uploadReadyDiagnostics = scope === 'custom-level'
        ? uploadReadyCustomDiagnosticsInBackground
        : uploadOfflineDiagnostics;
      await uploadReadyDiagnostics(diagnostics, playData, {
        status: 'ready',
        manifestVersion,
        fileCount: files.length,
        verifiedFiles: files.length,
        missingFiles: 0,
        totalBytes,
        downloadedBytes: updateBytes,
        pendingFilesAtStart: pending.length,
        cachedFilesAtStart: files.length - pending.length,
      });
      return { status: 'ready', scope, manifestVersion, fileCount: files.length, totalBytes };
    } catch (error) {
      const contextualError = error?.offlineDiagnostic
        ? error
        : makeStageError(error, { stage: currentStage, operation: currentOperation });
      const readableMessage = diagnosticMessage(contextualError, scope);
      const stackLines = String(contextualError?.stack || '')
        .split('\n')
        .map((line) => line.trim())
        .filter(Boolean);
      const sourceLine = stackLines.find((line) => /^at\s+/.test(line)) || '';
      const sourceHint = sourceLine
        ? ` · source: ${sanitizeDiagnosticValue(sourceLine.replace(/^at\s+/, ''))}`
        : '';
      const fullDiagnostic = `${readableMessage}${sourceHint}`;

      // Never rethrow the original URIError/TypeError. Some callers/render paths
      // end up reading the native error message and discard our contextual fields.
      // A fresh Error guarantees the stage/operation detail is what reaches UI.
      const diagnosticError = new Error(fullDiagnostic);
      diagnosticError.name = 'OfflinePreparationError';
      diagnosticError.offlineStage = contextualError.offlineStage || currentStage;
      diagnosticError.offlineOperation = contextualError.offlineOperation || currentOperation;
      diagnosticError.offlineDiagnostic = fullDiagnostic;
      diagnosticError.originalErrorName = contextualError?.name || 'Error';
      diagnosticError.originalErrorMessage = contextualError?.message || String(contextualError);
      diagnosticError.originalStack = contextualError?.stack || null;

      console.warn('[mobile-offline] asset sync failed', {
        scope,
        stage: diagnosticError.offlineStage,
        operation: diagnosticError.offlineOperation,
        diagnostic: fullDiagnostic,
        originalErrorName: diagnosticError.originalErrorName,
        originalErrorMessage: diagnosticError.originalErrorMessage,
        originalStack: diagnosticError.originalStack,
      });
      updateOfflineState({
        packStatus: 'partial',
        message: fullDiagnostic,
        partial: true,
      });
      emitLoadingPhase(emitLoading, loadingSource, {
        label: scope === 'placement' ? 'Placement preparation failed' : 'Custom Level preparation failed',
        detail: fullDiagnostic,
      });
      throw diagnosticError;
    }
  })();

  try {
    return await syncPromise;
  } finally {
    syncPromise = null;
  }
}

export async function initializeAssetResolver() {
  await loadMeta();
  const nativeUriRebase = await rebasePersistedAssetNativeUris();
  installDomResolver();
  await logOfflineDiagnosticEvent('asset-flow-audit-enabled', {
    version: '2026-10-08-asset-flow-v1',
    note: 'Diagnostics only; download/render behavior unchanged',
  });
  await logOfflineDiagnosticEvent('runtime-asset-diagnostics-enabled', {
    version: '2026-10-06-runtime-assets-v2-root-rebase',
    assetMetaCount: Object.keys(assetMeta || {}).length,
    nativeUriRebaseStatus: nativeUriRebase?.status || '',
    nativeUriRebasedCount: Number(nativeUriRebase?.changedCount || 0),
    nativeUriAnchorKey: nativeUriRebase?.anchorKey || '',
    connected: getOfflineState()?.connected !== false,
    connectionType: getOfflineState()?.connectionType || '',
    navigatorOnline: typeof navigator === 'undefined' ? null : navigator.onLine !== false,
  });
}

const rewriteCssUrls = (value = '') => String(value || '').replace(
  /url\((['"]?)(.*?)\1\)/gi,
  (match, quote, rawUrl) => {
    const resolved = resolveOfflineAssetUrlSync(rawUrl);
    return `url(${quote || ''}${resolved}${quote || ''})`;
  },
);

const rewriteSrcSet = (value = '') => String(value || '')
  .split(',')
  .map((candidate) => {
    const trimmed = candidate.trim();
    if (!trimmed) return trimmed;
    const parts = trimmed.split(/\s+/);
    const resolved = resolveOfflineAssetUrlSync(parts[0]);
    return [resolved, ...parts.slice(1)].join(' ');
  })
  .join(', ');

function rewriteElement(element) {
  if (!element || !shouldResolveFromOfflineStorage()) return;

  ['src', 'poster'].forEach((attr) => {
    const value = element.getAttribute?.(attr);
    const resolved = resolveOfflineAssetUrlSync(value);
    if (value && resolved && resolved !== value) element.setAttribute(attr, resolved);
  });

  const srcset = element.getAttribute?.('srcset');
  if (srcset) {
    const nextSrcset = rewriteSrcSet(srcset);
    if (nextSrcset !== srcset) element.setAttribute('srcset', nextSrcset);
  }

  const style = element.getAttribute?.('style');
  if (style && /url\(/i.test(style)) {
    const nextStyle = rewriteCssUrls(style);
    if (nextStyle !== style) element.setAttribute('style', nextStyle);
  }
}


const trimRuntimeDiagnosticValue = (value, limit = 1200) => {
  const text = String(value == null ? '' : value);
  return text.length > limit ? `${text.slice(0, limit)}…` : text;
};

const extractCssRuntimeUrls = (value = '') => {
  const urls = [];
  String(value || '').replace(/url\((['"]?)(.*?)\1\)/gi, (match, quote, rawUrl) => {
    if (rawUrl) urls.push(rawUrl);
    return match;
  });
  return urls;
};

const runtimeAssetCandidate = (rawUrl = '') => {
  const url = String(rawUrl || '').trim();
  if (!url || /^data:/i.test(url)) return null;
  const key = canonicalAssetKey(url);
  const entry = key ? getAssetMetaEntry(key) : null;
  return {
    url: trimRuntimeDiagnosticValue(url),
    key: String(key || ''),
    alreadyLocal: isAlreadyLocalOfflineUrl(url),
    hasAssetMeta: Boolean(entry),
    nativeUri: trimRuntimeDiagnosticValue(entry?.nativeUri || ''),
    localCandidate: trimRuntimeDiagnosticValue(entry?.nativeUri ? convertFileSrc(entry.nativeUri) : ''),
    size: Number(entry?.size || 0),
    manifestVersion: String(entry?.manifestVersion || ''),
    prebuiltPackId: String(entry?.prebuiltPackId || ''),
    prebuiltPackVersion: String(entry?.prebuiltPackVersion || ''),
  };
};

const describeRuntimeAssetElement = (element) => {
  if (!element || element.nodeType !== 1) return null;
  let computedBackgroundImage = '';
  try {
    computedBackgroundImage = window.getComputedStyle?.(element)?.backgroundImage || '';
  } catch (_) {}

  const src = element.getAttribute?.('src') || '';
  const currentSrc = element.currentSrc || '';
  const poster = element.getAttribute?.('poster') || '';
  const srcset = element.getAttribute?.('srcset') || '';
  const inlineStyle = element.getAttribute?.('style') || '';
  const backgroundUrls = [
    ...extractCssRuntimeUrls(inlineStyle),
    ...extractCssRuntimeUrls(computedBackgroundImage),
  ];
  const srcsetUrls = String(srcset || '')
    .split(',')
    .map((candidate) => candidate.trim().split(/\s+/)[0])
    .filter(Boolean);
  const rawUrls = [src, currentSrc, poster, ...srcsetUrls, ...backgroundUrls]
    .map((value) => String(value || '').trim())
    .filter(Boolean);

  if (!rawUrls.length && (!computedBackgroundImage || computedBackgroundImage === 'none')) return null;

  const seen = new Set();
  const candidates = rawUrls
    .filter((value) => {
      if (seen.has(value)) return false;
      seen.add(value);
      return true;
    })
    .map(runtimeAssetCandidate)
    .filter(Boolean);

  let rect = null;
  try {
    const value = element.getBoundingClientRect?.();
    if (value) rect = {
      x: Math.round(value.x || 0),
      y: Math.round(value.y || 0),
      width: Math.round(value.width || 0),
      height: Math.round(value.height || 0),
    };
  } catch (_) {}

  return {
    tag: String(element.tagName || '').toLowerCase(),
    id: trimRuntimeDiagnosticValue(element.id || '', 240),
    className: trimRuntimeDiagnosticValue(
      typeof element.className === 'string' ? element.className : element.getAttribute?.('class') || '',
      500,
    ),
    src: trimRuntimeDiagnosticValue(src),
    currentSrc: trimRuntimeDiagnosticValue(currentSrc),
    poster: trimRuntimeDiagnosticValue(poster),
    srcset: trimRuntimeDiagnosticValue(srcset),
    inlineStyle: trimRuntimeDiagnosticValue(inlineStyle),
    computedBackgroundImage: trimRuntimeDiagnosticValue(computedBackgroundImage),
    complete: typeof element.complete === 'boolean' ? element.complete : null,
    naturalWidth: Number(element.naturalWidth || 0),
    naturalHeight: Number(element.naturalHeight || 0),
    readyState: typeof element.readyState === 'number' ? element.readyState : null,
    rect,
    candidates,
  };
};

const logRuntimeAssetSnapshot = async (phase, source = '') => {
  if (typeof document === 'undefined' || !document.documentElement) return;
  const all = Array.from(document.querySelectorAll('body *'));
  const items = all.map(describeRuntimeAssetElement).filter(Boolean);
  const connected = getOfflineState()?.connected !== false;
  const remoteCandidates = items.reduce((sum, item) => (
    sum + item.candidates.filter((candidate) => !candidate.alreadyLocal).length
  ), 0);
  const localCandidates = items.reduce((sum, item) => (
    sum + item.candidates.filter((candidate) => candidate.alreadyLocal).length
  ), 0);
  const metaHits = items.reduce((sum, item) => (
    sum + item.candidates.filter((candidate) => candidate.hasAssetMeta).length
  ), 0);
  const zeroSizedImages = items.filter((item) => (
    item.tag === 'img' && item.complete === true && item.naturalWidth === 0
  )).length;

  await logOfflineDiagnosticEvent('runtime-asset-snapshot-summary', {
    epoch: runtimeAssetDiagnosticEpoch,
    phase,
    source,
    connected,
    connectionType: getOfflineState()?.connectionType || '',
    navigatorOnline: typeof navigator === 'undefined' ? null : navigator.onLine !== false,
    shouldResolveOffline: shouldResolveFromOfflineStorage(),
    location: trimRuntimeDiagnosticValue(window.location?.href || ''),
    bodyClassName: trimRuntimeDiagnosticValue(document.body?.className || '', 500),
    elementCount: items.length,
    remoteCandidates,
    localCandidates,
    metaHits,
    zeroSizedImages,
  });

  const chunkSize = 20;
  for (let offset = 0; offset < items.length; offset += chunkSize) {
    await logOfflineDiagnosticEvent('runtime-asset-snapshot-items', {
      epoch: runtimeAssetDiagnosticEpoch,
      phase,
      source,
      offset,
      total: items.length,
      items: items.slice(offset, offset + chunkSize),
    });
  }
};

const scheduleRuntimeAssetSnapshots = (direction, source = '') => {
  runtimeAssetDiagnosticWindowUntil = Date.now() + 6000;
  const schedule = (delay, phase) => {
    const run = () => Promise.resolve(logRuntimeAssetSnapshot(`${direction}:${phase}`, source)).catch(() => undefined);
    if (delay <= 0) run();
    else window.setTimeout?.(run, delay);
  };
  schedule(0, 't0');
  schedule(50, 't50');
  schedule(250, 't250');
  schedule(1000, 't1000');
};

const logRuntimeAssetLoadEvent = (type, element, extra = {}) => {
  const details = describeRuntimeAssetElement(element);
  if (!details) return;
  Promise.resolve(logOfflineDiagnosticEvent(type, {
    epoch: runtimeAssetDiagnosticEpoch,
    connected: getOfflineState()?.connected !== false,
    connectionType: getOfflineState()?.connectionType || '',
    navigatorOnline: typeof navigator === 'undefined' ? null : navigator.onLine !== false,
    shouldResolveOffline: shouldResolveFromOfflineStorage(),
    element: details,
    ...extra,
  })).catch(() => undefined);
};

const offlineOriginalElementValues = new WeakMap();

const rememberOfflineOriginalValue = (element, attribute, value) => {
  if (!element || typeof value !== 'string' || !value || isAlreadyLocalOfflineUrl(value)) return;
  const current = offlineOriginalElementValues.get(element) || {};
  current[String(attribute || '').toLowerCase()] = value;
  offlineOriginalElementValues.set(element, current);
};

const restoreOfflineOriginalValues = (element) => {
  if (!element) return 0;
  const originals = offlineOriginalElementValues.get(element);
  if (!originals) return 0;

  let restored = 0;
  Object.entries(originals).forEach(([attribute, value]) => {
    if (!value) return;
    try {
      element.setAttribute?.(attribute, value);
      restored += 1;
    } catch (_) {}
  });
  offlineOriginalElementValues.delete(element);
  return restored;
};

function installDomResolver() {
  if (typeof window === 'undefined' || window.__flcOfflineDomResolverInstalled) return;
  window.__flcOfflineDomResolverInstalled = true;

  const rewriteCurrentDocument = () => {
    if (!shouldResolveFromOfflineStorage() || typeof document === 'undefined') return;
    document.querySelectorAll?.('[src],[srcset],[poster],[style]').forEach(rewriteElement);
  };

  const scheduleDocumentRewrite = () => {
    rewriteCurrentDocument();
    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(rewriteCurrentDocument);
    }
    window.setTimeout?.(rewriteCurrentDocument, 25);
    window.setTimeout?.(rewriteCurrentDocument, 100);
    window.setTimeout?.(rewriteCurrentDocument, 300);
    window.setTimeout?.(rewriteCurrentDocument, 1000);
  };

  const restoreCurrentDocument = () => {
    if (shouldResolveFromOfflineStorage() || typeof document === 'undefined') return;
    let restoredCount = 0;
    document.querySelectorAll?.('*').forEach((element) => {
      restoredCount += restoreOfflineOriginalValues(element);
    });
    if (restoredCount > 0) {
      Promise.resolve(logOfflineDiagnosticEvent('runtime-asset-online-restore', {
        epoch: runtimeAssetDiagnosticEpoch,
        restoredCount,
        connected: getOfflineState()?.connected !== false,
        navigatorOnline: typeof navigator === 'undefined' ? null : navigator.onLine !== false,
      })).catch(() => undefined);
    }
  };

  const scheduleDocumentRestore = () => {
    restoreCurrentDocument();
    if (typeof window.requestAnimationFrame === 'function') {
      window.requestAnimationFrame(restoreCurrentDocument);
    }
    window.setTimeout?.(restoreCurrentDocument, 25);
    window.setTimeout?.(restoreCurrentDocument, 100);
    window.setTimeout?.(restoreCurrentDocument, 300);
    window.setTimeout?.(restoreCurrentDocument, 1000);
  };

  const handleOfflineTransition = () => {
    if (!shouldResolveFromOfflineStorage()) return;
    scheduleDocumentRewrite();
  };

  let lastObservedConnected = getOfflineState()?.connected !== false;
  Promise.resolve(logOfflineDiagnosticEvent('runtime-network-initial', {
    connected: lastObservedConnected,
    connectionType: getOfflineState()?.connectionType || '',
    navigatorOnline: typeof navigator === 'undefined' ? null : navigator.onLine !== false,
    shouldResolveOffline: shouldResolveFromOfflineStorage(),
    location: trimRuntimeDiagnosticValue(window.location?.href || ''),
  })).catch(() => undefined);

  const handleObservedNetworkState = (source, nextState = {}) => {
    const connected = nextState?.connected !== false;
    const previousConnected = lastObservedConnected;
    lastObservedConnected = connected;
    if (previousConnected === connected) return;

    runtimeAssetDiagnosticEpoch += 1;
    runtimeAssetResolverLogKeys.clear();
    missingAssetLogKeys.clear();
    const direction = connected ? 'offline-to-online' : 'online-to-offline';

    Promise.resolve(logOfflineDiagnosticEvent('runtime-network-transition', {
      epoch: runtimeAssetDiagnosticEpoch,
      source,
      direction,
      previousConnected,
      connected,
      connectionType: nextState?.connectionType || getOfflineState()?.connectionType || '',
      navigatorOnline: typeof navigator === 'undefined' ? null : navigator.onLine !== false,
      shouldResolveOffline: shouldResolveFromOfflineStorage(),
      location: trimRuntimeDiagnosticValue(window.location?.href || ''),
    })).catch(() => undefined);

    // Snapshot before the resolver mutates the DOM, then again after React,
    // MutationObserver and native network callbacks have had time to run.
    scheduleRuntimeAssetSnapshots(direction, source);
    if (connected) scheduleDocumentRestore();
    else handleOfflineTransition();
  };

  // Subscribe directly to the single offline state source. This is the main
  // transition record used to correlate UI disappearance/recovery with state.
  subscribeOfflineState((nextState) => {
    handleObservedNetworkState('offline-state', nextState || {});
  });

  // Browser events are logged separately because Capacitor and navigator can
  // briefly disagree during airplane/Wi-Fi transitions.
  window.addEventListener('offline', () => {
    Promise.resolve(logOfflineDiagnosticEvent('runtime-network-browser-event', {
      event: 'offline',
      connectedBeforeUpdate: getOfflineState()?.connected !== false,
      navigatorOnline: typeof navigator === 'undefined' ? null : navigator.onLine !== false,
    })).catch(() => undefined);
    if (!isNativeAssetRuntime()) {
      updateOfflineState({ connected: false, connectionType: 'none' });
    }
  });

  window.addEventListener('online', () => {
    Promise.resolve(logOfflineDiagnosticEvent('runtime-network-browser-event', {
      event: 'online',
      connectedBeforeUpdate: getOfflineState()?.connected !== false,
      navigatorOnline: typeof navigator === 'undefined' ? null : navigator.onLine !== false,
    })).catch(() => undefined);
    if (!isNativeAssetRuntime()) {
      updateOfflineState({ connected: true, connectionType: 'browser' });
      scheduleDocumentRestore();
    }
  });

  // Capacitor/iOS is authoritative in the bundled app. Log raw native status
  // before feeding it into the shared state so we can detect stale state races.
  const network = window.Capacitor?.Plugins?.Network;
  if (network?.addListener) {
    Promise.resolve(network.addListener('networkStatusChange', (status) => {
      const connected = status?.connected !== false;
      Promise.resolve(logOfflineDiagnosticEvent('runtime-network-native-event', {
        connected,
        connectionType: status?.connectionType || status?.type || (connected ? 'unknown' : 'none'),
        stateConnectedBeforeUpdate: getOfflineState()?.connected !== false,
        navigatorOnline: typeof navigator === 'undefined' ? null : navigator.onLine !== false,
      })).catch(() => undefined);
      updateOfflineState({
        connected,
        connectionType: status?.connectionType || status?.type || (connected ? 'unknown' : 'none'),
      });
    })).catch((error) => {
      Promise.resolve(logOfflineDiagnosticEvent('runtime-network-native-listener-error', {
        errorMessage: error?.message || String(error),
      })).catch(() => undefined);
    });
  }

  // Preserve the existing event for callers that already dispatch it and log
  // it as a separate signal for correlation with the state/native events.
  window.addEventListener('flc:offline-status', (event) => {
    Promise.resolve(logOfflineDiagnosticEvent('runtime-network-custom-event', {
      connected: event?.detail?.connected !== false,
      connectionType: event?.detail?.connectionType || '',
      navigatorOnline: typeof navigator === 'undefined' ? null : navigator.onLine !== false,
    })).catch(() => undefined);
    if (event?.detail?.connected === false) handleOfflineTransition();
    else if (event?.detail?.connected === true) scheduleDocumentRestore();
  });

  // Resolve URLs at assignment time, not only after an element is mounted.
  // React, Howler and several activity templates assign src to detached
  // Image/Audio/Source elements; a MutationObserver on document cannot see
  // those assignments. This keeps the fix centralized in the offline layer.
  const patchUrlProperty = (prototype, property) => {
    if (!prototype) return;
    const descriptor = Object.getOwnPropertyDescriptor(prototype, property);
    if (!descriptor?.set || !descriptor?.get || descriptor.set.__flcOfflinePatched) return;

    const originalSet = descriptor.set;
    const patchedSet = function patchedOfflineUrl(value) {
      if (typeof value === 'string') {
        logAssetFlowValue(value, {
          channel: `property:${property}`,
          tag: String(this?.tagName || '').toLowerCase(),
        });
      }
      const nextValue = shouldResolveFromOfflineStorage() && typeof value === 'string'
        ? resolveOfflineAssetUrlSync(value)
        : (typeof value === 'string' ? preferCachedVmMedia(value) : value);
      if (
        typeof value === 'string'
        && String(nextValue || '') !== String(value || '')
      ) {
        rememberOfflineOriginalValue(this, property, value);
      }
      if (
        Date.now() <= runtimeAssetDiagnosticWindowUntil
        && typeof value === 'string'
        && String(nextValue || '') !== String(value || '')
      ) {
        Promise.resolve(logOfflineDiagnosticEvent('runtime-asset-property-rewrite', {
          epoch: runtimeAssetDiagnosticEpoch,
          property,
          tag: String(this?.tagName || '').toLowerCase(),
          id: trimRuntimeDiagnosticValue(this?.id || '', 240),
          className: trimRuntimeDiagnosticValue(
            typeof this?.className === 'string' ? this.className : this?.getAttribute?.('class') || '',
            500,
          ),
          originalUrl: trimRuntimeDiagnosticValue(value),
          rewrittenUrl: trimRuntimeDiagnosticValue(nextValue),
        })).catch(() => undefined);
      }
      return originalSet.call(this, nextValue);
    };
    patchedSet.__flcOfflinePatched = true;

    try {
      Object.defineProperty(prototype, property, {
        ...descriptor,
        set: patchedSet,
      });
    } catch (_) {}
  };

  patchUrlProperty(window.HTMLImageElement?.prototype, 'src');
  patchUrlProperty(window.HTMLMediaElement?.prototype, 'src');
  patchUrlProperty(window.HTMLSourceElement?.prototype, 'src');
  patchUrlProperty(window.HTMLVideoElement?.prototype, 'poster');

  const originalSetAttribute = window.Element?.prototype?.setAttribute;
  if (originalSetAttribute && !originalSetAttribute.__flcOfflinePatched) {
    const patchedSetAttribute = function patchedOfflineSetAttribute(name, value) {
      const attr = String(name || '').toLowerCase();
      let nextValue = value;

      if (typeof value === 'string' && ['src', 'poster', 'srcset', 'style'].includes(attr)) {
        logAssetFlowValue(value, {
          channel: `attribute:${attr}`,
          tag: String(this?.tagName || '').toLowerCase(),
        });
      }

      if (shouldResolveFromOfflineStorage() && typeof value === 'string') {
        if (attr === 'src' || attr === 'poster') {
          nextValue = resolveOfflineAssetUrlSync(value);
        } else if (attr === 'srcset') {
          nextValue = rewriteSrcSet(value);
        } else if (attr === 'style' && /url\(/i.test(value)) {
          nextValue = rewriteCssUrls(value);
        }
      } else if (typeof value === 'string' && (attr === 'src' || attr === 'poster')) {
        nextValue = preferCachedVmMedia(value);
      }

      if (
        typeof value === 'string'
        && String(nextValue || '') !== String(value || '')
      ) {
        rememberOfflineOriginalValue(this, attr, value);
      }

      if (
        Date.now() <= runtimeAssetDiagnosticWindowUntil
        && typeof value === 'string'
        && String(nextValue || '') !== String(value || '')
      ) {
        Promise.resolve(logOfflineDiagnosticEvent('runtime-asset-attribute-rewrite', {
          epoch: runtimeAssetDiagnosticEpoch,
          attribute: attr,
          tag: String(this?.tagName || '').toLowerCase(),
          id: trimRuntimeDiagnosticValue(this?.id || '', 240),
          className: trimRuntimeDiagnosticValue(
            typeof this?.className === 'string' ? this.className : this?.getAttribute?.('class') || '',
            500,
          ),
          originalValue: trimRuntimeDiagnosticValue(value),
          rewrittenValue: trimRuntimeDiagnosticValue(nextValue),
        })).catch(() => undefined);
      }

      return originalSetAttribute.call(this, name, nextValue);
    };
    patchedSetAttribute.__flcOfflinePatched = true;
    try { window.Element.prototype.setAttribute = patchedSetAttribute; } catch (_) {}
  }

  const originalFetch = window.fetch?.bind(window);
  if (originalFetch && !window.fetch?.__flcOfflinePatched) {
    const patchedFetch = (input, init) => {
      const originalUrl = typeof input === 'string' ? input : input?.url;
      logAssetFlowRuntimeRequest(originalUrl, { channel: 'fetch', tag: 'fetch' });
      const resolved = resolveOfflineAssetUrlSync(originalUrl);
      if (resolved && originalUrl && resolved !== originalUrl) return originalFetch(resolved, init);
      return originalFetch(input, init);
    };
    patchedFetch.__flcOfflinePatched = true;
    window.fetch = patchedFetch;
  }

  const xhrPrototype = window.XMLHttpRequest?.prototype;
  if (xhrPrototype?.open && !xhrPrototype.open.__flcAssetFlowPatched) {
    const originalOpen = xhrPrototype.open;
    const patchedOpen = function patchedAssetFlowXhrOpen(method, url, ...rest) {
      if (typeof url === 'string') {
        logAssetFlowRuntimeRequest(url, { channel: 'xhr', tag: 'xhr' });
      }
      return originalOpen.call(this, method, url, ...rest);
    };
    patchedOpen.__flcAssetFlowPatched = true;
    try { xhrPrototype.open = patchedOpen; } catch (_) {}
  }

  const observer = new MutationObserver((mutations) => {
    mutations.forEach((mutation) => {
      if (mutation.type === 'attributes') {
        const attr = String(mutation.attributeName || '');
        const before = mutation.oldValue;
        const currentRaw = mutation.target?.getAttribute?.(attr) || '';
        if (['src', 'poster', 'srcset', 'style'].includes(attr.toLowerCase())) {
          logAssetFlowValue(currentRaw, {
            channel: `mutation:${attr.toLowerCase()}`,
            tag: String(mutation.target?.tagName || '').toLowerCase(),
          });
        }
        rewriteElement(mutation.target);
        if (Date.now() <= runtimeAssetDiagnosticWindowUntil) {
          const after = mutation.target?.getAttribute?.(attr);
          if (String(before || '') !== String(after || '')) {
            Promise.resolve(logOfflineDiagnosticEvent('runtime-asset-dom-mutation', {
              epoch: runtimeAssetDiagnosticEpoch,
              attribute: attr,
              oldValue: trimRuntimeDiagnosticValue(before),
              currentValue: trimRuntimeDiagnosticValue(after),
              element: describeRuntimeAssetElement(mutation.target),
            })).catch(() => undefined);
          }
        }
      }
      mutation.addedNodes?.forEach?.((node) => {
        if (node?.nodeType !== 1) return;
        rewriteElement(node);
        node.querySelectorAll?.('[src],[srcset],[poster],[style]').forEach(rewriteElement);
      });
    });
  });
  observer.observe(document.documentElement, {
    subtree: true,
    childList: true,
    attributes: true,
    attributeOldValue: true,
    attributeFilter: ['src', 'srcset', 'poster', 'style'],
  });

  // Log every resource failure, including failures of already-local capacitor://
  // URLs. This distinguishes "resolver had no local entry" from "entry existed
  // but the native URI itself could not be rendered".
  window.addEventListener('error', (event) => {
    const element = event?.target;
    if (!element || element === window || element === document) return;
    logRuntimeAssetLoadEvent('runtime-asset-load-error', element);

    if (!shouldResolveFromOfflineStorage()) return;
    ['src', 'poster'].some((attr) => {
      const value = element.getAttribute?.(attr);
      if (!value || isAlreadyLocalOfflineUrl(value)) return false;
      const resolved = resolveOfflineAssetUrlSync(value);
      const recovered = Boolean(resolved && resolved !== value);
      Promise.resolve(logOfflineDiagnosticEvent('runtime-asset-error-recovery', {
        epoch: runtimeAssetDiagnosticEpoch,
        attribute: attr,
        originalUrl: trimRuntimeDiagnosticValue(value),
        resolvedUrl: trimRuntimeDiagnosticValue(resolved),
        recovered,
        key: canonicalAssetKey(value),
        hasAssetMeta: Boolean(getAssetMetaEntry(canonicalAssetKey(value))),
      })).catch(() => undefined);
      if (!recovered) return false;
      element.setAttribute(attr, resolved);
      return true;
    });
  }, true);

  // Successful loads are noisy, so record them only for a few seconds around
  // network transitions. Together with load-error this shows exactly which
  // elements survived or failed after each rewrite.
  window.addEventListener('load', (event) => {
    if (Date.now() > runtimeAssetDiagnosticWindowUntil) return;
    const element = event?.target;
    if (!element || element === window || element === document) return;
    logRuntimeAssetLoadEvent('runtime-asset-load-success', element);
  }, true);

  // Covers launches that are already offline before React renders.
  scheduleDocumentRewrite();
}

export function formatBytes(bytes = 0) {
  const value = Number(bytes || 0);
  if (value < 1024) return `${value} B`;
  const kb = value / 1024;
  if (kb < 1024) return `${kb.toFixed(kb >= 100 ? 0 : 1)} KB`;
  const mb = kb / 1024;
  if (mb < 1024) return `${mb.toFixed(mb >= 100 ? 0 : 1)} MB`;
  return `${(mb / 1024).toFixed(2)} GB`;
}
